import React, { useState, useEffect, useMemo, useCallback } from "react";
import liff from '@line/liff';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
// import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import {
  Plus,
  LogOut,
  Settings,
  CreditCard,
  TrendingUp,
  TrendingDown,
  FileDown,
  User,
  Filter,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  SquarePen,
  Trash,
  Info,
  Eye,
  EyeOff,
  CalendarClock,
  PiggyBank,
  RefreshCw,
  LayoutDashboard,
  X,
  XCircle,
  CheckCircle,
  Play,
  Clock,
} from "lucide-react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import axios from "axios";
import { format } from "date-fns";


dayjs.extend(utc);
dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

// ตั้งค่าให้ axios ส่ง token ไปกับทุก request
const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const parseMoney = (s) => {
  if (s === null || s === undefined) return 0;
  const v = String(s).replace(/,/g, "");
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const formatBudgetTyping = (val) => {
  const raw = String(val || "").replace(/,/g, "");
  // keep only digits and dot
  let out = raw.replace(/[^0-9.]/g, "");
  // keep only the first dot and limit to 2 decimals
  const firstDot = out.indexOf(".");
  if (firstDot !== -1) {
    const intPart = out.slice(0, firstDot).replace(/\./g, "");
    let decPart = out.slice(firstDot + 1).replace(/\./g, "");
    decPart = decPart.slice(0, 2);
    out = intPart + "." + decPart;
  } else {
    // no dot allowed elsewhere
    out = out.replace(/\./g, "");
  }
  return out;
};

// --- Bulk update helpers (DOM-driven; ใช้ได้ทันที) ---
const bulkTxUpdateStatus = async (ids = [], status = "paid") => {
  try {
    if (!Array.isArray(ids) || ids.length === 0)
      return { ok: false, message: "no ids" };
    const { data } = await api.put("/transactions/bulk-status", {
      ids,
      status,
    });
    return data;
  } catch (err) {
    console.error(
      "bulkTxUpdateStatus error:",
      err?.response?.data || err?.message,
    );
    return { ok: false, error: err?.response?.data || err?.message };
  }
};
const getSelectedTxIdsFromDOM = () => {
  const nodes = document.querySelectorAll("table .tx-select:checked");
  return Array.from(nodes)
    .map((n) => Number(n.dataset.id))
    .filter(Boolean);
};
const handleBulkMarkStatus = async (status) => {
  const ids = getSelectedTxIdsFromDOM();
  if (!ids.length) {
    showToast("กรุณาเลือกอย่างน้อย 1 รายการ", "warning");
    return;
  }

  // Keep references to the selected checkboxes/rows for immediate UI update
  const selectedCheckboxes = Array.from(
    document.querySelectorAll("table .tx-select:checked"),
  );

  const res = await bulkTxUpdateStatus(ids, status);
  if (res?.ok) {
    // --- Immediate visual refresh for desktop table rows ---
    try {
      selectedCheckboxes.forEach((cb) => {
        const row = cb.closest("tr");
        if (!row) return;
        const badge = row.querySelector("span.rounded-full");
        if (badge) {
          // Reset classes
          badge.classList.remove(
            "bg-emerald-100",
            "text-emerald-700",
            "bg-gray-100",
            "text-gray-700",
          );
          if (String(status) === "paid") {
            badge.classList.add("bg-emerald-100", "text-emerald-700");
            badge.textContent = "จ่ายแล้ว";
          } else {
            badge.classList.add("bg-gray-100", "text-gray-700");
            badge.textContent = "ยังไม่จ่าย";
          }
        }
      });
    } catch (e) {
      console.error("DOM patch after bulk update failed:", e);
    }

    // --- Clear selections in the table ---
    document.querySelectorAll("table .tx-select").forEach((n) => {
      n.checked = false;
    });
    const selAll = document.querySelector("table thead input.tx-select-all");
    if (selAll) selAll.checked = false;

    // Optional: if you have a data reload function, call it here (uncomment and implement)
    // await loadTransactions();

    showToast(
      `อัปเดตสถานะ ${status === "paid" ? "จ่ายแล้ว" : "ยังไม่จ่าย"} สำเร็จ (${res.affectedRows || 0} รายการ)`,
      "success",
    );
  } else {
    showToast("อัปเดตไม่สำเร็จ", "error");
    console.error(res);
  }
};

// --- Pretty Toast Notifications (no external libs) ---
const ensureToastRoot = () => {
  const id = "toast-root";
  let root = document.getElementById(id);
  if (!root) {
    root = document.createElement("div");
    root.id = id;
    root.className = "fixed top-4 right-4 z-[9999] flex flex-col gap-2";
    document.body.appendChild(root);
  }
  return root;
};
const showToast = (message, type = "info", { duration = 2800 } = {}) => {
  const root = ensureToastRoot();
  const wrap = document.createElement("div");
  const base =
    "max-w-sm w-[19rem] md:w-[22rem] px-3 py-2 rounded-xl shadow-lg border text-sm flex items-start gap-2 transition-all duration-300";
  const styles = {
    success: "bg-white border-emerald-200 text-emerald-800",
    error: "bg-white border-rose-200 text-rose-800",
    warning: "bg-white border-amber-200 text-amber-800",
    info: "bg-white border-slate-200 text-slate-800",
  };
  const icons = {
    success:
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5"><path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-2.59a.75.75 0 1 0-1.22-.86l-3.233 4.6-1.61-1.61a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.16-.11l3.73-5.33Z" clip-rule="evenodd"/></svg>',
    error:
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5"><path fill-rule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm0 6a.75.75 0 0 1 .75.75v5a.75.75 0 0 1-1.5 0v-5A.75.75 0 0 1 12 8.25Zm0 8.25a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z" clip-rule="evenodd"/></svg>',
    warning:
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5"><path d="M10.29 3.86a2.25 2.25 0 0 1 3.42 0l7.8 9.48c1.17 1.42.16 3.66-1.71 3.66H4.2c-1.87 0-2.88-2.24-1.71-3.66l7.8-9.48Z"/><path fill="#fff" d="M12 8a.75.75 0 0 1 .75.75V13a.75.75 0 0 1-1.5 0V8.75A.75.75 0 0 1 12 8Zm0 8a.875.875 0 1 1 0-1.75.875.875 0 0 1 0 1.75Z"/></svg>',
    info: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5"><path d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-.75 6A.75.75 0 0 1 12 7.5h.008a.75.75 0 1 1 0 1.5H12a.75.75 0 0 1-.75-.75ZM9.75 11.25A.75.75 0 0 1 10.5 10.5h1.125a.375.375 0 0 1 .375.375V16.5h.75a.75.75 0 1 1 0 1.5H10.5a.75.75 0 1 1 0-1.5h.375v-4.875H10.5a.75.75 0 0 1-.75-.75Z"/></svg>',
  };
  wrap.className = `${base} ${styles[type] || styles.info} opacity-0 translate-y-2`;
  wrap.innerHTML = `
    <div class="shrink-0 pt-0.5">${icons[type] || icons.info}</div>
    <div class="whitespace-pre-line">${message}</div>
    <button class="ml-auto text-slate-400 hover:text-slate-600" aria-label="ปิด">×</button>
  `;
  root.appendChild(wrap);
  // animate in
  requestAnimationFrame(() => {
    wrap.classList.remove("opacity-0", "translate-y-2");
    wrap.classList.add("opacity-100");
  });
  const close = () => {
    wrap.classList.add("opacity-0", "translate-y-2");
    setTimeout(() => {
      try {
        root.removeChild(wrap);
      } catch { }
    }, 220);
  };
  const btn = wrap.querySelector("button");
  btn.addEventListener("click", close);
  const t = setTimeout(close, Math.max(1500, duration));
  wrap.addEventListener("mouseenter", () => clearTimeout(t));
  wrap.addEventListener("mouseleave", () => setTimeout(close, 800));
};

const parseDayValue = (val) => {
  if (val === "" || val === null || val === undefined) return null;
  const raw = String(val);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const d = dayjs(raw);
    if (!d.isValid()) return null;
    return d.date();
  }
  const num = Number(raw);
  if (!Number.isInteger(num) || num < 1 || num > 31) return null;
  return num;
};

const parseMonthInput = (val) => {
  if (val === "" || val === null || val === undefined) return null;
  const num = Number(String(val));
  if (!Number.isInteger(num) || num < 1 || num > 12) return null;
  return num;
};

const getNextDueDate = (val) => {
  const dayNumber = parseDayValue(val);
  if (dayNumber === null) return null;
  const today = dayjs().tz("Asia/Bangkok").startOf("day");
  if (dayNumber < 1) return null;

  if (dayNumber < today.date()) {
    const nextMonth = today.add(1, "month");
    const safeDay = Math.min(dayNumber, nextMonth.daysInMonth());
    return nextMonth.date(safeDay);
  }
  const safeDay = Math.min(dayNumber, today.daysInMonth());
  return today.date(safeDay);
};

const getNextFeeDate = (val) => {
  const monthNumber = parseMonthInput(val);
  if (monthNumber === null) return null;
  const today = dayjs().tz("Asia/Bangkok").startOf("day");
  let year = today.year();
  const currentMonth = today.month() + 1;
  if (monthNumber < currentMonth) {
    year += 1;
  }
  const dateStr = `${year}-${String(monthNumber).padStart(2, "0")}-01`;
  return dayjs.tz(dateStr, "Asia/Bangkok");
};

// --- COMPONENTS ---

// Modal Component
const Modal = ({
  children,
  isOpen,
  onClose,
  title,
  hideHeader = false,
  size = "xl",
  variant = "bottom",
}) => {
  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 bg-black/60 z-[1000] flex justify-center ${variant === "center" ? "items-center p-4" : "items-end md:items-center p-0 md:p-4"} overflow-y-auto overscroll-none backdrop-blur-sm transition-all duration-300`}>
      <div
        className={`bg-white rounded-t-[20px] md:rounded-2xl shadow-2xl w-full ${size === "sm"
          ? "max-w-sm"
          : size === "md"
            ? "max-w-md"
            : size === "lg"
              ? "max-w-lg"
              : size === "xl"
                ? "max-w-xl"
                : size === "2xl"
                  ? "max-w-2xl"
                  : "max-w-xl"
          } transform transition-all ease-out duration-300 translate-y-0 flex flex-col overflow-visible shadow-2xl ${variant === "center" ? "rounded-2xl" : "rounded-t-[20px] md:rounded-2xl"}`}
      >
        {!hideHeader ? (
          <>
            <div className="flex justify-between items-center p-4 sticky top-0 bg-white border-b border-slate-100 z-10 rounded-t-[20px] md:rounded-t-2xl select-none">
              <h3 className="text-sm font-bold text-slate-800 truncate">
                {title}
              </h3>
              <button
                onClick={onClose}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600 active:scale-95 transition-all"
                aria-label="ปิดหน้าต่าง"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-4 md:p-6 flex-1 overflow-visible">
              {children}
            </div>
          </>
        ) : (
          <div className="relative p-4 md:p-6 overflow-y-auto overscroll-contain flex-1">
            <button
              onClick={onClose}
              className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600 active:scale-95 transition-all z-10"
              aria-label="ปิดหน้าต่าง"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

const PaymentMethodsPage = ({
  onNavigate,
  onRequestLogout,
  paymentMethods,
  paymentUsage,
  transactions,
  filterMonth,
  filterYear,
  onPaymentMethodsUpdate,
}) => {
  const emptyForm = {
    name: "",
    due_date: "",
    credit_limit: "",
    fee: "",
    fee_due_month: "",
  };
  const [items, setItems] = useState(paymentMethods || []);
  const [methodSearchTerm, setMethodSearchTerm] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showUsageFilters, setShowUsageFilters] = useState(false);
  const INITIAL_VISIBLE_METHODS = 10;
  const [visibleMethodCount, setVisibleMethodCount] = useState(
    INITIAL_VISIBLE_METHODS,
  );
  const tzNow = dayjs().tz("Asia/Bangkok");
  const currentYear = tzNow.year();
  const currentMonth = tzNow.month() + 1;

  const [filterStartYear, setFilterStartYear] = useState(currentYear);
  const [filterStartMonth, setFilterStartMonth] = useState(currentMonth);
  const [filterEndYear, setFilterEndYear] = useState(currentYear);
  const [filterEndMonth, setFilterEndMonth] = useState(currentMonth);
  const [usageRange, setUsageRange] = useState({
    start: { year: filterYear, month: filterMonth },
    end: { year: filterYear, month: filterMonth },
  });
  const [usageTransactions, setUsageTransactions] = useState(transactions || []);
  const [usageLoading, setUsageLoading] = useState(false);
  const [methodUsageTotal, setMethodUsageTotal] = useState(paymentUsage || {});
  const [usageTotalsReady, setUsageTotalsReady] = useState(!!paymentUsage);
  const [usageDetailOpenId, setUsageDetailOpenId] = useState(null);
  const [paymentSort, setPaymentSort] = useState({ key: "usage_amount", direction: "desc" });

  const YEAR_OPTIONS = [currentYear - 1, currentYear, currentYear + 1];
  const DAY_OPTIONS = useMemo(() => {
    const days = Array.from({ length: 31 }, (_, idx) => {
      const label = String(idx + 1);
      return { id: label, name: label };
    });
    return [{ id: "", name: "วันครบกำหนด" }, ...days];
  }, []);
  const MONTH_OPTIONS = useMemo(() => {
    const months = Array.from({ length: 12 }, (_, idx) => {
      const label = String(idx + 1);
      return { id: label, name: label };
    });
    return [{ id: "", name: "เดือนครบกำหนด" }, ...months];
  }, []);
  useEffect(() => {
    setItems(paymentMethods || []);
  }, [paymentMethods]);

  useEffect(() => {
    setVisibleMethodCount(INITIAL_VISIBLE_METHODS);
  }, [methodSearchTerm, items]);

  useEffect(() => {
    const handleGlobalClick = () => setUsageDetailOpenId(null);
    document.addEventListener("click", handleGlobalClick);
    return () => document.removeEventListener("click", handleGlobalClick);
  }, []);

  // --- Scroll Lock when modal is open ---
  useEffect(() => {
    const body = document.body;
    const docEl = document.documentElement;
    const scrollLockRef = { scrollY: 0, paddingRight: "" };

    if (formOpen || confirmDeleteId !== null) {
      // Save current scroll position
      const scrollY = window.scrollY || window.pageYOffset || 0;
      scrollLockRef.scrollY = scrollY;
      // Compensate for scrollbar to avoid content shift
      const scrollBarWidth = window.innerWidth - docEl.clientWidth;
      scrollLockRef.paddingRight = body.style.paddingRight;

      body.style.overflow = "hidden";
      if (scrollBarWidth > 0) body.style.paddingRight = `${scrollBarWidth}px`;
      // Fix the body to prevent iOS/Android background scroll
      body.style.position = "fixed";
      body.style.top = `-${scrollY}px`;
      body.style.width = "100%";
    } else {
      // Restore styles and scroll position
      const y = scrollLockRef.scrollY || 0;
      body.style.overflow = "";
      body.style.paddingRight = scrollLockRef.paddingRight || "";
      body.style.position = "";
      body.style.top = "";
      body.style.width = "";
      window.scrollTo(0, y);
    }
  }, [formOpen, confirmDeleteId]);

  const refreshMethodUsageTotals = useCallback(async () => {
    try {
      setUsageTotalsReady(false);
      const res = await api.get("/payment-methods/usage-summary");
      const nextTotals = {};
      (res.data || []).forEach((item) => {
        const key = Number(item.payment_method_id);
        if (!key) return;
        nextTotals[key] = item.usage_count;
      });
      setMethodUsageTotal(nextTotals);
      setUsageTotalsReady(true);
    } catch (err) {
      console.error("refreshMethodUsageTotals failed", err);
      setUsageTotalsReady(false);
      showToast("ไม่สามารถตรวจสอบการใช้งานวิธีชำระได้", "error");
    }
  }, []);

  useEffect(() => {
    if (paymentUsage) {
      setMethodUsageTotal(paymentUsage);
      setUsageTotalsReady(true);
    }
  }, [paymentUsage]);

  useEffect(() => {
    if (!paymentUsage || Object.keys(paymentUsage).length === 0) {
      refreshMethodUsageTotals();
    }
  }, [refreshMethodUsageTotals, paymentUsage]);

  const parseNullableDecimal = (val) => {
    if (val === "" || val === null || val === undefined) return null;
    const cleaned = String(val).replace(/,/g, "");
    const num = Number.parseFloat(cleaned);
    return Number.isFinite(num) ? num : null;
  };

  const formatMoneyInput = (val) => {
    if (val === "" || val === null || val === undefined) return "";
    let cleaned = String(val).replace(/,/g, "");
    cleaned = cleaned.replace(/[^\d.]/g, "");
    const firstDot = cleaned.indexOf(".");
    if (firstDot !== -1) {
      const before = cleaned.slice(0, firstDot + 1);
      const after = cleaned.slice(firstDot + 1).replace(/\./g, "");
      cleaned = before + after;
    }
    return cleaned;
  };

  const formatMoneyDisplay = (val) => {
    if (val === "" || val === null || val === undefined) return "";
    const num = Number(String(val).replace(/,/g, ""));
    if (!Number.isFinite(num)) return String(val);
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const sanitizeDayInput = (raw) => {
    if (!raw) return "";
    return String(raw).replace(/\D/g, "").slice(0, 2);
  };

  const sanitizeMonthInput = (val) => {
    if (!val) return "";
    return String(val).replace(/\D/g, "").slice(0, 2);
  };

  const toDayInputValue = (val) => {
    if (!val) return "";
    const parsed = parseDayValue(val);
    if (parsed === null) return "";
    return String(parsed);
  };

  const toMonthInputValue = (val) => {
    if (!val && val !== 0) return "";
    if (Number.isInteger(val)) return String(val);
    const num = Number(String(val));
    if (Number.isInteger(num)) return String(num);
    const d = dayjs(val);
    if (d.isValid()) return String(d.month() + 1);
    return "";
  };

  const formatDueDate = (val) => {
    const nextDue = getNextDueDate(val);
    if (!nextDue) return "-";
    return nextDue.format("DD MMM YYYY");
  };

  const formatFeeDueMonth = (val) => {
    const feeDate = getNextFeeDate(val);
    if (!feeDate) return "-";
    return feeDate.format("MMM YYYY");
  };

  const formatCurrencyText = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    const num = Number(val);
    if (!Number.isFinite(num)) return "-";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const methodUsageStats = useMemo(() => {
    const map = new Map();
    (usageTransactions || []).forEach((tx = {}) => {
      const key = Number(tx.payment_method_id);
      if (!key) return;
      const amount = Number(tx.amount);
      const type = String(tx.type || "").toLowerCase();
      const status = String(tx.status || "").toLowerCase();
      const next =
        map.get(key) || {
          count: 0,
          amountTotal: 0,
          amountPaid: 0,
          amountUnpaid: 0,
        };
      next.count += 1;
      const isExpense = type === "expense";
      if (isExpense && Number.isFinite(amount)) {
        next.amountTotal += amount;
        if (status === "paid") {
          next.amountPaid += amount;
        } else {
          next.amountUnpaid += amount;
        }
      }
      map.set(key, next);
    });
    return map;
  }, [usageTransactions]);

  const loadPaymentMethods = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/payment-methods");
      const next = res.data || [];
      setItems(next);
      onPaymentMethodsUpdate?.(next);
    } catch (e) {
      console.error("loadPaymentMethods failed", e);
      setError("โหลดข้อมูลไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  }, [onPaymentMethodsUpdate]);



  const resetForm = () => {
    setForm(emptyForm);
    setEditingItem(null);
    setError("");
    setFieldErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        name === "due_date"
          ? sanitizeDayInput(value)
          : name === "fee_due_month"
            ? sanitizeMonthInput(value)
            : name === "credit_limit" || name === "fee"
              ? formatMoneyInput(value)
              : value,
    }));
    if (error) setError("");
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const clone = { ...prev };
      delete clone[name];
      return clone;
    });
  };

  const handleMoneyBlur = (e) => {
    const { name } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: formatMoneyDisplay(prev[name]),
    }));
  };

  const applyUsageFilter = useCallback(() => {
    const startYearVal = filterStartYear ?? null;
    const startMonthVal = filterStartMonth ?? null;
    const endYearVal = filterEndYear ?? null;
    const endMonthVal = filterEndMonth ?? null;

    if (
      startYearVal &&
      startMonthVal &&
      endYearVal &&
      endMonthVal
    ) {
      const startDate = dayjs(
        `${startYearVal}-${String(startMonthVal).padStart(2, "0")}-01`,
      ).startOf("month");
      const endDate = dayjs(
        `${endYearVal}-${String(endMonthVal).padStart(2, "0")}-01`,
      ).startOf("month");
      if (startDate.isAfter(endDate)) {
        showToast("เดือนเริ่มต้นต้องน้อยกว่าหรือเท่ากับเดือนสิ้นสุด", "warning");
        return;
      }
    }

    setUsageRange({
      start: {
        year: startYearVal,
        month: startMonthVal,
      },
      end: {
        year: endYearVal,
        month: endMonthVal,
      },
    });
  }, [filterStartYear, filterStartMonth, filterEndYear, filterEndMonth]);

  useEffect(() => {
    applyUsageFilter();
  }, [applyUsageFilter]);

  const resetUsageFilter = () => {
    setFilterStartYear(currentYear);
    setFilterStartMonth(currentMonth);
    setFilterEndYear(currentYear);
    setFilterEndMonth(currentMonth);
    setMethodSearchTerm("");
    setUsageRange({
      start: { year: currentYear, month: currentMonth },
      end: { year: currentYear, month: currentMonth },
    });
  };

  useEffect(() => {
    const { start, end } = usageRange;

    const resolveMonth = (value) => {
      if (value === null || value === undefined || value === "") return -1;
      if (typeof value === "number") {
        return value >= 1 && value <= 12 ? value : -1;
      }
      const numeric = Number(value);
      if (Number.isInteger(numeric) && numeric >= 1 && numeric <= 12) {
        return numeric;
      }
      const idx = MONTH_NAMES.indexOf(String(value));
      return idx >= 0 ? idx + 1 : -1;
    };

    const hasStart = Boolean(start?.year) && Boolean(start?.month);
    const hasEnd = Boolean(end?.year) && Boolean(end?.month);

    if (!hasStart && !hasEnd) {
      setUsageTransactions([]); // Default to empty
      setUsageLoading(false);
      return;
    }

    const startYear = hasStart ? Number(start.year) : Number(end?.year);
    const startMonthNum = hasStart ? resolveMonth(start.month) : resolveMonth(end?.month);
    const endYear = hasEnd ? Number(end.year) : Number(start?.year);
    const endMonthNum = hasEnd ? resolveMonth(end.month) : resolveMonth(start?.month);

    if (!startYear || startMonthNum <= 0 || !endYear || endMonthNum <= 0) {
      setUsageTransactions([]); // Default to empty
      setUsageLoading(false);
      return;
    }

    const isDefaultRange =
      startYear === filterYear &&
      startMonthNum === filterMonth &&
      endYear === filterYear &&
      endMonthNum === filterMonth;

    if (isDefaultRange && transactions && transactions.length > 0) {
      setUsageTransactions(transactions);
      setUsageLoading(false);
      return;
    }

    let from = dayjs(`${startYear}-${String(startMonthNum).padStart(2, "0")}-01`).startOf("month");
    let to = dayjs(`${endYear}-${String(endMonthNum).padStart(2, "0")}-01`).endOf("month");

    if (!from.isValid() || !to.isValid()) {
      setUsageTransactions([]); // Default to empty
      setUsageLoading(false);
      return;
    }

    if (from.isAfter(to)) {
      const temp = from;
      from = to.startOf("month");
      to = temp.endOf("month");
    }

    const months = [];
    let cursor = from.clone();
    while (cursor.isBefore(to) || cursor.isSame(to, "month")) {
      months.push({ year: cursor.year(), month: cursor.month() + 1 });
      cursor = cursor.add(1, "month");
    }

    if (months.length === 0) {
      setUsageTransactions([]);
      return;
    }

    let cancelled = false;
    const fetchData = async () => {
      try {
        setUsageLoading(true);
        const responses = await Promise.all(
          months.map(({ month, year }) =>
            api.get("/transactions", { params: { month, year } }),
          ),
        );
        if (cancelled) return;
        const seen = new Set();
        const combined = [];
        responses.forEach((res) => {
          (res.data || []).forEach((tx) => {
            if (!seen.has(tx.id)) {
              seen.add(tx.id);
              combined.push(tx);
            }
          });
        });
        setUsageTransactions(combined);
      } catch (err) {
        console.error("load usage range failed", err);
        if (!cancelled) setUsageTransactions([]); // Set to empty on error
      } finally {
        if (!cancelled) setUsageLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [usageRange]);

  const openCreateModal = () => {
    resetForm();
    setFormOpen(true);
  };

  const cancelForm = () => {
    setFormOpen(false);
    resetForm();
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name || "",
      due_date: toDayInputValue(item.due_date),
      fee_due_month: toMonthInputValue(item.fee_due_month),
      credit_limit:
        item.credit_limit !== undefined && item.credit_limit !== null
          ? formatMoneyDisplay(item.credit_limit)
          : "",
      fee:
        item.fee !== undefined && item.fee !== null
          ? formatMoneyDisplay(item.fee)
          : "",
    });
    setFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmedName = form.name.trim();
    const nextFieldErrors = {};

    if (!trimmedName) {
      nextFieldErrors.name = "กรุณากรอกชื่อวิธีชำระ";
    }
    const dueDay = parseDayValue(form.due_date);
    if (form.due_date && dueDay === null) {
      nextFieldErrors.due_date = "กรุณากรอกวันที่ครบกำหนดให้ถูกต้อง (1-31)";
    }
    const feeDuePayload = parseMonthInput(form.fee_due_month);
    if (form.fee_due_month && !feeDuePayload) {
      nextFieldErrors.fee_due_month =
        "กรุณากรอกเดือนค่าธรรมเนียมให้ถูกต้อง (1-12)";
    }

    const payload = {
      name: trimmedName,
      due_date: dueDay,
      fee_due_month: feeDuePayload,
      credit_limit: parseNullableDecimal(form.credit_limit),
      fee: parseNullableDecimal(form.fee),
    };

    if (payload.credit_limit === null && form.credit_limit !== "") {
      nextFieldErrors.credit_limit = "กรุณากรอกตัวเลขให้ถูกต้อง";
    }
    if (payload.fee === null && form.fee !== "") {
      nextFieldErrors.fee = "กรุณากรอกตัวเลขให้ถูกต้อง";
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      setError("กรุณาตรวจสอบข้อมูลอีกครั้ง");
      return;
    }
    setFieldErrors({});
    try {
      setSaving(true);
      if (editingItem) {
        await api.put(`/payment-methods/${editingItem.id}`, payload);
        showToast("อัปเดตวิธีชำระเรียบร้อยแล้ว", "success");
      } else {
        await api.post("/payment-methods", payload);
        showToast("เพิ่มวิธีชำระเรียบร้อยแล้ว", "success");
      }
      await loadPaymentMethods();
      await refreshMethodUsageTotals();
      resetForm();
      setFormOpen(false);
    } catch (e) {
      console.error("savePaymentMethod failed", e);
      setError("บันทึกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
      setFormOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRequest = (id, totalUsageOverride = null) => {
    if (!usageTotalsReady) {
      setError("ระบบกำลังตรวจสอบการใช้งานวิธีชำระ กรุณาลองใหม่อีกครั้ง");
      showToast("ยังไม่สามารถลบได้ กรุณาลองใหม่อีกครั้ง", "warning");
      return;
    }
    const totalUsage =
      totalUsageOverride ?? methodUsageTotal[Number(id)] ?? 0;
    if (totalUsage > 0) {
      const message = `ไม่สามารถลบได้ มี ${totalUsage} รายการ ที่ใช้งานวิธีชำระนี้อยู่`;
      setError(message);
      showToast(message, "error");
      return;
    }
    setConfirmDeleteId(id);
  };

  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      setSaving(true);
      await api.delete(`/payment-methods/${confirmDeleteId}`);
      showToast("ลบวิธีชำระเรียบร้อยแล้ว", "success");
      await loadPaymentMethods();
      await refreshMethodUsageTotals();
      setConfirmDeleteId(null);
    } catch (e) {
      console.error("deletePaymentMethod failed", e);
      const msg =
        e?.response?.data?.message || "ลบไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
      setError(msg);
      showToast(msg, "error");
      setConfirmDeleteId(null);
    } finally {
      setSaving(false);
    }
  };

  const getCreatedTimestamp = useCallback((item) => {
    if (!item || typeof item !== "object") return 0;
    const candidates = [
      item.created_at,
      item.createdAt,
      item.create_date,
      item.createDate,
      item.created,
    ];
    for (const value of candidates) {
      if (value === null || value === undefined || value === "") continue;
      const numeric = Number(value);
      if (Number.isFinite(numeric)) return numeric;
      const parsed = dayjs(value);
      if (parsed.isValid()) return parsed.valueOf();
    }
    const idNumeric = Number(item.id);
    return Number.isFinite(idNumeric) ? idNumeric : 0;
  }, []);

  const filteredPaymentItems = useMemo(() => {
    const term = methodSearchTerm.trim().toLowerCase();
    const base = Array.isArray(items) ? items : [];
    return term
      ? base.filter((item) =>
        String(item?.name || "")
          .toLowerCase()
          .includes(term),
      )
      : base;
  }, [items, methodSearchTerm]);

  const dueHighlights = useMemo(() => {
    const today = dayjs().tz("Asia/Bangkok").startOf("day");
    const filtered = (Array.isArray(items) ? items : [])
      .map((item) => {
        const dueDate = getNextDueDate(item?.due_date);
        if (!dueDate) return null;
        const daysLeft = Math.max(dueDate.diff(today, "day"), 0);
        return { item, dueDate, daysLeft };
      })
      .filter((entry) => entry && entry.daysLeft <= 7)
      .sort((a, b) => a.dueDate.valueOf() - b.dueDate.valueOf());
    const initial = filtered.slice(0, 3);

    return {
      list: filtered,
      initial,
      count: filtered.length,
    };
  }, [items]);

  const feeHighlights = useMemo(() => {
    const today = dayjs().tz("Asia/Bangkok").startOf("day");
    const currentMonthStart = today.startOf("month");
    const filtered = (Array.isArray(items) ? items : [])
      .map((item) => {
        const feeDate = getNextFeeDate(item?.fee_due_month);
        if (!feeDate) return null;
        const feeMonthStart = feeDate.startOf("month");
        const monthsLeft = Math.max(feeMonthStart.diff(currentMonthStart, "month"), 0);
        return { item, feeDate, monthsLeft };
      })
      .filter((entry) => entry && entry.monthsLeft <= 1)
      .sort((a, b) => a.feeDate.valueOf() - b.feeDate.valueOf());

    const initial = filtered.slice(0, 3);

    return {
      list: filtered,
      initial,
      count: filtered.length,
    };
  }, [items]);

  const [showAllDue, setShowAllDue] = useState(false);
  const [showAllFees, setShowAllFees] = useState(false);

  const getUsageMetrics = useCallback(
    (item) => {
      const stats =
        methodUsageStats.get(Number(item?.id)) || {
          count: 0,
          amountTotal: 0,
          amountPaid: 0,
          amountUnpaid: 0,
        };

      const toNumber = (value) => {
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
      };

      const usageAmountPaid = toNumber(stats.amountPaid);
      const usageAmountUnpaid = toNumber(stats.amountUnpaid);
      const totalCandidate = toNumber(stats.amountTotal);
      const usageAmountTotal =
        totalCandidate !== 0
          ? totalCandidate
          : usageAmountPaid + usageAmountUnpaid;

      const usageCount = toNumber(stats.count);
      const totalUsageRaw = Number(methodUsageTotal[Number(item?.id)]);
      const totalUsage = Number.isFinite(totalUsageRaw)
        ? totalUsageRaw
        : usageCount;

      const creditLimitNum = parseNullableDecimal(item?.credit_limit);
      const remainingRaw =
        creditLimitNum === null || Number.isNaN(creditLimitNum)
          ? null
          : creditLimitNum - usageAmountTotal;
      const remaining =
        remainingRaw === null
          ? null
          : Math.abs(remainingRaw) < 0.005
            ? 0
            : remainingRaw;

      return {
        usageAmountTotal,
        usageAmountPaid,
        usageAmountUnpaid,
        usageCount,
        totalUsage,
        creditLimit: creditLimitNum,
        remaining,
      };
    },
    [methodUsageStats, methodUsageTotal],
  );

  const displayDueList = (showAllDue ? dueHighlights.list : dueHighlights.initial) ?? [];
  const displayFeeList = (showAllFees ? feeHighlights.list : feeHighlights.initial) ?? [];

  const sortedPaymentItems = useMemo(() => {
    const base = Array.isArray(filteredPaymentItems)
      ? filteredPaymentItems
      : [];
    const arr = [...base];
    const metricsCache = new Map();
    const ensureMetrics = (item) => {
      if (!metricsCache.has(item)) {
        metricsCache.set(item, getUsageMetrics(item));
      }
      return metricsCache.get(item);
    };

    const getValue = (item) => {
      const metrics = ensureMetrics(item);
      switch (paymentSort.key) {
        case "name":
          return String(item?.name || "").toLowerCase();
        case "credit_limit":
          return metrics.creditLimit;
        case "usage_amount":
          return metrics.usageAmountTotal;
        case "remaining":
          return metrics.remaining;
        case "due_date": {
          const due = getNextDueDate(item?.due_date);
          return due ? due.valueOf() : Number.POSITIVE_INFINITY;
        }
        case "fee":
          return parseNullableDecimal(item?.fee);
        case "fee_month": {
          const feeDate = getNextFeeDate(item?.fee_due_month);
          return feeDate ? feeDate.valueOf() : Number.POSITIVE_INFINITY;
        }
        case "usage_count":
          return metrics.usageCount;
        case "created":
        default:
          return getCreatedTimestamp(item);
      }
    };

    const compareNumbers = (a, b) => {
      const finiteA = Number.isFinite(a);
      const finiteB = Number.isFinite(b);
      if (!finiteA && !finiteB) return 0;
      if (!finiteA) return 1;
      if (!finiteB) return -1;
      if (a === b) return 0;
      return paymentSort.direction === "asc" ? a - b : b - a;
    };

    arr.sort((a, b) => {
      const valA = getValue(a);
      const valB = getValue(b);
      if (typeof valA === "string" && typeof valB === "string") {
        return paymentSort.direction === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
      return compareNumbers(valA, valB);
    });

    return arr;
  }, [filteredPaymentItems, paymentSort, getUsageMetrics, getCreatedTimestamp]);

  const togglePaymentSort = (key) => {
    setPaymentSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" },
    );
  };

  const renderSortIndicator = (key) => {
    if (paymentSort.key !== key) return <span className="text-[10px] text-slate-400">↕</span>;
    return (
      <span className="text-[10px] text-slate-600">
        {paymentSort.direction === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  const totalMethods = sortedPaymentItems.length;
  const activeMethodCount = useMemo(() => {
    if (!methodUsageTotal || typeof methodUsageTotal !== "object") return 0;
    return Object.values(methodUsageTotal).filter(
      (count) => Number(count) > 0,
    ).length;
  }, [methodUsageTotal]);
  const totalCreditLimit = useMemo(() => {
    if (!Array.isArray(items)) return 0;
    return items.reduce((sum, item) => {
      const limit = parseNullableDecimal(item?.credit_limit);
      return limit ? sum + limit : sum;
    }, 0);
  }, [items]);

  useEffect(() => {
    const handlePlus = () => setFormOpen(true);
    window.addEventListener("app-plus-clicked", handlePlus);
    return () => window.removeEventListener("app-plus-clicked", handlePlus);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 text-slate-800">
      <div className="px-4 pt-2 pb-24 sm:px-6 sm:py-3 lg:px-8 lg:py-4">

        <main className="w-full space-y-6 pt-4">
          <section className="w-full">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-700">
                  ภาพรวมวิธีชำระ
                </h3>
                <div className="mt-2 grid grid-cols-2 gap-3 text-sm text-slate-600">
                  <div>
                    <p className="text-xs text-slate-500">ทั้งหมด</p>
                    <p className="text-sm font-semibold text-slate-800">
                      {totalMethods}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">มีรายการใช้งาน</p>
                    <p className="text-sm font-semibold text-slate-800">
                      {activeMethodCount}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500">วงเงินรวม</p>
                    <p className="text-sm font-semibold text-slate-800">
                      {Number(totalCreditLimit || 0).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      ฿
                    </p>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-700">
                    บัตรใกล้ครบกำหนด
                  </h3>
                  <span className="text-xs text-slate-500">
                    ภายใน 7 วัน: {dueHighlights.count}
                  </span>
                </div>
                <div className="mt-2 space-y-1">
                  {displayDueList.length ? (
                    displayDueList.map(({ item, dueDate, daysLeft }) => (
                      <div
                        key={item.id ?? item.name ?? dueDate.valueOf()}
                        className="flex items-start justify-between gap-2"
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-slate-700 truncate">
                            {item.name || "-"}
                          </p>
                          <p className="text-[11px] text-slate-400">
                            {daysLeft === 0 ? "ครบกำหนดวันนี้" : `อีก ${daysLeft} วัน`}
                          </p>
                        </div>
                        <span className="text-xs text-slate-600 whitespace-nowrap">
                          {dueDate.format("DD MMM YYYY")}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-slate-400">ยังไม่มีข้อมูลครบกำหนด</p>
                  )}
                </div>
                {dueHighlights.count > (dueHighlights.initial?.length ?? 0) && (
                  <button
                    type="button"
                    onClick={() => setShowAllDue((prev) => !prev)}
                    className="mt-2 text-xs text-sky-600 hover:underline"
                  >
                    {showAllDue ? "แสดงน้อยลง" : `ดูทั้งหมด ${dueHighlights.count} รายการ`}
                  </button>
                )}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-700">
                    รอบค่าธรรมเนียมถัดไป
                  </h3>
                  <span className="text-xs text-slate-500">
                    เดือนนี้และถัดไป: {feeHighlights.count}
                  </span>
                </div>
                <div className="mt-2 space-y-1">
                  {displayFeeList.length ? (
                    displayFeeList.map(({ item, feeDate, monthsLeft }) => (
                      <div
                        key={item.id ?? item.name ?? feeDate.valueOf()}
                        className="flex items-start justify-between gap-2"
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-slate-700 truncate">
                            {item.name || "-"}
                          </p>
                          <p className="text-[11px] text-slate-400">
                            {monthsLeft === 0 ? "เดือนนี้" : "เดือนถัดไป"}
                          </p>
                        </div>
                        <span className="text-xs text-slate-600 whitespace-nowrap">
                          {feeDate.format("MMM YYYY")}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-slate-400">ยังไม่มีข้อมูลค่าธรรมเนียม</p>
                  )}
                </div>
                {feeHighlights.count > (feeHighlights.initial?.length ?? 0) && (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setShowAllFees((prev) => !prev)}
                      className="mt-2 text-xs text-sky-600 hover:underline"
                    >
                      {showAllFees ? "แสดงน้อยลง" : `ดูทั้งหมด ${feeHighlights.count} รายการ`}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>

          <section className="w-full bg-white rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6">
            <header className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-semibold text-slate-800">
                  รายการวิธีชำระเงิน
                </h2>
                <p className="text-xs text-slate-500 mt-0.5 mb-2">
                  ทั้งหมด {sortedPaymentItems.length} รายการ
                </p>
              </div>
            </header>
            <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-2">
              <button
                type="button"
                onClick={() => setShowUsageFilters((prev) => !prev)}
                aria-expanded={showUsageFilters}
                aria-controls="payment-method-filters"
                className="flex items-center space-x-1 text-[10px] md:text-sm bg-white border border-gray-300 text-slate-700 hover:bg-gray-50 font-bold py-1 px-2 rounded-lg transition-colors"
              >
                <Filter size={14} />
                <span>Filter</span>
                <ChevronDown
                  size={14}
                  className={`text-slate-500 transition-transform ${showUsageFilters ? "rotate-180" : ""}`}
                />
              </button>
              <button
                type="button"
                onClick={openCreateModal}
                className="hidden md:flex items-center space-x-1 text-[10px] md:text-sm bg-gradient-to-r from-sky-500 to-blue-500 hover:from-sky-600 hover:to-blue-600 text-white font-bold py-1 px-2 rounded-lg transition-colors shadow"
              >
                <Plus size={14} />
                <span>เพิ่มวิธีชำระ</span>
              </button>
              {usageLoading && (
                <span className="text-[10px] md:text-xs text-slate-400">
                  กำลังโหลดข้อมูลการใช้งาน...
                </span>
              )}
            </div>
            <div
              id="payment-method-filters"
              className={`${showUsageFilters ? "mt-2" : "max-h-0 overflow-hidden"} transition-all duration-200`}
            >
              {showUsageFilters && (
                <div className="w-full lg:w-1/2 lg:max-w-[50%]">
                  <div className="flex flex-wrap items-end gap-1 sm:gap-1.5 md:gap-2 mb-4">
                    <div className="flex-1 min-w-[180px] space-y-1">
                      <label className="block text-sm font-medium text-slate-400">
                        ค้นหาวิธีชำระ
                      </label>
                      <input
                        type="text"
                        value={methodSearchTerm}
                        onChange={(e) => setMethodSearchTerm(e.target.value)}
                        className="w-full inline-flex justify-between rounded-lg border border-gray-300 bg-white text-slate-800 text-base md:text-sm px-4 py-1 h-8 outline-none transition-all"
                        placeholder="ค้นหาชื่อวิธีชำระ"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-slate-400">
                        เริ่มต้น
                      </label>
                      <div className="flex gap-1">
                        <div className="w-24">
                          <TailwindDropdown
                            options={YEAR_OPTIONS.map((year) => ({
                              id: year,
                              name: String(year),
                            }))}
                            value={filterStartYear ?? ""}
                            onChange={(e) => {
                              const nextValue = e?.target?.value;
                              if (nextValue === "" || nextValue == null) {
                                setFilterStartYear(null);
                                return;
                              }
                              const next = Number(nextValue);
                              setFilterStartYear(
                                Number.isNaN(next) ? null : next,
                              );
                            }}
                            placeholder="ปี"
                            className="usage-start-year"
                            searchable={false}
                          />
                        </div>
                        <div className="w-20">
                          <TailwindDropdown
                            options={MONTH_NAMES.map((name, idx) => ({
                              id: idx + 1,
                              name,
                            }))}
                            value={filterStartMonth ?? ""}
                            onChange={(e) => {
                              const nextValue = e?.target?.value;
                              if (nextValue === "" || nextValue == null) {
                                setFilterStartMonth(null);
                                return;
                              }
                              const next = Number(nextValue);
                              setFilterStartMonth(
                                Number.isNaN(next) || next < 1 || next > 12
                                  ? null
                                  : next,
                              );
                            }}
                            placeholder="เดือน"
                            className="usage-start-month"
                            searchable={false}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-slate-400">
                        สิ้นสุด
                      </label>
                      <div className="flex gap-1">
                        <div className="w-24">
                          <TailwindDropdown
                            options={YEAR_OPTIONS.map((year) => ({
                              id: year,
                              name: String(year),
                            }))}
                            value={filterEndYear ?? ""}
                            onChange={(e) => {
                              const nextValue = e?.target?.value;
                              if (nextValue === "" || nextValue == null) {
                                setFilterEndYear(null);
                                return;
                              }
                              const next = Number(nextValue);
                              setFilterEndYear(Number.isNaN(next) ? null : next);
                            }}
                            placeholder="ปี"
                            className="usage-end-year"
                            searchable={false}
                          />
                        </div>
                        <div className="w-20">
                          <TailwindDropdown
                            options={MONTH_NAMES.map((name, idx) => ({
                              id: idx + 1,
                              name,
                            }))}
                            value={filterEndMonth ?? ""}
                            onChange={(e) => {
                              const nextValue = e?.target?.value;
                              if (nextValue === "" || nextValue == null) {
                                setFilterEndMonth(null);
                                return;
                              }
                              const next = Number(nextValue);
                              setFilterEndMonth(
                                Number.isNaN(next) || next < 1 || next > 12
                                  ? null
                                  : next,
                              );
                            }}
                            placeholder="เดือน"
                            className="usage-end-month"
                            searchable={false}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-end gap-1 sm:gap-1.5 md:gap-2">
                      <button
                        type="button"
                        onClick={resetUsageFilter}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-1.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 hover:border-slate-400 transition"
                      >
                        <XCircle size={14} />
                        <span>ล้าง</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {sortedPaymentItems.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm">
                ไม่มีวิธีชำระ
              </div>
            ) : (
              <>
                {loading && (
                  <div className="text-center py-2">
                    <span className="text-[10px] text-slate-400 animate-pulse">กำลังอัปเดตข้อมูล...</span>
                  </div>
                )}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full text-sm table-fixed">
                    <colgroup>
                      <col className="w-[17%]" /> {/* วิธีชำระ */}
                      <col className="w-[12%]" /> {/* วงเงินทั้งหมด */}
                      <col className="w-[12%]" /> {/* ยอดที่ใช้ไป */}
                      <col className="w-[12%]" /> {/* วงเงินคงเหลือ */}
                      <col className="w-[11%]" /> {/* ครบกำหนดชำระ */}
                      <col className="w-[11%]" /> {/* ค่าธรรมเนียม */}
                      <col className="w-[11%]" /> {/* รอบค่าธรรมเนียม */}
                      <col className="w-[8%]" /> {/* รายการ */}
                      <col className="w-[6%]" /> {/* Actions */}
                    </colgroup>
                    <thead className="border-b border-gray-200 text-slate-600 text-sm bg-gray-50">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-semibold">
                          <button
                            type="button"
                            onClick={() => togglePaymentSort("name")}
                            className="flex items-center gap-1"
                          >
                            <span>วิธีชำระ</span>
                            {renderSortIndicator("name")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => togglePaymentSort("credit_limit")}
                            className="flex items-center gap-1 w-full justify-end"
                          >
                            <span>วงเงินทั้งหมด</span>
                            {renderSortIndicator("credit_limit")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => togglePaymentSort("usage_amount")}
                            className="flex items-center gap-1 w-full justify-end"
                          >
                            <span>ยอดที่ใช้ไป</span>
                            {renderSortIndicator("usage_amount")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => togglePaymentSort("remaining")}
                            className="flex items-center gap-1 w-full justify-end"
                          >
                            <span>วงเงินคงเหลือ</span>
                            {renderSortIndicator("remaining")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-center font-semibold whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => togglePaymentSort("due_date")}
                            className="flex items-center gap-1 mx-auto"
                          >
                            <span>ครบกำหนดชำระ</span>
                            {renderSortIndicator("due_date")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => togglePaymentSort("fee")}
                            className="flex items-center gap-1 w-full justify-end"
                          >
                            <span>ค่าธรรมเนียม</span>
                            {renderSortIndicator("fee")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-center font-semibold whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => togglePaymentSort("fee_month")}
                            className="flex items-center gap-1 mx-auto"
                          >
                            <span>รอบค่าธรรมเนียม</span>
                            {renderSortIndicator("fee_month")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-center font-semibold whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => togglePaymentSort("usage_count")}
                            className="flex items-center gap-1 mx-auto"
                          >
                            <span>รายการ</span>
                            {renderSortIndicator("usage_count")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-center font-semibold">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPaymentItems.slice(0, visibleMethodCount).map((item) => {
                        const metrics = getUsageMetrics(item) || {};
                        const {
                          usageCount = 0,
                          totalUsage = 0,
                          usageAmountTotal = 0,
                          usageAmountPaid = 0,
                          usageAmountUnpaid = 0,
                          remaining,
                          creditLimit,
                        } = metrics;
                        const usageTooltip = `จ่ายแล้ว: ${formatCurrencyText(usageAmountPaid)}\nยังไม่จ่าย: ${formatCurrencyText(usageAmountUnpaid)}`;
                        const deleteTitle =
                          !usageTotalsReady
                            ? "กำลังตรวจสอบการใช้งาน..."
                            : totalUsage > 0
                              ? `มีการใช้งานอยู่ ${totalUsage} รายการ`
                              : "ลบวิธีชำระ";
                        return (
                          <tr
                            key={item.id}
                            className="border-b border-gray-100 hover:bg-gray-50 text-sm transition"
                          >
                            <td className="px-2 text-slate-800">
                              {item.name || "-"}
                            </td>
                            <td className="px-2 text-right text-slate-700 tabular-nums">
                              {formatCurrencyText(creditLimit)}
                            </td>
                            <td className="px-2 text-right text-slate-700 tabular-nums">
                              <div className="relative flex items-center justify-end gap-1">
                                <span title={usageTooltip}>
                                  {formatCurrencyText(usageAmountTotal)}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setUsageDetailOpenId((prev) =>
                                      prev === item.id ? null : item.id,
                                    );
                                  }}
                                  className="p-0.5 text-slate-400 hover:text-slate-600 transition"
                                  aria-label="ดูรายละเอียดการใช้วงเงิน"
                                  aria-expanded={usageDetailOpenId === item.id}
                                >
                                  <Info className="h-3.5 w-3.5" />
                                </button>
                                {usageDetailOpenId === item.id && (
                                  <div
                                    className="absolute right-0 top-full mt-2 w-44 rounded-xl border border-gray-200 bg-white p-3 text-xs text-slate-600 shadow-xl z-[200]"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="pointer-events-none absolute -top-1 right-6 h-2 w-2 rotate-45 bg-white border-r border-t border-gray-200" />
                                    {/* <div className="flex items-center justify-between text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                                    <span>ใช้ไปทั้งหมด</span>
                                    <span>{formatCurrencyText(usageAmountTotal)}</span>
                                  </div> */}
                                    <div className="space-y-1 text-[11px]">
                                      <div className="flex items-center justify-between">
                                        <span className="text-slate-500">
                                          จ่ายแล้ว
                                        </span>
                                        <span className="font-semibold text-emerald-600">
                                          {formatCurrencyText(usageAmountPaid)}
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <span className="text-slate-500">
                                          ยังไม่จ่าย
                                        </span>
                                        <span className="font-semibold text-amber-600">
                                          {formatCurrencyText(usageAmountUnpaid)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-2 text-right text-slate-700 tabular-nums">
                              {formatCurrencyText(remaining)}
                            </td>
                            <td className="px-2 py-1.5 text-center whitespace-nowrap text-slate-700">
                              {formatDueDate(item.due_date)}
                            </td>
                            <td className="px-2 text-right text-slate-700 tabular-nums">
                              {formatCurrencyText(item.fee)}
                            </td>
                            <td className="px-2 text-center whitespace-nowrap text-slate-700">
                              {formatFeeDueMonth(item.fee_due_month)}
                            </td>
                            <td
                              className="px-2 text-center text-slate-700"
                              title={
                                totalUsage !== usageCount
                                  ? `มีการใช้งานทั้งหมด ${totalUsage} รายการ`
                                  : undefined
                              }
                            >
                              {usageCount}
                            </td>
                            <td className="px-2 text-center">
                              <div className="inline-flex gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleEdit(item)}
                                  className="p-2 text-slate-400 hover:text-sky-400"
                                  aria-label="แก้ไขวิธีชำระ"
                                >
                                  <SquarePen className="h-[14px] w-[14px]" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleDeleteRequest(item.id, totalUsage)
                                  }
                                  className="p-2 text-slate-400 hover:text-red-400"
                                  title={deleteTitle}
                                  aria-label="ลบวิธีชำระ"
                                >
                                  <Trash className="h-[14px] w-[14px]" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="md:hidden space-y-3">
                  {sortedPaymentItems.slice(0, visibleMethodCount).map((item) => {
                    const metrics = getUsageMetrics(item) || {};
                    const {
                      usageCount = 0,
                      totalUsage = 0,
                      usageAmountTotal = 0,
                      usageAmountPaid = 0,
                      usageAmountUnpaid = 0,
                      remaining,
                      creditLimit,
                    } = metrics;
                    const deleteTitle =
                      !usageTotalsReady
                        ? "กำลังตรวจสอบการใช้งาน..."
                        : totalUsage > 0
                          ? `มีการใช้งานอยู่ ${totalUsage} รายการ`
                          : "ลบวิธีชำระ";
                    return (
                      <div
                        key={item.id}
                        className="bg-white shadow rounded-md p-3 text-sm text-slate-800"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="font-semibold truncate">
                            {item.name || "-"}
                          </div>
                          <div className="text-right text-slate-600 text-xs">
                            {usageCount} รายการ
                          </div>
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-600">
                          <div>
                            วงเงินทั้งหมด:{" "}
                            <span className="font-semibold text-slate-800">
                              {formatCurrencyText(creditLimit)}
                            </span>
                          </div>
                          <div>
                            วงเงินคงเหลือ:{" "}
                            <span className="font-semibold text-slate-800">
                              {formatCurrencyText(remaining)}
                            </span>
                          </div>
                          <div>
                            ครบกำหนดชำระ:{" "}
                            <span className="font-semibold text-slate-800">
                              {formatDueDate(item.due_date)}
                            </span>
                          </div>
                          <div>
                            ค่าธรรมเนียม:{" "}
                            <span className="font-semibold text-slate-800">
                              {formatCurrencyText(item.fee)}
                            </span>
                          </div>
                          <div>
                            รอบค่าธรรมเนียม:{" "}
                            <span className="font-semibold text-slate-800">
                              {formatFeeDueMonth(item.fee_due_month)}
                            </span>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center justify-between text-xs text-slate-600">
                          <span>ยอดที่ใช้ไป:</span>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-800">
                              {formatCurrencyText(usageAmountTotal)}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setUsageDetailOpenId((prev) =>
                                  prev === item.id ? null : item.id,
                                );
                              }}
                              className="p-1 rounded-full text-slate-400 hover:text-slate-600 transition"
                              aria-label="ดูรายละเอียดการใช้วงเงิน"
                              aria-expanded={usageDetailOpenId === item.id}
                            >
                              <Info className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        {usageDetailOpenId === item.id && (
                          <div
                            className="mt-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-slate-600 shadow-md"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="space-y-1 text-xs">
                              <div className="flex items-center justify-between">
                                <span className="text-slate-500">จ่ายแล้ว</span>
                                <span className="font-semibold text-emerald-600">
                                  {formatCurrencyText(usageAmountPaid)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-slate-500">
                                  ยังไม่จ่าย
                                </span>
                                <span className="font-semibold text-amber-600">
                                  {formatCurrencyText(usageAmountUnpaid)}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="flex justify-between items-center mt-3">
                          <div className="inline-flex">
                            <button
                              type="button"
                              onClick={() => handleEdit(item)}
                              className="p-1 text-slate-400 hover:text-sky-400"
                              aria-label="แก้ไขวิธีชำระ"
                            >
                              <SquarePen className="h-[14px] w-[14px]" />
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                handleDeleteRequest(item.id, totalUsage)
                              }
                              className="p-1 text-slate-400 hover:text-red-400"
                              title={deleteTitle}
                              aria-label="ลบวิธีชำระ"
                            >
                              <Trash className="h-[14px] w-[14px]" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {sortedPaymentItems.length > visibleMethodCount && (
                  <div className="mt-4 flex justify-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setVisibleMethodCount((prev) =>
                          Math.min(
                            prev + INITIAL_VISIBLE_METHODS,
                            sortedPaymentItems.length,
                          ),
                        )
                      }
                      className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white text-slate-700 hover:bg-gray-50"
                    >
                      Load more
                    </button>
                    <button
                      type="button"
                      onClick={() => setVisibleMethodCount(sortedPaymentItems.length)}
                      className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white text-slate-700 hover:bg-gray-50"
                    >
                      All ({sortedPaymentItems.length})
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        </main>

        <Modal
          isOpen={formOpen}
          onClose={cancelForm}
          title={editingItem ? "แก้ไขวิธีชำระ" : "เพิ่มวิธีชำระใหม่"}
          size="lg"
          variant="bottom"
        >
          <form onSubmit={handleSubmit} className="px-1 md:px-2 py-2 space-y-6">
            <div className="space-y-5 pb-4 md:pb-6">
              <div>
                <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                  ชื่อวิธีชำระ
                </label>
                <div className="relative mt-1.5">
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleInputChange}
                    placeholder="เช่น เงินสด, บัตรเครดิต, โอน"
                    autoComplete="off"
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 h-8 text-base md:text-sm text-slate-800 outline-none transition-all shadow-sm"
                  />
                </div>
                {fieldErrors.name && (
                  <p className="text-xs text-red-400 font-medium mt-1">
                    {fieldErrors.name}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 md:gap-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                    วงเงินทั้งหมด
                  </label>
                  <div className="relative mt-1.5">
                    <input
                      type="text"
                      inputMode="decimal"
                      name="credit_limit"
                      value={form.credit_limit}
                      onChange={handleInputChange}
                      onBlur={handleMoneyBlur}
                      autoComplete="off"
                      className="w-full rounded-lg border border-gray-300 bg-white px-4 h-8 text-base md:text-sm text-slate-800 outline-none transition-all shadow-sm"
                      placeholder="0.00"
                    />
                  </div>
                  {fieldErrors.credit_limit && (
                    <p className="text-xs text-red-400 font-medium mt-1">
                      {fieldErrors.credit_limit}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                    ครบกำหนดชำระ
                  </label>
                  <div className="relative mt-1.5">
                    <TailwindDropdown
                      options={DAY_OPTIONS}
                      value={form.due_date}
                      onChange={(e) => handleInputChange({ target: { name: "due_date", value: e.target.value } })}
                      placeholder="เลือกวันที่"
                      className="w-full block"
                      searchable={false}
                      visibleOptionCount={5}
                      direction="up-mobile"
                    />
                  </div>
                  {fieldErrors.due_date && (
                    <p className="text-xs text-red-400 font-medium mt-1">
                      {fieldErrors.due_date}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 md:gap-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                    ค่าธรรมเนียม
                  </label>
                  <div className="relative mt-1.5">
                    <input
                      type="text"
                      inputMode="decimal"
                      name="fee"
                      value={form.fee}
                      onChange={handleInputChange}
                      onBlur={handleMoneyBlur}
                      autoComplete="off"
                      className="w-full rounded-lg border border-gray-300 bg-white px-4 h-8 text-base md:text-sm text-slate-800 outline-none transition-all shadow-sm"
                      placeholder="0.00"
                    />
                  </div>
                  {fieldErrors.fee && (
                    <p className="text-xs text-red-400 font-medium mt-1">
                      {fieldErrors.fee}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                    รอบค่าธรรมเนียม
                  </label>
                  <div className="relative mt-1.5">
                    <TailwindDropdown
                      options={MONTH_OPTIONS}
                      value={form.fee_due_month}
                      onChange={(e) => handleInputChange({ target: { name: "fee_due_month", value: e.target.value } })}
                      placeholder="เลือกเดือน"
                      className="w-full block"
                      searchable={false}
                      visibleOptionCount={5}
                      direction="up-mobile"
                    />
                  </div>
                  {fieldErrors.fee_due_month && (
                    <p className="text-xs text-red-400 font-medium mt-1">
                      {fieldErrors.fee_due_month}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-row gap-2.5 pt-6">
              <button
                type="button"
                onClick={cancelForm}
                className="flex-1 py-2.5 rounded-xl bg-slate-100 border border-slate-200 text-slate-600 font-bold hover:bg-slate-200 transition-all active:scale-95 text-xs"
              >
                ยกเลิก
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 text-white font-bold hover:from-sky-600 hover:to-blue-700 transition-all shadow-md active:scale-95 flex items-center justify-center disabled:opacity-50 text-xs"
              >
                {saving ? (
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  "บันทึก"
                )}
              </button>
            </div>
          </form>
        </Modal>

        {confirmDeleteId !== null && (
          <Modal
            isOpen={confirmDeleteId !== null}
            onClose={() => setConfirmDeleteId(null)}
            hideHeader
            size="sm"
            variant="center"
          >
            <div className="space-y-3">
              <div>
                <p className="text-base font-semibold text-slate-900">
                  คุณต้องการลบวิธีชำระนี้หรือไม่?
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  การลบจะไม่สามารถย้อนกลับได้
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmDeleteId(null)}
                  className="px-3 py-1 rounded-md border border-gray-200 bg-white text-sm"
                >
                  ยกเลิก
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  disabled={saving}
                  className="px-3 py-1 rounded-md bg-red-600 text-white text-sm disabled:opacity-50"
                >
                  ลบ
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

const SavingsPage = ({
  onNavigate,
  onRequestLogout,
  savings,
  owners,
  onSavingsUpdate,
}) => {
  const emptyForm = {
    bank: "",
    account_type: "",
    account_nickname: "",
    account_number: "",
    amount: "",
    deposit_start_month: "",
    deposit_months: "",
    interest_rate_annual: "",
    is_withdrawable: true,
    note: "",
    owner_id: "",
  };

  const [items, setItems] = useState(savings || []);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSavingsFilters, setShowSavingsFilters] = useState(false);
  const [filterOwnerId, setFilterOwnerId] = useState("");
  const [filterWithdrawable, setFilterWithdrawable] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [formOpen, setFormOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const INITIAL_VISIBLE_SAVINGS = 10;
  const [visibleSavingsCount, setVisibleSavingsCount] = useState(
    INITIAL_VISIBLE_SAVINGS,
  );
  const [savingsSort, setSavingsSort] = useState({
    key: "amount",
    direction: "desc",
  });
  const [showAllUpcomingMaturity, setShowAllUpcomingMaturity] = useState(false);

  useEffect(() => {
    setItems(savings || []);
  }, [savings]);

  useEffect(() => {
    setVisibleSavingsCount(INITIAL_VISIBLE_SAVINGS);
  }, [searchTerm, items, filterOwnerId, filterWithdrawable]);

  // --- Scroll Lock when modal is open ---
  useEffect(() => {
    const body = document.body;
    const docEl = document.documentElement;
    const scrollLockRef = { scrollY: 0, paddingRight: "" };

    if (formOpen || confirmDeleteId !== null) {
      const scrollY = window.scrollY || window.pageYOffset || 0;
      scrollLockRef.scrollY = scrollY;
      const scrollBarWidth = window.innerWidth - docEl.clientWidth;
      scrollLockRef.paddingRight = body.style.paddingRight;

      body.style.overflow = "hidden";
      if (scrollBarWidth > 0) body.style.paddingRight = `${scrollBarWidth}px`;
      body.style.position = "fixed";
      body.style.top = `-${scrollY}px`;
      body.style.width = "100%";
    } else {
      const y = scrollLockRef.scrollY || 0;
      body.style.overflow = "";
      body.style.paddingRight = scrollLockRef.paddingRight || "";
      body.style.position = "";
      body.style.top = "";
      body.style.width = "";
      window.scrollTo(0, y);
    }
  }, [formOpen, confirmDeleteId]);

  const parseNullableDecimal = (val) => {
    if (val === "" || val === null || val === undefined) return null;
    const cleaned = String(val).replace(/,/g, "");
    const num = Number.parseFloat(cleaned);
    return Number.isFinite(num) ? num : null;
  };

  const parseNullableInt = (val) => {
    if (val === "" || val === null || val === undefined) return null;
    const cleaned = String(val).replace(/[^\d-]/g, "");
    if (!cleaned) return null;
    const num = Number.parseInt(cleaned, 10);
    return Number.isFinite(num) ? num : null;
  };

  const formatMoneyInput = (val) => {
    if (val === "" || val === null || val === undefined) return "";
    let cleaned = String(val).replace(/,/g, "");
    cleaned = cleaned.replace(/[^\d.]/g, "");
    const firstDot = cleaned.indexOf(".");
    if (firstDot !== -1) {
      const before = cleaned.slice(0, firstDot + 1);
      const after = cleaned.slice(firstDot + 1).replace(/\./g, "");
      cleaned = before + after;
    }
    return cleaned;
  };

  const formatMoneyDisplay = (val, decimals = 2) => {
    if (val === "" || val === null || val === undefined) return "";
    const num = Number(String(val).replace(/,/g, ""));
    if (!Number.isFinite(num)) return String(val);
    return num.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const loadSavings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/savings");
      const next = res.data || [];
      setItems(next);
      onSavingsUpdate?.(next);
    } catch (e) {
      console.error("loadSavings failed", e);
      setError("โหลดข้อมูลเงินออมไม่สำเร็จ");
      showToast("โหลดข้อมูลเงินออมไม่สำเร็จ", "error");
    } finally {
      setLoading(false);
    }
  }, [onSavingsUpdate]);

  // We rely on the savings prop passed from App.js for the initial data.
  // loadSavings is still used for manual refreshes after updates if needed.

  const resetForm = () => {
    setForm(emptyForm);
    setEditingItem(null);
    setError("");
    setFieldErrors({});
  };

  const openCreateModal = () => {
    resetForm();
    setFormOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setForm({
      bank: item?.bank ?? "",
      account_type: item?.account_type ?? "",
      account_nickname: item?.account_nickname ?? "",
      account_number: item?.account_number ?? "",
      amount:
        item?.amount === null || item?.amount === undefined
          ? ""
          : formatMoneyDisplay(item.amount),
      deposit_start_month: item?.deposit_start_month ?? "",
      deposit_months:
        item?.deposit_months === null || item?.deposit_months === undefined
          ? ""
          : String(item.deposit_months),
      interest_rate_annual:
        item?.interest_rate_annual === null ||
          item?.interest_rate_annual === undefined
          ? ""
          : String(item.interest_rate_annual),
      is_withdrawable:
        item?.is_withdrawable === null || item?.is_withdrawable === undefined
          ? true
          : Boolean(Number(item.is_withdrawable)),
      note: item?.note ?? "",
      owner_id:
        item?.owner_id === null || item?.owner_id === undefined
          ? ""
          : String(item.owner_id),
    });
    setFieldErrors({});
    setError("");
    setFormOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "amount"
            ? formatMoneyInput(value)
            : name === "deposit_months"
              ? String(value).replace(/\D/g, "").slice(0, 3)
              : name === "interest_rate_annual"
                ? formatMoneyInput(value)
                : value,
    }));
    if (error) setError("");
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const clone = { ...prev };
      delete clone[name];
      return clone;
    });
  };

  const handleMoneyBlur = (e) => {
    const { name } = e.target;
    if (name === "interest_rate_annual") {
      setForm((prev) => ({
        ...prev,
        [name]: formatMoneyDisplay(prev[name], 3),
      }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      [name]: formatMoneyDisplay(prev[name]),
    }));
  };

  const validateForm = () => {
    const errs = {};
    if (!String(form.bank || "").trim()) errs.bank = "กรุณาระบุธนาคาร";
    if (!String(form.account_type || "").trim())
      errs.account_type = "กรุณาระบุประเภทบัญชี";
    if (!String(form.account_nickname || "").trim())
      errs.account_nickname = "กรุณาระบุชื่อเล่นบัญชี";
    const amount = parseNullableDecimal(form.amount);
    if (amount === null) errs.amount = "กรุณากรอกจำนวนเงิน";
    else if (amount <= 0) errs.amount = "กรุณากรอกจำนวนเงินมากกว่า 0.00";
    const months = parseNullableInt(form.deposit_months);
    if (months !== null && months < 0)
      errs.deposit_months = "จำนวนเดือนไม่ถูกต้อง";
    const rate = parseNullableDecimal(form.interest_rate_annual);
    if (rate !== null && rate < 0)
      errs.interest_rate_annual = "ดอกเบี้ยต้องไม่ติดลบ";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const saveSavings = async (e) => {
    e?.preventDefault?.();
    if (!validateForm()) {
      showToast("กรุณาตรวจสอบข้อมูลให้ครบถ้วน", "warning");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        bank: String(form.bank || "").trim(),
        account_type: String(form.account_type || "").trim() || null,
        account_nickname: String(form.account_nickname || "").trim() || null,
        account_number: String(form.account_number || "").trim() || null,
        amount: parseNullableDecimal(form.amount),
        deposit_start_month:
          String(form.deposit_start_month || "").trim() || null,
        deposit_months: parseNullableInt(form.deposit_months),
        interest_rate_annual: parseNullableDecimal(form.interest_rate_annual),
        is_withdrawable: form.is_withdrawable ? 1 : 0,
        note: String(form.note || "").trim() || null,
        owner_id: form.owner_id ? Number(form.owner_id) : null,
      };

      if (editingItem?.id) {
        await api.put(`/savings/${editingItem.id}`, payload);
        showToast("อัปเดตเงินออมเรียบร้อยแล้ว", "success");
      } else {
        await api.post("/savings", payload);
        showToast("เพิ่มเงินออมเรียบร้อยแล้ว", "success");
      }

      await loadSavings();
      resetForm();
      setFormOpen(false);
    } catch (e2) {
      console.error("saveSavings failed", e2);
      const msg =
        e2?.response?.data?.message || "บันทึกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
      setError(msg);
      showToast(msg, "error");
      setFormOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = (id) => {
    setConfirmDeleteId(id);
  };

  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      setSaving(true);
      await api.delete(`/savings/${confirmDeleteId}`);
      showToast("ลบรายการเงินออมเรียบร้อยแล้ว", "success");
      await loadSavings();
      setConfirmDeleteId(null);
    } catch (e) {
      console.error("deleteSavings failed", e);
      const msg = e?.response?.data?.message || "ลบไม่สำเร็จ";
      setError(msg);
      showToast(msg, "error");
      setConfirmDeleteId(null);
    } finally {
      setSaving(false);
    }
  };

  const ownerNameById = useMemo(() => {
    const map = new Map();
    (owners || []).forEach((o) => map.set(String(o.id), o.username));
    return (id) => map.get(String(id)) || "-";
  }, [owners]);

  const resetSavingsFilters = () => {
    setSearchTerm("");
    setFilterOwnerId("");
    setFilterWithdrawable("");
  };

  const toggleSavingsSort = (key) => {
    setSavingsSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" },
    );
  };

  const renderSavingsSortIndicator = (key) => {
    if (savingsSort.key !== key)
      return <span className="text-[10px] text-slate-400">↕</span>;
    return (
      <span className="text-[10px] text-slate-600">
        {savingsSort.direction === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  const getUpdatedTimestamp = useCallback((item) => {
    if (!item || typeof item !== "object") return null;
    const candidates = [
      item.updated_at,
      item.updatedAt,
      item.updated,
      item.created_at,
      item.createdAt,
      item.create_date,
      item.createDate,
      item.created,
    ];
    for (const value of candidates) {
      if (value === null || value === undefined || value === "") continue;
      const numeric = Number(value);
      if (Number.isFinite(numeric)) return numeric;
      const parsed = dayjs(value);
      if (parsed.isValid()) return parsed.valueOf();
    }
    return null;
  }, []);

  const isUpdatedThisMonth = useCallback(
    (item) => {
      const ts = getUpdatedTimestamp(item);
      if (!ts) return false;
      const monthStart = dayjs().tz("Asia/Bangkok").startOf("month");
      const nextMonthStart = monthStart.add(1, "month");
      const d = dayjs(ts);
      return (
        d.isValid() &&
        (d.isAfter(monthStart) || d.isSame(monthStart)) &&
        d.isBefore(nextMonthStart)
      );
    },
    [getUpdatedTimestamp],
  );

  const filteredSavingsItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return (Array.isArray(items) ? items : []).filter((it) => {
      if (filterOwnerId) {
        if (String(it?.owner_id || "") !== String(filterOwnerId)) return false;
      }
      if (filterWithdrawable !== "") {
        const flag = Boolean(Number(it?.is_withdrawable)) ? "1" : "0";
        if (flag !== String(filterWithdrawable)) return false;
      }

      if (!term) return true;
      const hay = [
        it?.bank,
        it?.account_type,
        it?.account_nickname,
        it?.account_number,
        it?.deposit_start_month,
        it?.note,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(term);
    });
  }, [items, searchTerm, filterOwnerId, filterWithdrawable]);

  const sortedSavingsItems = useMemo(() => {
    const base = Array.isArray(filteredSavingsItems)
      ? filteredSavingsItems.slice()
      : [];

    const parseNullableDecimalLocal = (val) => {
      if (val === "" || val === null || val === undefined) return null;
      const cleaned = String(val).replace(/,/g, "");
      const num = Number.parseFloat(cleaned);
      return Number.isFinite(num) ? num : null;
    };

    const parseNullableIntLocal = (val) => {
      if (val === "" || val === null || val === undefined) return null;
      const cleaned = String(val).replace(/[^\d-]/g, "");
      if (!cleaned) return null;
      const num = Number.parseInt(cleaned, 10);
      return Number.isFinite(num) ? num : null;
    };

    const toMonthTs = (yyyyMm) => {
      if (!yyyyMm) return null;
      const parsed = dayjs(`${yyyyMm}-01`);
      return parsed.isValid() ? parsed.valueOf() : null;
    };

    const maturityTs = (startMonth, depositMonths) => {
      const startTs = toMonthTs(startMonth);
      if (!startTs) return null;
      const months = parseNullableIntLocal(depositMonths);
      if (months === null || months <= 0) return null;
      return dayjs(startTs).add(months, "month").valueOf();
    };

    const getCreatedTimestamp = (item) => {
      if (!item || typeof item !== "object") return 0;
      const candidates = [
        item.created_at,
        item.createdAt,
        item.create_date,
        item.createDate,
        item.created,
      ];
      for (const value of candidates) {
        if (value === null || value === undefined || value === "") continue;
        const numeric = Number(value);
        if (Number.isFinite(numeric)) return numeric;
        const parsed = dayjs(value);
        if (parsed.isValid()) return parsed.valueOf();
      }
      const idNumeric = Number(item.id);
      return Number.isFinite(idNumeric) ? idNumeric : 0;
    };

    const compareNumbers = (a, b) => {
      const aNum = typeof a === "number" && Number.isFinite(a) ? a : null;
      const bNum = typeof b === "number" && Number.isFinite(b) ? b : null;
      if (aNum === null && bNum === null) return 0;
      if (aNum === null) return 1;
      if (bNum === null) return -1;
      return savingsSort.direction === "asc" ? aNum - bNum : bNum - aNum;
    };

    const getValue = (item) => {
      switch (savingsSort.key) {
        case "bank":
          return String(item?.bank ?? "").toLowerCase();
        case "account_type":
          return String(item?.account_type ?? "").toLowerCase();
        case "account_nickname":
          return String(item?.account_nickname ?? "").toLowerCase();
        case "account_number":
          return String(item?.account_number ?? "").toLowerCase();
        case "amount":
          return parseNullableDecimalLocal(item?.amount);
        case "deposit_start_month":
          return toMonthTs(item?.deposit_start_month);
        case "deposit_months":
          return parseNullableIntLocal(item?.deposit_months);
        case "maturity":
          return maturityTs(item?.deposit_start_month, item?.deposit_months);
        case "interest_rate_annual":
          return parseNullableDecimalLocal(item?.interest_rate_annual);
        case "is_withdrawable":
          return Boolean(Number(item?.is_withdrawable)) ? 1 : 0;
        case "updated_this_month":
          return isUpdatedThisMonth(item) ? 1 : 0;
        case "note":
          return String(item?.note ?? "").toLowerCase();
        case "owner":
          return String(
            item?.owner_id ? ownerNameById(item.owner_id) : "",
          ).toLowerCase();
        case "created":
        default:
          return getCreatedTimestamp(item);
      }
    };

    base.sort((a, b) => {
      const valA = getValue(a);
      const valB = getValue(b);
      if (typeof valA === "number" || typeof valB === "number") {
        return compareNumbers(valA, valB);
      }
      const aStr = String(valA ?? "");
      const bStr = String(valB ?? "");
      return savingsSort.direction === "asc"
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });

    return base;
  }, [
    filteredSavingsItems,
    savingsSort,
    ownerNameById,
    isUpdatedThisMonth,
  ]);

  const totalAmount = useMemo(() => {
    return (Array.isArray(filteredSavingsItems) ? filteredSavingsItems : []).reduce((sum, it) => {
      const amount = parseNullableDecimal(it?.amount);
      return amount ? sum + amount : sum;
    }, 0);
  }, [filteredSavingsItems]);

  const withdrawableAmount = useMemo(() => {
    return (Array.isArray(filteredSavingsItems) ? filteredSavingsItems : []).reduce((sum, it) => {
      if (!Boolean(Number(it?.is_withdrawable))) return sum;
      const amount = parseNullableDecimal(it?.amount);
      return amount ? sum + amount : sum;
    }, 0);
  }, [filteredSavingsItems]);

  const withdrawableCount = useMemo(() => {
    return (Array.isArray(filteredSavingsItems) ? filteredSavingsItems : []).filter((it) =>
      Boolean(Number(it?.is_withdrawable)),
    ).length;
  }, [filteredSavingsItems]);

  const ownerBreakdown = useMemo(() => {
    const list = Array.isArray(filteredSavingsItems) ? filteredSavingsItems : [];
    const totalsByOwner = new Map(); // key: owner_id string ("" for unassigned)

    list.forEach((it) => {
      const ownerKey =
        it?.owner_id === null || it?.owner_id === undefined
          ? ""
          : String(it.owner_id);
      const amount = parseNullableDecimal(it?.amount) || 0;
      const isWithdrawable = Boolean(Number(it?.is_withdrawable));

      const prev =
        totalsByOwner.get(ownerKey) || {
          ownerKey,
          totalAmount: 0,
          withdrawableAmount: 0,
          count: 0,
          withdrawableCount: 0,
        };

      prev.count += 1;
      prev.totalAmount += amount;
      if (isWithdrawable) {
        prev.withdrawableCount += 1;
        prev.withdrawableAmount += amount;
      }

      totalsByOwner.set(ownerKey, prev);
    });

    return Array.from(totalsByOwner.values()).sort(
      (a, b) => b.totalAmount - a.totalAmount,
    );
  }, [filteredSavingsItems]);

  const upcomingMaturity = useMemo(() => {
    const WINDOW_DAYS = 60;
    const today = dayjs().tz("Asia/Bangkok").startOf("day");
    const list = Array.isArray(filteredSavingsItems) ? filteredSavingsItems : [];
    const rows = list
      .map((it) => {
        const startMonth = it?.deposit_start_month;
        const months = parseNullableInt(it?.deposit_months);
        if (!startMonth || months === null || months <= 0) return null;
        const start = dayjs(`${startMonth}-01`).tz("Asia/Bangkok").startOf("month");
        if (!start.isValid()) return null;
        const maturity = start.add(months, "month").startOf("month");
        const daysLeft = maturity.diff(today, "day");
        if (daysLeft < 0) return null; // already matured
        if (daysLeft > WINDOW_DAYS) return null;
        return { item: it, maturity, daysLeft };
      })
      .filter(Boolean)
      .sort((a, b) => a.maturity.valueOf() - b.maturity.valueOf());

    const initial = rows.slice(0, 5);
    return { windowDays: WINDOW_DAYS, all: rows, initial };
  }, [filteredSavingsItems]);

  const formatMonthYear = (yyyyMm) => {
    if (!yyyyMm) return "-";
    const parsed = dayjs(`${yyyyMm}-01`);
    return parsed.isValid() ? parsed.format("MMM YYYY") : String(yyyyMm);
  };

  const calcMaturityMonth = (startMonth, depositMonths) => {
    if (!startMonth) return "-";
    const months = parseNullableInt(depositMonths);
    if (months === null || months <= 0) return "-";
    const start = dayjs(`${startMonth}-01`);
    if (!start.isValid()) return "-";
    return start.add(months, "month").format("MMM YYYY");
  };

  useEffect(() => {
    const handlePlus = () => setFormOpen(true);
    window.addEventListener("app-plus-clicked", handlePlus);
    return () => window.removeEventListener("app-plus-clicked", handlePlus);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 text-slate-800">
      <div className="px-4 pt-2 pb-24 sm:px-6 sm:py-3 lg:px-8 lg:py-4">

        <main className="w-full space-y-6 pt-4">
          <section className="w-full">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-700">
                  ภาพรวมเงินออม
                </h3>
                <div className="mt-2 grid grid-cols-2 gap-3 text-sm text-slate-600">
                  <div>
                    <p className="text-xs text-slate-500">ยอดเงินทั้งหมด</p>
                    <p className="text-sm font-semibold text-slate-800">
                      {Number(totalAmount || 0).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      ฿
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">ยอดเงินที่ถอนได้</p>
                    <p className="text-sm font-semibold text-slate-800">
                      {Number(withdrawableAmount || 0).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      ฿
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">จำนวนรายการทั้งหมด</p>
                    <p className="text-sm font-semibold text-slate-800">
                      {filteredSavingsItems.length}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">จำนวนรายการที่ถอนได้</p>
                    <p className="text-sm font-semibold text-slate-800">
                      {withdrawableCount}
                    </p>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-700">
                  แยกตามเจ้าของ
                </h3>
                <div className="mt-2 max-h-[260px] overflow-auto pr-1">
                  <table className="min-w-full text-xs">
                    <thead className="text-slate-500">
                      <tr>
                        <th className="py-1 pr-2 text-left font-semibold">
                          เจ้าของ
                        </th>
                        <th className="py-1 pr-2 text-right font-semibold whitespace-nowrap">
                          ยอดรวม
                        </th>
                        <th className="py-1 text-right font-semibold whitespace-nowrap">
                          ถอนได้
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {ownerBreakdown.length ? (
                        ownerBreakdown.map((row) => {
                          const ownerLabel = row.ownerKey
                            ? ownerNameById(row.ownerKey)
                            : "ไม่ระบุ";
                          return (
                            <tr key={row.ownerKey || "unassigned"}>
                              <td className="py-1 pr-2 text-slate-700 max-w-[160px] truncate">
                                {ownerLabel}
                              </td>
                              <td className="py-1 pr-2 text-right tabular-nums font-semibold text-slate-800 whitespace-nowrap">
                                {Number(row.totalAmount || 0).toLocaleString(
                                  "en-US",
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  },
                                )}{" "}
                                ฿
                              </td>
                              <td className="py-1 text-right tabular-nums font-semibold text-slate-800 whitespace-nowrap">
                                {Number(
                                  row.withdrawableAmount || 0,
                                ).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}{" "}
                                ฿
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan={3}
                            className="py-3 text-center text-slate-400"
                          >
                            ยังไม่มีข้อมูล
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-700">
                  ครบกำหนดใกล้ถึง
                </h3>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-slate-500">
                    ภายใน {upcomingMaturity.windowDays} วัน:{" "}
                    <span className="font-semibold text-slate-800">
                      {upcomingMaturity.all.length}
                    </span>
                  </span>
                  {loading && (
                    <span className="text-xs text-slate-400">กำลังโหลด...</span>
                  )}
                </div>

                {error && (
                  <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    {error}
                  </div>
                )}

                <div className="mt-3 space-y-2">
                  {(showAllUpcomingMaturity
                    ? upcomingMaturity.all
                    : upcomingMaturity.initial
                  ).length ? (
                    (showAllUpcomingMaturity
                      ? upcomingMaturity.all
                      : upcomingMaturity.initial
                    ).map(({ item, maturity, daysLeft }) => (
                      <div
                        key={item.id ?? `${item.bank}-${item.account_number}-${maturity.valueOf()}`}
                        className="flex items-start justify-between gap-2"
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-slate-700 truncate">
                            {item?.account_nickname || item?.bank || "-"}
                          </p>
                          <p className="text-[11px] text-slate-400 truncate">
                            {item?.bank || "-"} • {maturity.format("MMM YYYY")}
                          </p>
                        </div>
                        <span className="text-xs text-slate-600 whitespace-nowrap">
                          {daysLeft === 0 ? "วันนี้" : `อีก ${daysLeft} วัน`}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-slate-400">
                      ยังไม่มีรายการที่ใกล้ครบกำหนด
                    </p>
                  )}
                </div>

                {upcomingMaturity.all.length >
                  (upcomingMaturity.initial?.length ?? 0) && (
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => setShowAllUpcomingMaturity((p) => !p)}
                        className="mt-2 text-xs text-sky-600 hover:underline"
                      >
                        {showAllUpcomingMaturity
                          ? "แสดงน้อยลง"
                          : `ดูทั้งหมด ${upcomingMaturity.all.length} รายการ`}
                      </button>
                    </div>
                  )}
              </div>
            </div>
          </section>

          <section className="w-full bg-white rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6">
            <header className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-semibold text-slate-800">
                  รายการเงินออม
                </h2>
                <p className="text-xs text-slate-500 mt-0.5 mb-2">
                  ทั้งหมด {sortedSavingsItems.length} รายการ
                </p>
              </div>
              <button
                type="button"
                onClick={loadSavings}
                className="p-2 rounded-full bg-white border border-gray-200 hover:bg-gray-50 text-slate-700 transition-colors"
                title="รีเฟรช"
                aria-label="รีเฟรช"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </header>

            <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-2">
              <button
                type="button"
                onClick={() => setShowSavingsFilters((prev) => !prev)}
                aria-expanded={showSavingsFilters}
                aria-controls="savings-filters"
                className="flex items-center space-x-1 text-[10px] md:text-sm bg-white border border-gray-300 text-slate-700 hover:bg-gray-50 font-bold py-1 px-2 rounded-lg transition-colors"
              >
                <Filter size={14} />
                <span>Filter</span>
                <ChevronDown
                  size={14}
                  className={`text-slate-500 transition-transform ${showSavingsFilters ? "rotate-180" : ""}`}
                />
              </button>
              <button
                type="button"
                onClick={openCreateModal}
                className="hidden md:flex items-center space-x-1 text-[10px] md:text-sm bg-gradient-to-r from-sky-500 to-blue-500 hover:from-sky-600 hover:to-blue-600 text-white font-bold py-1 px-2 rounded-lg transition-colors shadow"
              >
                <Plus size={14} />
                <span>เพิ่มเงินออม</span>
              </button>
            </div>

            <div
              id="savings-filters"
              className={`${showSavingsFilters ? "mt-2" : "max-h-0 overflow-hidden"} transition-all duration-200`}
            >
              {showSavingsFilters && (
                <div className="w-full lg:w-1/2 lg:max-w-[50%]">
                  <div className="flex flex-wrap items-end gap-1 sm:gap-1.5 md:gap-2 mb-4">
                    <div className="flex-1 min-w-[180px] space-y-1">
                      <label className="block text-sm font-medium text-slate-400">
                        ค้นหา
                      </label>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full inline-flex justify-between rounded-lg border border-gray-300 bg-white text-slate-800 text-base md:text-sm px-4 py-1 h-8 outline-none transition-all"
                        placeholder="ธนาคาร / ชื่อเล่น / เลขบัญชี / หมายเหตุ"
                      />
                    </div>
                    <div className="w-44 space-y-1">
                      <label className="block text-sm font-medium text-slate-400">
                        ถอนได้
                      </label>
                      <TailwindDropdown
                        options={[
                          { id: "", name: "All" },
                          { id: "1", name: "ได้" },
                          { id: "0", name: "ไม่ได้" },
                        ]}
                        value={filterWithdrawable}
                        onChange={(e) => setFilterWithdrawable(e.target.value)}
                        className="filterWithdrawable"
                        searchable={false}
                      />
                    </div>
                    <div className="w-44 space-y-1">
                      <label className="block text-sm font-medium text-slate-400">
                        เจ้าของ
                      </label>
                      <TailwindDropdown
                        options={[
                          { id: "", username: "All" },
                          ...(owners || []),
                        ]}
                        value={filterOwnerId}
                        onChange={(e) => setFilterOwnerId(e.target.value)}
                        className="filterOwnerId"
                        optionLabel="username"
                        optionValue="id"
                        searchable={false}
                      />
                    </div>
                    <div className="flex items-end gap-1 sm:gap-1.5 md:gap-2">
                      <button
                        type="button"
                        onClick={resetSavingsFilters}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-1.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 hover:border-slate-400 transition"
                      >
                        <XCircle size={14} />
                        <span>ล้าง</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {sortedSavingsItems.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm">
                {items.length > 0
                  ? "ไม่พบรายการที่ตรงกับเงื่อนไข"
                  : "ยังไม่มีข้อมูลเงินออม"}
              </div>
            ) : (
              <>
                {loading && (
                  <div className="text-center py-2">
                    <span className="text-[10px] text-slate-400 animate-pulse">กำลังอัปเดตข้อมูล...</span>
                  </div>
                )}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full text-sm table-fixed">
                    <colgroup>
                      <col className="w-[11%]" /> {/* ธนาคาร */}
                      <col className="w-[10%]" /> {/* ประเภทบัญชี */}
                      <col className="w-[12%]" /> {/* ชื่อเล่นบัญชี */}
                      <col className="w-[10%]" /> {/* เลขที่บัญชี */}
                      <col className="w-[10%]" /> {/* จำนวนเงิน */}
                      <col className="w-[9%]" /> {/* เดือนเริ่มฝาก */}
                      <col className="w-[8%]" /> {/* จำนวนเดือนฝาก */}
                      <col className="w-[9%]" /> {/* เดือนที่ครบกำหนด */}
                      <col className="w-[8%]" /> {/* ดอกเบี้ย/ปี */}
                      <col className="w-[6%]" /> {/* ถอนได้ */}
                      <col className="w-[7%]" /> {/* อัปเดต */}
                      <col className="w-[9%]" /> {/* หมายเหตุ */}
                      <col className="w-[5%]" /> {/* เจ้าของ */}
                      <col className="w-[2%]" /> {/* Actions */}
                    </colgroup>
                    <thead className="border-b border-gray-200 text-slate-600 text-sm bg-gray-50">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-semibold">
                          <button
                            type="button"
                            onClick={() => toggleSavingsSort("bank")}
                            className="flex items-center gap-1"
                          >
                            <span>ธนาคาร</span>
                            {renderSavingsSortIndicator("bank")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-left font-semibold">
                          <button
                            type="button"
                            onClick={() => toggleSavingsSort("account_type")}
                            className="flex items-center gap-1"
                          >
                            <span>ประเภทบัญชี</span>
                            {renderSavingsSortIndicator("account_type")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-left font-semibold">
                          <button
                            type="button"
                            onClick={() => toggleSavingsSort("account_nickname")}
                            className="flex items-center gap-1"
                          >
                            <span>ชื่อเล่นบัญชี</span>
                            {renderSavingsSortIndicator("account_nickname")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-left font-semibold">
                          <button
                            type="button"
                            onClick={() => toggleSavingsSort("account_number")}
                            className="flex items-center gap-1"
                          >
                            <span>เลขที่บัญชี</span>
                            {renderSavingsSortIndicator("account_number")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => toggleSavingsSort("amount")}
                            className="flex items-center gap-1 w-full justify-end"
                          >
                            <span>จำนวนเงิน</span>
                            {renderSavingsSortIndicator("amount")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-center font-semibold whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() =>
                              toggleSavingsSort("deposit_start_month")
                            }
                            className="flex items-center gap-1 mx-auto"
                          >
                            <span>เดือนเริ่มฝาก</span>
                            {renderSavingsSortIndicator("deposit_start_month")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => toggleSavingsSort("deposit_months")}
                            className="flex items-center gap-1 w-full justify-end"
                          >
                            <span>เดือนฝาก</span>
                            {renderSavingsSortIndicator("deposit_months")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-center font-semibold whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => toggleSavingsSort("maturity")}
                            className="flex items-center gap-1 mx-auto"
                          >
                            <span>ครบกำหนด</span>
                            {renderSavingsSortIndicator("maturity")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() =>
                              toggleSavingsSort("interest_rate_annual")
                            }
                            className="flex items-center gap-1 w-full justify-end"
                          >
                            <span>ดอกเบี้ย/ปี</span>
                            {renderSavingsSortIndicator("interest_rate_annual")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-center font-semibold whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => toggleSavingsSort("is_withdrawable")}
                            className="flex items-center gap-1 mx-auto"
                          >
                            <span>ถอนได้</span>
                            {renderSavingsSortIndicator("is_withdrawable")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-center font-semibold whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => toggleSavingsSort("updated_this_month")}
                            className="flex items-center gap-1 mx-auto"
                          >
                            <span>อัปเดต</span>
                            {renderSavingsSortIndicator("updated_this_month")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-left font-semibold">
                          <button
                            type="button"
                            onClick={() => toggleSavingsSort("note")}
                            className="flex items-center gap-1"
                          >
                            <span>หมายเหตุ</span>
                            {renderSavingsSortIndicator("note")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-left font-semibold">
                          <button
                            type="button"
                            onClick={() => toggleSavingsSort("owner")}
                            className="flex items-center gap-1"
                          >
                            <span>เจ้าของ</span>
                            {renderSavingsSortIndicator("owner")}
                          </button>
                        </th>
                        <th className="px-2 py-1.5 text-right font-semibold">
                          <button
                            type="button"
                            onClick={() => toggleSavingsSort("created")}
                            className="flex items-center gap-1 w-full justify-end"
                          >
                            <span>จัดการ</span>
                            {renderSavingsSortIndicator("created")}
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {sortedSavingsItems
                        .slice(0, visibleSavingsCount)
                        .map((it) => (
                          <tr key={it.id} className="hover:bg-slate-50">
                            <td className="px-2 py-2 text-slate-700 truncate">
                              {it?.bank || "-"}
                            </td>
                            <td className="px-2 py-2 text-slate-700 truncate">
                              {it?.account_type || "-"}
                            </td>
                            <td className="px-2 py-2 font-medium text-slate-800 truncate">
                              {it?.account_nickname || "-"}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-slate-600 truncate">
                              {it?.account_number || "-"}
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap font-semibold text-slate-800">
                              {it?.amount === null || it?.amount === undefined
                                ? "-"
                                : Number(it.amount).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                            </td>
                            <td className="px-2 py-2 text-center whitespace-nowrap">
                              {formatMonthYear(it?.deposit_start_month)}
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">
                              {it?.deposit_months === null ||
                                it?.deposit_months === undefined
                                ? "-"
                                : it.deposit_months}
                            </td>
                            <td className="px-2 py-2 text-center whitespace-nowrap">
                              {calcMaturityMonth(
                                it?.deposit_start_month,
                                it?.deposit_months,
                              )}
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">
                              {it?.interest_rate_annual === null ||
                                it?.interest_rate_annual === undefined
                                ? "-"
                                : `${formatMoneyDisplay(it.interest_rate_annual, 3)}%`}
                            </td>
                            <td className="px-2 py-2 text-center whitespace-nowrap">
                              {Boolean(Number(it?.is_withdrawable)) ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700">
                                  ได้
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">
                                  ไม่ได้
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-2 text-center whitespace-nowrap">
                              {isUpdatedThisMonth(it) ? (
                                <span
                                  className="inline-flex items-center justify-center"
                                  title="อัปเดตแล้ว"
                                  aria-label="อัปเดตแล้ว"
                                  role="img"
                                >
                                  <CheckCircle
                                    aria-hidden="true"
                                    className="h-4 w-4 text-emerald-600"
                                  />
                                </span>
                              ) : (
                                <span
                                  className="inline-flex items-center justify-center"
                                  title="ยังไม่อัปเดต"
                                  aria-label="ยังไม่อัปเดต"
                                  role="img"
                                >
                                  <XCircle
                                    aria-hidden="true"
                                    className="h-4 w-4 text-slate-400"
                                  />
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-2 text-slate-700 truncate">
                              {it?.note || "-"}
                            </td>
                            <td className="px-2 py-2 text-slate-700 truncate">
                              {it?.owner_id ? ownerNameById(it.owner_id) : "-"}
                            </td>
                            <td className="px-2 py-2 text-right whitespace-nowrap">
                              <div className="inline-flex">
                                <button
                                  type="button"
                                  onClick={() => openEditModal(it)}
                                  className="p-1 text-slate-400 hover:text-sky-400"
                                  aria-label="แก้ไขเงินออม"
                                  title="แก้ไข"
                                >
                                  <SquarePen className="h-[14px] w-[14px]" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => requestDelete(it.id)}
                                  className="p-1 text-slate-400 hover:text-red-400"
                                  aria-label="ลบเงินออม"
                                  title="ลบ"
                                >
                                  <Trash className="h-[14px] w-[14px]" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                <div className="md:hidden space-y-3">
                  {sortedSavingsItems
                    .slice(0, visibleSavingsCount)
                    .map((it) => (
                      <div
                        key={it.id}
                        className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-800 truncate inline-flex items-center gap-1 min-w-0">
                              {isUpdatedThisMonth(it) ? (
                                <span
                                  className="inline-flex items-center justify-center shrink-0"
                                  title="อัปเดตแล้ว"
                                  aria-label="อัปเดตแล้ว"
                                  role="img"
                                >
                                  <CheckCircle
                                    aria-hidden="true"
                                    className="h-4 w-4 text-emerald-600"
                                  />
                                </span>
                              ) : (
                                <span
                                  className="inline-flex items-center justify-center shrink-0"
                                  title="ยังไม่อัปเดต"
                                  aria-label="ยังไม่อัปเดต"
                                  role="img"
                                >
                                  <XCircle
                                    aria-hidden="true"
                                    className="h-4 w-4 text-slate-400"
                                  />
                                </span>
                              )}
                              <span className="truncate">
                                {(it?.bank || "-") + ": " + (it?.account_nickname || "-")}
                              </span>
                            </p>
                            <p className="mt-0.5 text-xs text-slate-500 truncate">
                              {`${it?.account_type || "-"} (${it?.account_number || "-"})`}
                            </p>
                            {/* (removed) */}
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-slate-800 tabular-nums">
                              {it?.amount === null || it?.amount === undefined
                                ? "-"
                                : Number(it.amount).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}{" "}
                              ฿
                            </p>
                            <p className="mt-0.5 text-[11px] text-slate-500">
                              {Boolean(Number(it?.is_withdrawable))
                                ? "ถอนได้"
                                : "ถอนไม่ได้"}
                            </p>
                          </div>
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-slate-500">เริ่มฝาก</span>
                            <span className="font-medium text-slate-800">
                              {formatMonthYear(it?.deposit_start_month)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-slate-500">เดือนฝาก</span>
                            <span className="font-medium text-slate-800">
                              {it?.deposit_months ?? "-"}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-slate-500">ครบกำหนด</span>
                            <span className="font-medium text-slate-800">
                              {calcMaturityMonth(
                                it?.deposit_start_month,
                                it?.deposit_months,
                              )}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-slate-500">ดอกเบี้ย/ปี</span>
                            <span className="font-medium text-slate-800">
                              {it?.interest_rate_annual === null ||
                                it?.interest_rate_annual === undefined
                                ? "-"
                                : `${formatMoneyDisplay(it.interest_rate_annual, 3)}%`}
                            </span>
                          </div>
                          {it?.note ? (
                            <div className="col-span-2 text-[11px] text-slate-500 truncate">
                              หมายเหตุ: {it.note}
                            </div>
                          ) : null}
                        </div>

                        <div className="flex justify-between items-center mt-3">
                          <div className="inline-flex">
                            <button
                              type="button"
                              onClick={() => openEditModal(it)}
                              className="p-1 text-slate-400 hover:text-sky-400"
                              aria-label="แก้ไขเงินออม"
                            >
                              <SquarePen className="h-[14px] w-[14px]" />
                            </button>
                            <button
                              type="button"
                              onClick={() => requestDelete(it.id)}
                              className="p-1 text-slate-400 hover:text-red-400"
                              aria-label="ลบเงินออม"
                            >
                              <Trash className="h-[14px] w-[14px]" />
                            </button>
                          </div>
                          <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700 truncate max-w-[55%]">
                            {it?.owner_id ? ownerNameById(it.owner_id) : "-"}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>

                {sortedSavingsItems.length > visibleSavingsCount && (
                  <div className="mt-4 flex justify-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setVisibleSavingsCount((prev) =>
                          Math.min(
                            prev + INITIAL_VISIBLE_SAVINGS,
                            sortedSavingsItems.length,
                          ),
                        )
                      }
                      className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white text-slate-700 hover:bg-gray-50"
                    >
                      Load more
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setVisibleSavingsCount(sortedSavingsItems.length)
                      }
                      className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white text-slate-700 hover:bg-gray-50"
                    >
                      All ({sortedSavingsItems.length})
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        </main>

        <Modal
          isOpen={formOpen}
          onClose={() => setFormOpen(false)}
          title={editingItem ? "แก้ไขเงินออม" : "เพิ่มเงินออม"}
          size="lg"
          variant="bottom"
        >
          <form onSubmit={saveSavings} className="px-1 md:px-2 py-2 space-y-6">
            <div className="space-y-5 pb-4 md:pb-6">
              {/* Row 1: Bank & Account Type */}
              <div className="grid grid-cols-2 gap-3 md:gap-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                    ธนาคาร
                  </label>
                  <div className="relative mt-1.5">
                    <input
                      name="bank"
                      value={form.bank}
                      onChange={handleInputChange}
                      autoComplete="off"
                      className={`w-full rounded-lg border px-4 h-8 text-base md:text-sm outline-none transition-all bg-white text-slate-800 shadow-sm ${fieldErrors.bank ? "border-red-500" : "border-gray-300"}`}
                      placeholder="เช่น KBank, SCB"
                    />
                  </div>
                  {fieldErrors.bank && (
                    <p className="mt-1 text-xs text-red-400 font-medium">{fieldErrors.bank}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                    ประเภทบัญชี
                  </label>
                  <div className="relative mt-1.5">
                    <input
                      name="account_type"
                      value={form.account_type}
                      onChange={handleInputChange}
                      autoComplete="off"
                      className={`w-full rounded-lg border bg-white px-4 h-8 text-base md:text-sm text-slate-800 outline-none transition-all shadow-sm ${fieldErrors.account_type ? "border-red-500" : "border-gray-300"}`}
                      placeholder="ออมทรัพย์ / ฝากประจำ / ฯลฯ"
                    />
                  </div>
                  {fieldErrors.account_type && (
                    <p className="mt-1 text-xs text-red-400 font-medium">
                      {fieldErrors.account_type}
                    </p>
                  )}
                </div>
              </div>

              {/* Row 2: Nickname & Account Number */}
              <div className="grid grid-cols-2 gap-3 md:gap-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                    ชื่อเล่นบัญชี
                  </label>
                  <div className="relative mt-1.5">
                    <input
                      name="account_nickname"
                      value={form.account_nickname}
                      onChange={handleInputChange}
                      autoComplete="off"
                      className={`w-full rounded-lg border px-4 h-8 text-base md:text-sm outline-none transition-all bg-white text-slate-800 shadow-sm ${fieldErrors.account_nickname ? "border-red-500" : "border-gray-300"}`}
                      placeholder="เช่น เงินสำรอง, ฝากประจำบ้าน"
                    />
                  </div>
                  {fieldErrors.account_nickname && (
                    <p className="mt-1 text-xs text-red-400 font-medium">
                      {fieldErrors.account_nickname}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                    เลขที่บัญชี
                  </label>
                  <div className="relative mt-1.5">
                    <input
                      name="account_number"
                      value={form.account_number}
                      onChange={handleInputChange}
                      autoComplete="off"
                      className="w-full rounded-lg border border-gray-300 bg-white px-4 h-8 text-base md:text-sm text-slate-800 outline-none transition-all shadow-sm"
                      placeholder="เช่น 123-4-56789-0"
                    />
                  </div>
                </div>
              </div>

              {/* Row 3: Amount & Start Month */}
              <div className="grid grid-cols-2 gap-3 md:gap-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                    จำนวนเงิน
                  </label>
                  <div className="relative mt-1.5">
                    <input
                      name="amount"
                      value={form.amount}
                      onChange={handleInputChange}
                      onBlur={handleMoneyBlur}
                      autoComplete="off"
                      className={`w-full rounded-lg border px-4 h-8 text-base md:text-sm outline-none transition-all bg-white text-slate-800 shadow-sm ${fieldErrors.amount ? "border-red-500" : "border-gray-300"}`}
                      placeholder="0.00"
                      inputMode="decimal"
                    />
                  </div>
                  {fieldErrors.amount && (
                    <p className="mt-1 text-xs text-red-400 font-medium">{fieldErrors.amount}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                    เดือนเริ่มฝาก
                  </label>
                  <div className="relative mt-1.5">
                    <MonthYearPicker
                      label=""
                      name="deposit_start_month"
                      value={form.deposit_start_month}
                      onChange={handleInputChange}
                      className="w-full deposit_start_month"
                      variant="form"
                    />
                  </div>
                </div>
              </div>

              {/* Row 4: Deposit Months & Interest Rate */}
              <div className="grid grid-cols-2 gap-3 md:gap-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                    จำนวนเดือนฝาก
                  </label>
                  <div className="relative mt-1.5">
                    <input
                      name="deposit_months"
                      value={form.deposit_months}
                      onChange={handleInputChange}
                      autoComplete="off"
                      className={`w-full rounded-lg border px-4 h-8 text-base md:text-sm outline-none transition-all bg-white text-slate-800 shadow-sm ${fieldErrors.deposit_months ? "border-red-500" : "border-gray-300"}`}
                      placeholder="เช่น 12"
                      inputMode="numeric"
                    />
                  </div>
                  {fieldErrors.deposit_months && (
                    <p className="mt-1 text-xs text-red-400 font-medium">
                      {fieldErrors.deposit_months}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                    ดอกเบี้ยต่อปี (%)
                  </label>
                  <div className="relative mt-1.5">
                    <input
                      name="interest_rate_annual"
                      value={form.interest_rate_annual}
                      onChange={handleInputChange}
                      onBlur={handleMoneyBlur}
                      autoComplete="off"
                      className={`w-full rounded-lg border px-4 h-8 text-base md:text-sm outline-none transition-all bg-white text-slate-800 shadow-sm ${fieldErrors.interest_rate_annual ? "border-red-500" : "border-gray-300"}`}
                      placeholder="เช่น 1.250"
                      inputMode="decimal"
                    />
                  </div>
                  {fieldErrors.interest_rate_annual && (
                    <p className="mt-1 text-xs text-red-400 font-medium">
                      {fieldErrors.interest_rate_annual}
                    </p>
                  )}
                </div>
              </div>

              {/* Row 5: Owner & Withdrawable */}
              <div className="grid grid-cols-2 gap-3 md:gap-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                    เจ้าของ
                  </label>
                  <div className="relative mt-1.5">
                    <TailwindDropdown
                      options={[{ id: "", username: "เลือกเจ้าของ" }, ...(owners || [])]}
                      value={form.owner_id}
                      onChange={(e) => handleInputChange({ target: { name: "owner_id", value: e.target.value } })}
                      className="owner_id w-full block"
                      optionLabel="username"
                      optionValue="id"
                      searchable={false}
                      direction="up-mobile"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                    ถอนได้หรือไม่
                  </label>
                  <div className="flex items-center gap-3 h-9 mt-1.5">
                    <label className="relative flex items-center cursor-pointer group">
                      <input
                        type="checkbox"
                        name="is_withdrawable"
                        checked={!!form.is_withdrawable}
                        onChange={handleInputChange}
                        className="peer sr-only"
                      />
                      <div className="w-10 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-sky-600"></div>
                      <span className="ms-3 text-sm font-medium text-slate-600 group-hover:text-slate-900 transition-colors">
                        {form.is_withdrawable ? "ถอนได้" : "ถอนไม่ได้"}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                  หมายเหตุ
                </label>
                <div className="relative mt-1.5">
                  <textarea
                    name="note"
                    value={form.note}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-1.5 h-10 text-base md:text-sm text-slate-800 outline-none transition-all shadow-sm resize-none"
                    placeholder="เพิ่มเติม"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-row gap-2.5 pt-4">
              <button
                type="button"
                onClick={() => setFormOpen(false)}
                className="flex-1 py-2.5 rounded-xl bg-slate-100 border border-slate-200 text-slate-600 font-bold hover:bg-slate-200 transition-all active:scale-95 text-xs"
              >
                ยกเลิก
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 text-white font-bold hover:from-sky-600 hover:to-blue-700 transition-all shadow-md active:scale-95 flex items-center justify-center disabled:opacity-50 text-xs"
              >
                {saving ? (
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  "บันทึก"
                )}
              </button>
            </div>
          </form>
        </Modal>

        {confirmDeleteId !== null && (
          <Modal
            isOpen={confirmDeleteId !== null}
            onClose={() => setConfirmDeleteId(null)}
            hideHeader
            size="sm"
            variant="center"
          >
            <div className="space-y-3">
              <div>
                <p className="text-base font-semibold text-slate-900">
                  คุณต้องการลบรายการเงินออมนี้หรือไม่?
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  การลบจะไม่สามารถย้อนกลับได้
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmDeleteId(null)}
                  className="px-3 py-1 rounded-md border border-gray-200 bg-white text-sm"
                >
                  ยกเลิก
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  disabled={saving}
                  className="px-3 py-1 rounded-md bg-red-600 text-white text-sm disabled:opacity-50"
                >
                  ลบ
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

// Tailwind Dropdown Component
const TailwindDropdown = ({
  label,
  options,
  value,
  onChange,
  placeholder,
  className = "",
  optionLabel = "name",
  optionValue = "id",
  required = false,
  disabled = false,
  renderOption,
  searchable = true,
  visibleOptionCount = 5,
  direction = "down",
}) => {
  const [open, setOpen] = useState(false);
  const dropdownId = React.useId();
  const [autoDir, setAutoDir] = useState("down");

  React.useEffect(() => {
    const handleOtherOpen = (e) => {
      if (e.detail && e.detail.id !== dropdownId) {
        setOpen(false);
      }
    };
    window.addEventListener("dropdown:open", handleOtherOpen);
    return () => window.removeEventListener("dropdown:open", handleOtherOpen);
  }, [dropdownId]);
  const [searchTerm, setSearchTerm] = useState("");
  const showSearch =
    searchable && Array.isArray(options) && options.length > 12;
  const searchKey = optionLabel; // which field to search on
  const filteredOptions = React.useMemo(() => {
    if (!showSearch) return options;
    const q = String(searchTerm || "").toLowerCase();
    if (!q) return options;
    return options.filter((opt) =>
      String(opt[searchKey] ?? "")
        .toLowerCase()
        .includes(q),
    );
  }, [options, searchTerm, searchKey, showSearch]);
  const selectedOption =
    options.find((opt) => String(opt[optionValue]) === String(value)) || null;
  const handleSelect = (opt) => {
    setOpen(false);
    if (onChange) {
      // Fake event for compatibility with handleChange signature
      onChange({
        target: {
          name: className, // pass name via className prop
          value: opt ? opt[optionValue] : "",
        },
      });
    }
  };
  // Global click listener to close dropdowns
  React.useEffect(() => {
    const handler = (e) => {
      setOpen(false);
    };
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, []);
  React.useEffect(() => {
    if (!open && showSearch) setSearchTerm("");
  }, [open, showSearch]);
  // Input style: bg-slate-700 border border-slate-600 rounded-lg  outline-none transition-all text-white p-3
  // Dropdown item style: text-sm text-gray-900 hover:bg-gray-100 w-full px-4 py-2 text-left

  const finalDirection = direction === "auto" ? autoDir : direction;

  return (
    <div
      className={`relative inline-block text-left w-full ${open ? "z-[1000]" : "z-10"}`}
    >
      {label && (
        <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
          {label}
        </label>
      )}
      <div className={`relative ${label ? "mt-1" : ""}`}>
        <button
          type="button"
          className={`inline-flex items-center justify-between w-full rounded-lg border border-gray-300 bg-white text-slate-700 px-4 h-8 outline-none transition-all shadow-sm ${className.includes("text-") ? "" : "text-sm"} ${className} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          aria-haspopup="true"
          aria-expanded={open}
          onClick={(e) => {
            e.stopPropagation();
            if (!open && direction === "auto") {
              const rect = e.currentTarget.getBoundingClientRect();
              const spaceBelow = window.innerHeight - rect.bottom;
              const spaceAbove = rect.top;
              // If less than 200px below and more space above, open up
              setAutoDir(spaceBelow < 250 && spaceAbove > spaceBelow ? "up" : "down");
            }
            setOpen((prev) => {
              const next = !prev;
              if (next) {
                window.dispatchEvent(
                  new CustomEvent("dropdown:open", {
                    detail: { id: dropdownId },
                  }),
                );
              }
              return next;
            });
          }}
          tabIndex={0}
        >
          <span className="truncate whitespace-nowrap pr-1">
            {selectedOption
              ? renderOption
                ? renderOption(selectedOption)
                : selectedOption[optionLabel]
              : placeholder}
          </span>
          <svg
            className="-mr-1 ml-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.584l3.71-4.353a.75.75 0 111.14.976l-4.25 5a.75.75 0 01-1.14 0l-4.25-5a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        {open && (
          <div
            className={`absolute left-0 right-0 w-full rounded-xl shadow-2xl bg-white ring-1 ring-black/10 focus:outline-none z-[1100] overflow-hidden ${finalDirection === "up"
              ? "bottom-full mb-1"
              : finalDirection === "up-mobile"
                ? "bottom-full mb-1 md:bottom-auto md:top-full md:mt-1"
                : "mt-1"
              }`}
            onClick={(e) => e.stopPropagation()}
          >
            {showSearch && (
              <div className="p-2 border-b border-gray-100 bg-slate-50/80 sticky top-0 backdrop-blur-sm">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-slate-400">
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    placeholder="ค้นหา..."
                    inputMode="search"
                    className="w-full rounded-lg border border-gray-200 bg-white pl-8 pr-3 py-1.5 text-base md:text-sm text-slate-700 outline-none  transition-all"
                  />
                </div>
              </div>
            )}
            <div
              className={`py-1 overflow-y-auto overscroll-contain ${visibleOptionCount ? "" : "max-h-36 md:max-h-48"}`}
              style={
                visibleOptionCount
                  ? { maxHeight: `${Math.max(1, visibleOptionCount) * 30}px` }
                  : undefined
              }
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt) => {
                  const isSelected = String(value) === String(opt[optionValue]);
                  return (
                    <button
                      type="button"
                      key={opt[optionValue]}
                      className={`flex items-center justify-between w-full px-4 py-1.5 text-xs transition-colors ${isSelected
                        ? "bg-sky-50 text-sky-700 font-semibold"
                        : "text-slate-700 hover:bg-slate-50"
                        }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(opt);
                      }}
                    >
                      <span className="truncate whitespace-nowrap pr-2">
                        {renderOption ? renderOption(opt) : opt[optionLabel]}
                      </span>
                      {isSelected && (
                        <svg className="h-4 w-4 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-8 text-center text-xs text-slate-400">
                  ไม่พบข้อมูลที่ค้นหา
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Month/Year Picker (headless Tailwind)
const MonthYearPicker = ({
  label,
  value,
  onChange,
  name = "start_month",
  className = "",
  variant = "default",
  align = "auto",
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = React.useRef(null);
  const d =
    value && /^\d{4}-\d{2}$/.test(String(value))
      ? dayjs(`${value}-01`).tz("Asia/Bangkok")
      : dayjs().tz("Asia/Bangkok");
  const [displayYear, setDisplayYear] = useState(d.year());

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const selected =
    value && /^\d{4}-\d{2}$/.test(String(value))
      ? dayjs(`${value}-01`).tz("Asia/Bangkok")
      : null;

  const applyMonth = (year, monthIndexZeroBased) => {
    const mm = String(monthIndexZeroBased + 1).padStart(2, "0");
    const v = `${year}-${mm}`; // YYYY-MM
    if (onChange) onChange({ target: { name, value: v } });
    setOpen(false);
  };

  const displayLabel = selected ? selected.format("MM/YYYY") : "เลือกเดือน/ปี";
  const thaiMonths = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];

  const isForm = variant === "form";

  let alignClass = "left-0";
  if (align === "right") alignClass = "right-0";
  else if (align === "center") alignClass = "left-1/2 -translate-x-1/2";
  else if (align === "auto") {
    alignClass = isForm ? "left-0" : "left-[-38px]";
  }

  return (
    <div
      className={`relative inline-block text-left w-full ${open ? "z-[900]" : ""} ${className}`}
      ref={containerRef}
    >
      {label && (
        <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
          {label}
        </label>
      )}
      <div className={`relative ${label ? "mt-1" : ""}`}>
        <button
          type="button"
          className={
            isForm
              ? `inline-flex items-center justify-between w-full rounded-lg border border-gray-300 bg-white px-4 h-8 text-sm outline-none shadow-sm`
              : `inline-flex items-center justify-center w-full rounded-full bg-white h-7 outline-none transition-all border border-slate-100 ${open ? "border-blue-200 shadow-sm shadow-blue-50" : "hover:bg-slate-50"}`
          }
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <span
            className={
              isForm
                ? `truncate whitespace-nowrap pr-2 ${displayLabel === "เลือกเดือน/ปี" ? "text-slate-400" : "text-slate-800 font-medium"}`
                : `text-[9px] ${displayLabel === "เลือกเดือน/ปี" ? "text-slate-400" : "text-slate-800 font-bold"}`
            }
          >
            {displayLabel}
          </span>
          {isForm && (
            <svg
              className="-mr-1 ml-2 h-4 w-4 text-slate-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.584l3.71-4.353a.75.75 0 111.14.976l-4.25 5a.75.75 0 01-1.14 0l-4.25-5a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </button>

        {open && (
          <div className={`absolute ${alignClass} z-[1000] mt-2 w-48 rounded-2xl bg-white shadow-2xl border border-slate-100 p-1.5 animate-in fade-in zoom-in duration-200`}>
            {/* Header: year navigation */}
            <div className="flex items-center justify-between mb-1.5 px-0.5">
              <button
                type="button"
                className="p-1 rounded-xl hover:bg-slate-50 text-slate-400 hover:text-slate-900 transition-colors"
                onClick={() => setDisplayYear((y) => y - 1)}
                aria-label="ปีก่อนหน้า"
              >
                <ChevronLeft size={16} />
              </button>
              <div className="text-[11px] font-black text-slate-900 select-none tracking-tight">
                {displayYear}
              </div>
              <button
                type="button"
                className="p-1 rounded-xl hover:bg-slate-50 text-slate-400 hover:text-slate-900 transition-colors"
                onClick={() => setDisplayYear((y) => y + 1)}
                aria-label="ปีถัดไป"
              >
                <ChevronRight size={16} />
              </button>
            </div>

            {/* Month grid - Extra Compact */}
            <div className="grid grid-cols-3 gap-1">
              {[
                { n: "ม.ค.", v: "01" }, { n: "ก.พ.", v: "02" }, { n: "มี.ค.", v: "03" },
                { n: "เม.ย.", v: "04" }, { n: "พ.ค.", v: "05" }, { n: "มิ.ย.", v: "06" },
                { n: "ก.ค.", v: "07" }, { n: "ส.ค.", v: "08" }, { n: "ก.ย.", v: "09" },
                { n: "ต.ค.", v: "10" }, { n: "พ.ย.", v: "11" }, { n: "ธ.ค.", v: "12" }
              ].map((m, i) => {
                const targetVal = `${displayYear}-${m.v}`;
                const isSelected = String(value) === targetVal;

                return (
                  <div
                    key={m.v}
                    onClick={() => applyMonth(displayYear, i)}
                    style={{
                      backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                      color: isSelected ? '#2563eb' : '#64748b',
                      cursor: 'pointer',
                      userSelect: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    className={`
                      py-1.5 rounded-xl text-[10px] font-bold transition-all
                      ${isSelected
                        ? "shadow-md"
                        : "hover:bg-slate-50 hover:text-slate-900"
                      }
                    `}
                  >
                    {m.n}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Tailwind Datepicker (React, headless, no external deps)
// Helper component for calendar UI to avoid duplication
const CalendarContent = ({ displayMonth, setDisplayMonth, days, selected, applyDate, minDate }) => (
  <>
    <div className="p-2 bg-slate-50/80 backdrop-blur-sm flex items-center justify-between border-b border-gray-100 sticky top-0">
      <button
        type="button"
        className="p-1 rounded hover:bg-gray-100 transition-colors"
        onClick={() => setDisplayMonth((m) => m.subtract(1, "month"))}
        aria-label="เดือนก่อนหน้า"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
          <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 010 1.06L9.12 9.96l3.67 3.67a.75.75 0 11-1.06 1.06l-4.2-4.2a.75.75 0 010-1.06l4.2-4.2a.75.75 0 011.06 0z" clipRule="evenodd" />
        </svg>
      </button>
      <div className="text-xs font-bold text-slate-800 select-none">
        {displayMonth.format("MMMM YYYY")}
      </div>
      <button
        type="button"
        className="p-1 rounded hover:bg-gray-100 transition-colors"
        onClick={() => setDisplayMonth((m) => m.add(1, "month"))}
        aria-label="เดือนถัดไป"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 010-1.06L10.88 10 7.21 6.33a.75.75 0 111.06-1.06l4.2 4.2a.75.75 0 010 1.06l-4.2 4.2a.75.75 0 01-1.06 0z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
    <div className="p-1.5">
      <div className="grid grid-cols-7 mb-1">
        {["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"].map((d) => (
          <div key={d} className="text-[9px] font-bold text-slate-400 text-center py-1 uppercase">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px">
        {days.map((d, i) => {
          const isCurrentMonth = d.isSame(displayMonth, "month");
          const isSelected = selected && d.isSame(selected, "day");
          const isToday = d.isSame(dayjs().tz("Asia/Bangkok"), "day");
          return (
            <button
              key={i}
              type="button"
              disabled={minDate && d.format("YYYY-MM-DD") < minDate}
              className={`h-7 w-full flex items-center justify-center text-[10px] rounded-md transition-all ${isSelected
                ? "bg-gradient-to-br from-sky-500 to-blue-600 text-white font-bold shadow-sm"
                : isCurrentMonth
                  ? "text-slate-700 hover:bg-sky-50 hover:text-sky-600"
                  : "text-slate-300 hover:bg-slate-50"
                } ${isToday && !isSelected ? "ring-1 ring-inset ring-sky-200" : ""} ${minDate && d.format("YYYY-MM-DD") < minDate ? "opacity-30 cursor-not-allowed grayscale" : ""}`}
              onClick={() => applyDate(d)}
            >
              {d.date()}
            </button>
          );
        })}
      </div>
    </div>
    <div className="p-1.5 bg-slate-50 border-t border-gray-100 flex justify-end gap-1.5">
      <button
        type="button"
        className="text-[9px] font-bold text-slate-500 hover:text-slate-700 px-1.5 py-1 rounded"
        onClick={() => setDisplayMonth(dayjs().tz("Asia/Bangkok").startOf("month"))}
      >
        กลับเดือนนี้
      </button>
      <button
        type="button"
        className="text-[9px] font-bold text-sky-600 hover:text-sky-700 px-1.5 py-1 rounded"
        onClick={() => applyDate(dayjs().tz("Asia/Bangkok"))}
      >
        เลือกวันนี้
      </button>
    </div>
  </>
);

const TailwindDatepicker = ({
  label = "วันที่",
  value,
  onChange,
  className = "",
  name = "transaction_date",
  direction = "auto", // "down", "up", or "auto"
  minDate = null,
}) => {
  const [open, setOpen] = useState(false);
  const [autoDir, setAutoDir] = useState("down");
  const [displayMonth, setDisplayMonth] = useState(() => {
    const d = value ? dayjs(value).tz("Asia/Bangkok") : dayjs().tz("Asia/Bangkok");
    return d.startOf("month");
  });

  const containerRef = React.useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const startOfMonth = displayMonth.startOf("month");
  const endOfMonth = displayMonth.endOf("month");
  const startOfCalendar = startOfMonth.startOf("week");
  const endOfCalendar = endOfMonth.endOf("week");

  const days = [];
  let cur = startOfCalendar;
  while (cur.isBefore(endOfCalendar) || cur.isSame(endOfCalendar, "day")) {
    days.push(cur);
    cur = cur.add(1, "day");
  }

  const selected = value ? dayjs(value).tz("Asia/Bangkok") : null;

  const applyDate = (d) => {
    const v = d.tz("Asia/Bangkok").format("YYYY-MM-DD");
    if (onChange) onChange({ target: { name, value: v } });
    setOpen(false);
  };

  const displayLabel = selected ? selected.format("DD/MM/YYYY") : "เลือกวันที่";
  const finalDirection = direction === "auto" ? autoDir : direction;

  return (
    <div
      className={`relative w-full ${open ? "z-[900]" : ""} ${className}`}
      ref={containerRef}
    >
      {label && (
        <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
          {label}
        </label>
      )}
      <div className="relative mt-1">
        <button
          type="button"
          className="inline-flex items-center justify-between w-full rounded-lg border border-gray-300 bg-white text-slate-700 text-sm px-4 h-8 outline-none transition-all shadow-sm"
          onClick={(e) => {
            if (!open && direction === "auto") {
              const rect = e.currentTarget.getBoundingClientRect();
              const spaceBelow = window.innerHeight - rect.bottom;
              const spaceAbove = rect.top;
              setAutoDir(spaceBelow < 300 && spaceAbove > spaceBelow ? "up" : "down");
            }
            setOpen((o) => !o);
          }}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <span className={displayLabel === "เลือกวันที่" ? "text-slate-400" : ""}>{displayLabel}</span>
          <svg className={`-mr-1 ml-2 h-4 w-4 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.584l3.71-4.353a.75.75 0 111.14.976l-4.25 5a.75.75 0 01-1.14 0l-4.25-5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        </button>

        {open && (
          <>
            {/* Mobile: Centered Modal with Backdrop */}
            <div className="block md:hidden">
              <div
                className="fixed inset-0 bg-slate-900/60 backdrop-blur-[2px] z-[2000] animate-in fade-in duration-200"
                onClick={() => setOpen(false)}
              />
              <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[2001] w-[90%] max-w-[240px] rounded-2xl bg-white shadow-2xl overflow-hidden border border-slate-100 animate-in fade-in zoom-in duration-200">
                <CalendarContent
                  displayMonth={displayMonth}
                  setDisplayMonth={setDisplayMonth}
                  days={days}
                  selected={selected}
                  applyDate={applyDate}
                  minDate={minDate}
                />
              </div>
            </div>

            {/* Desktop: Absolute Position Popover */}
            <div className={`hidden md:block absolute right-0 left-auto z-[1000] ${finalDirection === 'up' ? 'bottom-full mb-1' : 'mt-1'} w-[13.5rem] rounded-xl bg-white shadow-2xl ring-1 ring-black/10 overflow-hidden border border-slate-100 animate-in fade-in slide-in-from-top-1 duration-200`}>
              <CalendarContent
                displayMonth={displayMonth}
                setDisplayMonth={setDisplayMonth}
                days={days}
                selected={selected}
                applyDate={applyDate}
                minDate={minDate}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Transaction Form Component
const TransactionForm = ({
  transaction,
  initialData,
  onSave,
  onCancel,
  categories,
  paymentMethods,
  owners,
}) => {
  // Helper function for today's date in Thai timezone (UTC+7)
  const getTodayTH = () => {
    return dayjs().tz("Asia/Bangkok").format("YYYY-MM-DD");
  };

  // --- รายการยอดนิยม (Preset) ---
  const popularPresets = [
    {
      label: "BTS",
      data: {
        type: "expense",
        amount: "25.00",
        category_id: 8,
        payment_method_id: 10,
        owner_ids: [9],
        status: "unpaid",
        notes: "",
      },
    },
    {
      label: "Grab",
      data: {
        type: "expense",
        category_id: 6,
        payment_method_id: 4,
        owner_ids: [],
        status: "unpaid",
        notes: "",
      },
    },
    {
      label: "TMN",
      data: {
        type: "expense",
        category_id: 4,
        payment_method_id: 5,
        owner_ids: [],
        status: "unpaid",
        notes: "",
      },
    },
    {
      label: "Line Pay",
      data: {
        type: "expense",
        category_id: 5,
        payment_method_id: 5,
        owner_ids: [],
        status: "unpaid",
        notes: "",
      },
    },
    {
      label: "Shopee",
      data: {
        type: "expense",
        category_id: 9,
        payment_method_id: 14,
        owner_ids: [],
        status: "unpaid",
        notes: "",
      },
    },
    {
      label: "Lazada",
      data: {
        type: "expense",
        category_id: 10,
        payment_method_id: 8,
        owner_ids: [],
        status: "unpaid",
        notes: "",
      },
    },
    {
      label: "ค่าใช้จ่าย",
      data: {
        type: "expense",
        amount: "1000.00",
        category_id: 19,
        payment_method_id: 48,
        owner_ids: [9],
        status: "unpaid",
        notes: "",
      },
    },
    //{ label: 'รายรับเงินเดือน', data: { type: 'income', amount: '30000.00', category_id: 4, payment_method_id: 3, owner_ids: [1], status: 'paid', notes: 'รายรับประจำเดือน' } },
  ];

  const applyPreset = (preset) => {
    setFormData((prev) => ({
      ...prev,
      ...preset.data,
      transaction_date: getTodayTH(),
    }));
    //แจ้งเตอร์เมื่อใช้ preset
    //showToast(`กรอกข้อมูล “${preset.label}” ให้แล้ว`, 'success');
  };

  const handleNextMonthClick = () => {
    const nextMonthFirstDay = dayjs().tz("Asia/Bangkok").add(1, 'month').startOf('month').format('YYYY-MM-DD');
    setFormData(prev => ({
      ...prev,
      transaction_date: nextMonthFirstDay,
      status: 'unpaid'
    }));
    showToast('ตั้งเป็นรายการล่วงหน้าของเดือนถัดไป', 'info');
  };

  const [formData, setFormData] = useState(() => ({
    type: "expense",
    amount: "",
    category_id: "",
    payment_method_id: "",
    owner_ids: [],
    transaction_date: getTodayTH(),
    status: "unpaid",
    notes: "",
  }));

  // --- errors state for per-field validation messages ---
  const [errors, setErrors] = useState({});
  const amountInputRef = React.useRef(null);
  const suppressAmountBlur = React.useRef(false);

  // --- Amount helpers (math expression support: + - * / ( ) ) ---
  const sanitizeExpr = (s) => String(s ?? "").replace(/[^0-9+\-*/()., ]/g, "");
  const containsOps = (s) => /[+\-*/()]/.test(String(s));
  const isExpressionComplete = (s) => {
    const raw = String(s ?? "")
      .replace(/,/g, "")
      .trim();
    if (!/[+\-*/()]/.test(raw)) return false; // must contain at least one operator/paren
    if (/[+\-*/]$/.test(raw)) return false; // cannot end with an operator like 156+
    // check parentheses balance
    let bal = 0;
    for (const ch of raw) {
      if (ch === "(") bal++;
      else if (ch === ")") {
        bal--;
        if (bal < 0) return false;
      }
    }
    return bal === 0; // all parentheses closed
  };

  const evalExpression = (expr) => {
    try {
      const s = sanitizeExpr(expr).replace(/,/g, "").trim();
      if (!s) return NaN;
      // Allow only digits, dot, spaces, parentheses, and + - * /
      if (/[^0-9+\-*/().\s]/.test(s)) return NaN;
      // Evaluate within a restricted Function scope
      // eslint-disable-next-line no-new-func
      const result = Function('"use strict";return (' + s + ")")();
      const n = Number(result);
      return Number.isFinite(n) ? n : NaN;
    } catch {
      return NaN;
    }
  };

  const parseAmountNumber = (val) => {
    const raw = String(val ?? "").replace(/,/g, "");
    const n = evalExpression(raw);
    return isNaN(n) ? 0 : n;
  };

  const formatAmountInput = (val) => {
    if (val === "" || val === null || val === undefined) return "";
    const raw = sanitizeExpr(val);
    if (containsOps(raw)) {
      // Keep the expression as typed (collapse multiple spaces)
      return raw.replace(/\s+/g, " ").trim();
    }
    // Pure number: keep original behavior (commas and up to 2 decimals)
    const cleaned = raw.replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    const intPart = parts[0] || "";
    const decPart = parts[1] !== undefined ? parts[1].slice(0, 2) : undefined;
    const intFormatted = intPart ? Number(intPart).toLocaleString("en-US") : "";
    return decPart !== undefined ? `${intFormatted}.${decPart}` : intFormatted;
  };

  // --- Validate required fields (except notes) ---
  const validate = () => {
    const next = {};

    const amtNum = parseAmountNumber(formData.amount);
    if (!amtNum || isNaN(amtNum) || amtNum <= 0) {
      next.amount = "กรุณากรอกจำนวนเงินมากกว่า 0.00";
    }
    if (!formData.transaction_date) {
      next.transaction_date = "กรุณาเลือกวันที่";
    }
    if (!formData.payment_method_id) {
      next.payment_method_id = "กรุณาเลือกวิธีชำระ";
    }
    if (!formData.category_id) {
      next.category_id = "กรุณาเลือกหมวดหมู่";
    }
    if (transaction) {
      // In edit mode, allow keeping the original owner if user doesn't change it.
      // Only validate if user attempted to select owners but it's invalid.
      if (Array.isArray(formData.owner_ids) && formData.owner_ids.length > 1) {
        next.owner_ids = "โหมดแก้ไข: เลือกได้ครั้งละ 1 คน";
      }
    } else {
      if (
        !Array.isArray(formData.owner_ids) ||
        formData.owner_ids.length === 0
      ) {
        next.owner_ids = "กรุณาเลือกเจ้าของรายการอย่างน้อย 1 คน";
      }
    }
    if (!formData.status) {
      next.status = "กรุณาเลือกสถานะ";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // --- Field-level validation (used for live validation) ---
  const validateField = (name, value) => {
    if (name === "amount") {
      const str = String(value ?? "");
      const hasOps = /[+\-*/()]/.test(str);

      // While typing expressions (has operators), suppress hard errors.
      // We'll enforce strict validation on blur/submit.
      if (hasOps) {
        setErrors((prev) => {
          if (!prev.amount) return prev;
          const clone = { ...prev };
          delete clone.amount;
          return clone;
        });
        return true;
      }

      const n = parseAmountNumber(str);
      if (!n || isNaN(n) || n <= 0) {
        setErrors((prev) => ({
          ...prev,
          amount: "กรุณากรอกจำนวนเงินมากกว่า 0.00",
        }));
        return false;
      }
      setErrors((prev) => {
        if (!prev.amount) return prev;
        const clone = { ...prev };
        delete clone.amount;
        return clone;
      });
      return true;
    }
    return true;
  };

  // Helper to format amount for display (e.g., "1,000.50")
  const formatAmountForDisplay = useCallback((amountValue) => {
    const n = Number(amountValue);
    if (!isNaN(n) && n > 0) {
      return n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    return String(amountValue || ""); // Fallback to original string if not a valid number
  }, []);

  // Set formData from transaction prop or initialData (for copy), convert date to TH timezone
  useEffect(() => {
    if (transaction && transaction.transaction_date) {
      const dateTH = dayjs(transaction.transaction_date)
        .tz("Asia/Bangkok")
        .format("YYYY-MM-DD");
      setFormData({
        ...transaction,
        transaction_date: dateTH,
        amount: formatAmountForDisplay(transaction.amount), // Apply formatting here
        owner_ids: transaction.owner_id ? [Number(transaction.owner_id)] : [],
      });
    } else if (initialData && initialData.transaction_date) {
      const dateTH = dayjs(initialData.transaction_date)
        .tz("Asia/Bangkok")
        .format("YYYY-MM-DD");
      const { id, ...rest } = initialData; // ensure no id when copying
      setFormData({
        ...rest,
        transaction_date: dateTH,
        amount: formatAmountForDisplay(initialData.amount), // Apply formatting here
        owner_ids: initialData.owner_id ? [Number(initialData.owner_id)] : [],
      });
    } else {
      setFormData((prev) => ({ ...prev, transaction_date: getTodayTH() }));
    }
  }, [transaction, initialData, formatAmountForDisplay]);

  // Local state for form options
  const [localCategories, setLocalCategories] = useState([]);
  const [localPaymentMethods, setLocalPaymentMethods] = useState([]);
  const [localOwners, setLocalOwners] = useState([]);
  const [ownerPickerOpen, setOwnerPickerOpen] = useState(false);
  const ownerPickerId = React.useId();
  // Close when any global click happens
  useEffect(() => {
    const handler = () => setOwnerPickerOpen(false);
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, []);
  // Close this popover when another dropdown/popover is opened
  useEffect(() => {
    const onOtherOpen = (e) => {
      if (e.detail && e.detail.id !== ownerPickerId) {
        setOwnerPickerOpen(false);
      }
    };
    window.addEventListener("dropdown:open", onOtherOpen);
    return () => window.removeEventListener("dropdown:open", onOtherOpen);
  }, [ownerPickerId]);

  // Load options from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, pmRes, ownerRes] = await Promise.all([
          api.get("/categories"),
          api.get("/payment-methods"),
          api.get("/owners"),
        ]);
        setLocalCategories(catRes.data);
        setLocalPaymentMethods(pmRes.data);
        setLocalOwners(ownerRes.data);
      } catch (err) {
        console.error("Failed to load form options", err);
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const parsedValue =
      ["category_id", "payment_method_id", "owner_id"].includes(name) &&
        value !== ""
        ? parseInt(value)
        : value;

    setFormData((prev) => ({ ...prev, [name]: parsedValue }));

    // clear error for this field when user edits
    if (errors[name]) {
      setErrors((prev) => {
        const clone = { ...prev };
        delete clone[name];
        return clone;
      });
    }

    // live validate amount while typing
    if (name === "amount") {
      validateField("amount", value);
    }
  };

  const filteredCategories = useMemo(
    () => localCategories.filter((c) => c.type === formData.type),
    [localCategories, formData.type],
  );

  useEffect(() => {
    // This effect resets the category if the selected category is not valid for the current transaction type.
    // It should NOT run when copying a transaction (initialData is present),
    // as we want to preserve the copied category.
    if (transaction || initialData) {
      return;
    }

    const categoryIds = filteredCategories.map((c) => Number(c.id));
    if (
      formData.category_id &&
      !categoryIds.includes(Number(formData.category_id))
    ) {
      setFormData((prev) => ({ ...prev, category_id: "" }));
    }
  }, [formData.type, formData.category_id, filteredCategories, transaction, initialData]);

  // Ensure transaction_date is set (TH timezone) if empty
  useEffect(() => {
    if (!formData.transaction_date) {
      const today = getTodayTH();
      setFormData((prev) => ({ ...prev, transaction_date: today }));
      console.log("transaction_date was empty, set to today (TH):", today);
    }
  }, [formData.transaction_date]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // validate before submit
    if (!validate()) return;
    // ส่งแบบ date-only ให้ backend เพื่อเลี่ยงปัญหา timezone ชดเชยเดือน
    const isoDate = dayjs(formData.transaction_date).format("YYYY-MM-DD");
    // Normalize amount to number
    const amountNumber = parseAmountNumber(formData.amount);
    // สร้าง object ใหม่สำหรับส่ง onSave
    let dataToSave;
    if (transaction) {
      dataToSave = {
        ...formData,
        amount: amountNumber,
        transaction_date: isoDate,
      };
      onSave(dataToSave);
    } else {
      const { id, owner_id, ...rest } = formData;
      dataToSave = {
        ...rest,
        amount: amountNumber,
        owner_ids: Array.isArray(formData.owner_ids) ? formData.owner_ids : [],
        transaction_date: isoDate,
      };
      onSave(dataToSave);
    }
  };

  const insertAtCursor = (text) => {
    const el = amountInputRef.current;
    if (!el) return;
    const start = el.selectionStart ?? String(formData.amount).length;
    const end = el.selectionEnd ?? start;
    const current = String(formData.amount ?? "");
    const nextRaw = current.slice(0, start) + text + current.slice(end);
    const next = formatAmountInput(nextRaw);
    setFormData((prev) => ({ ...prev, amount: next }));
    // restore caret after DOM updates
    requestAnimationFrame(() => {
      if (amountInputRef.current) {
        const pos = start + text.length;
        amountInputRef.current.setSelectionRange(pos, pos);
        amountInputRef.current.focus();
      }
    });
    validateField("amount", next);
  };

  const backspaceAtCursor = () => {
    const el = amountInputRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const current = String(formData.amount ?? "");
    let nextRaw;
    if (start !== end) {
      // delete selection
      nextRaw = current.slice(0, start) + current.slice(end);
    } else if (start > 0) {
      // delete char before cursor
      nextRaw = current.slice(0, start - 1) + current.slice(end);
    } else {
      nextRaw = current;
    }
    const next = formatAmountInput(nextRaw);
    const newPos = Math.max(0, start - (start === end ? 1 : end - start));
    setFormData((prev) => ({ ...prev, amount: next }));
    requestAnimationFrame(() => {
      if (amountInputRef.current) {
        amountInputRef.current.setSelectionRange(newPos, newPos);
        amountInputRef.current.focus();
      }
    });
    validateField("amount", next);
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      autoComplete="off"
      className="space-y-4 text-slate-800 w-full"
    >
      {/* Type Selector */}
      <div className="flex bg-slate-100 rounded-xl p-1 border border-slate-200 max-w-[240px] mx-auto select-none">
        <button
          type="button"
          aria-pressed={formData.type === "expense"}
          onClick={() => setFormData(p => ({ ...p, type: "expense" }))}
          className={`w-1/2 py-1 rounded-lg transition-all duration-200 text-xs font-bold ${formData.type === "expense"
            ? "bg-white text-rose-600 shadow-sm"
            : "bg-transparent text-slate-500 hover:text-slate-800"
            }`}
        >
          รายจ่าย
        </button>
        <button
          type="button"
          aria-pressed={formData.type === "income"}
          onClick={() => setFormData(p => ({ ...p, type: "income" }))}
          className={`w-1/2 py-1 rounded-lg transition-all duration-200 text-xs font-bold ${formData.type === "income"
            ? "bg-white text-emerald-600 shadow-sm"
            : "bg-transparent text-slate-500 hover:text-slate-800"
            }`}
        >
          รายรับ
        </button>
      </div>

      {/* Popular Preset Selector */}
      <div className="relative">
        <div className="flex overflow-x-auto no-scrollbar items-center gap-2 px-4 py-1.5 select-none scroll-smooth">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mr-1 shrink-0">ยอดนิยม:</span>
          {popularPresets.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => applyPreset(p)}
              className="whitespace-nowrap px-3 py-1 text-[11px] font-medium rounded-full bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 transition-all shadow-sm active:scale-95 select-none shrink-0"
            >
              {p.label}
            </button>
          ))}
          <div className="w-8 shrink-0" />
        </div>
        <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none" />
      </div>

      {/* Main Form Grid (2 Columns) */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-4 pt-2 border-t border-slate-100">
        {/* Amount */}
        <div className="col-span-1">
          <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
            จำนวนเงิน
          </label>
          <div className="mt-1">
            <input
              type="text"
              name="amount"
              value={formatAmountInput(formData.amount)}
              onChange={(e) => {
                const next = formatAmountInput(e.target.value);
                setFormData((prev) => ({ ...prev, amount: next }));
                validateField("amount", next);
              }}
              onBlur={(e) => {
                if (suppressAmountBlur.current) {
                  suppressAmountBlur.current = false;
                  return;
                }
                const n = parseAmountNumber(e.target.value);
                if (!isNaN(n) && n > 0) {
                  const fixed = n.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
                  setFormData((prev) => ({ ...prev, amount: fixed }));
                  setErrors((prev) => {
                    const clone = { ...prev };
                    delete clone.amount;
                    return clone;
                  });
                } else {
                  setErrors((prev) => ({
                    ...prev,
                    amount: "กรุณากรอกจำนวนเงินมากกว่า 0.00",
                  }));
                }
              }}
              ref={amountInputRef}
              inputMode="decimal"
              pattern="[0-9.,]*"
              placeholder="0.00"
              className={`w-full rounded-lg border px-3 h-8 text-base md:text-sm outline-none transition-all bg-white text-slate-700 shadow-sm ${errors.amount
                ? "border-red-500 focus:ring-1 focus:ring-red-500/20"
                : "border-gray-300"
                }`}
            />
          </div>
          {errors.amount && (
            <p className="mt-1 text-[10px] font-medium text-red-500 leading-tight">{errors.amount}</p>
          )}
          {(() => {
            const str = String(formData.amount ?? "");
            if (!isExpressionComplete(str)) return null;
            const n = parseAmountNumber(str);
            if (isNaN(n)) return null;
            return (
              <p className="mt-1 text-[10px] font-medium text-slate-500">
                ≈ {Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ฿
              </p>
            );
          })()}
          <div className="mt-1 flex flex-wrap gap-1 select-none">
            {["+", "-", "*", "/", "(", ")"].map((sym) => (
              <button
                key={sym}
                type="button"
                className="px-1.5 py-0.5 text-[10px] font-bold rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-all"
                onMouseDown={(e) => { e.preventDefault(); suppressAmountBlur.current = true; }}
                onClick={() => { insertAtCursor(sym); suppressAmountBlur.current = false; }}
              >
                {sym}
              </button>
            ))}
            <button
              type="button"
              className="px-1.5 py-0.5 text-[10px] font-bold rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-all"
              onMouseDown={(e) => { e.preventDefault(); suppressAmountBlur.current = true; }}
              onClick={() => { backspaceAtCursor(); suppressAmountBlur.current = false; }}
            >
              ⌫
            </button>
          </div>
        </div>

        {/* Date */}
        <div className="col-span-1">
          <TailwindDatepicker
            label="วันที่"
            value={formData.transaction_date}
            onChange={handleChange}
          />
          {errors.transaction_date && (
            <p className="mt-1 text-[10px] font-medium text-red-500 leading-tight">{errors.transaction_date}</p>
          )}
          {initialData && !transaction && (
            <div className="mt-1">
              <button
                type="button"
                onClick={handleNextMonthClick}
                className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 transition"
              >
                เดือนถัดไป
              </button>
            </div>
          )}
        </div>

        {/* Payment Method */}
        <div className="col-span-1">
          <TailwindDropdown
            label="วิธีชำระ"
            options={localPaymentMethods}
            value={formData.payment_method_id}
            onChange={(e) => handleChange({ target: { name: "payment_method_id", value: e.target.value } })}
            placeholder="เลือกวิธีชำระ"
            className="payment_method_id"
            required
            disabled={localPaymentMethods.length === 0}
            visibleOptionCount={5}
          />
          {errors.payment_method_id && (
            <p className="mt-1 text-[10px] font-medium text-red-500 leading-tight">{errors.payment_method_id}</p>
          )}
        </div>

        {/* Category */}
        <div className="col-span-1">
          <TailwindDropdown
            label="หมวดหมู่"
            options={filteredCategories}
            value={formData.category_id}
            onChange={(e) => handleChange({ target: { name: "category_id", value: e.target.value } })}
            placeholder="เลือกหมวดหมู่"
            className="category_id"
            required
            disabled={filteredCategories.length === 0}
            visibleOptionCount={5}
          />
          {errors.category_id && (
            <p className="mt-1 text-[10px] font-medium text-red-500 leading-tight">{errors.category_id}</p>
          )}
        </div>

        {/* Owner */}
        <div className="col-span-1">
          <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
            เจ้าของรายการ
          </label>
          <div className="flex flex-wrap items-center gap-1 mt-1">
            {localOwners.filter(o => o.username !== "admin").map((o) => {
              const checked = Array.isArray(formData.owner_ids) && formData.owner_ids.includes(Number(o.id));
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => {
                    setFormData((prev) => {
                      if (transaction) return { ...prev, owner_ids: [Number(o.id)] };
                      const set = new Set(prev.owner_ids || []);
                      if (set.has(Number(o.id))) set.delete(Number(o.id));
                      else set.add(Number(o.id));
                      return { ...prev, owner_ids: Array.from(set) };
                    });
                  }}
                  className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border transition-all ${checked
                    ? "bg-sky-50 border-sky-400 text-sky-700"
                    : "bg-white border-slate-200 text-slate-600"
                    }`}
                >
                  {o.username}
                </button>
              );
            })}
          </div>
        </div>

        {/* Status */}
        <div className="col-span-1">
          <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
            สถานะ
          </label>
          <div className="flex flex-wrap items-center gap-1 mt-1">
            {[
              { id: "paid", name: "จ่ายแล้ว" },
              { id: "unpaid", name: "ยังไม่จ่าย" },
            ].map((opt) => {
              const checked = formData.status === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, status: opt.id }))}
                  className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border transition-all ${checked
                    ? "bg-sky-50 border-sky-400 text-sky-700"
                    : "bg-white border-slate-200 text-slate-600"
                    }`}
                >
                  {opt.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Note */}
        <div className="col-span-2 pt-2 border-t border-slate-100">
          <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
            บันทึกช่วยจำ
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-base md:text-sm text-slate-800 outline-none shadow-sm resize-none h-12 mt-1"
            placeholder="รายละเอียดเพิ่มเติม..."
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-600 font-bold hover:bg-slate-100 transition-all text-xs"
        >
          ยกเลิก
        </button>
        <button
          type="submit"
          className="flex-1 py-2 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 text-white font-bold hover:from-sky-600 hover:to-blue-700 transition-all shadow-md text-xs"
        >
          บันทึก
        </button>
      </div>
    </form>
  );
};

/** ปฏิทินผ่อน (รายปี: คลิกเพื่อดูรายละเอียดเดือน) */
const InstallmentYearCalendar = ({
  items = [],
  owners = [],
  paymentMethods = [],
  year,
  ownerId,
}) => {
  const tz = "Asia/Bangkok";
  const [selectedMonth, setSelectedMonth] = React.useState(null); // 1-12 | null
  const ownerIdStr =
    ownerId === undefined || ownerId === null ? "" : String(ownerId);
  const ownersById = new Map(
    (Array.isArray(owners) ? owners : []).map((o) => [String(o.id), o]),
  );
  // ---- helpers ----
  const calcMonthly = (total, months, interest) => {
    const T = Number(String(total ?? 0).replace(/,/g, "")) || 0;
    const M = Number(months || 0);
    const R = Number(interest || 0);
    if (!T || !M) return 0;
    return (T * (1 + R / 100)) / M; // flat/simple interest per month
  };

  const isActiveInMonth = (it, y, m) => {
    const startYYYYMM = it.start_month
      ? /^\d{4}-\d{2}$/.test(String(it.start_month))
        ? String(it.start_month)
        : dayjs(it.start_month).tz(tz).format("YYYY-MM")
      : "";
    if (!startYYYYMM || !it.months) return false;
    const start = dayjs(`${startYYYYMM}-01`).tz(tz).startOf("month");
    const end = start.add(Number(it.months) - 1, "month").startOf("month");
    const target = dayjs(`${y}-${String(m).padStart(2, "0")}-01`)
      .tz(tz)
      .startOf("month");
    return (
      (target.isAfter(start) || target.isSame(start)) &&
      (target.isBefore(end) || target.isSame(end))
    );
  };

  const monthEntries = (m) => {
    const out = [];
    for (const it of Array.isArray(items) ? items : []) {
      if (!isActiveInMonth(it, year, m)) continue;

      const ownerIds =
        Array.isArray(it.owner_ids) && it.owner_ids.length
          ? it.owner_ids.map((x) => String(Number(x))).filter(Boolean)
          : Array.isArray(it.owners) && it.owners.length
            ? it.owners.map((o) => String(Number(o.id))).filter(Boolean)
            : it.owner_id != null
              ? [String(Number(it.owner_id))]
              : [];

      if (ownerIdStr && ownerIdStr !== "all" && !ownerIds.includes(ownerIdStr)) continue;

      const monthly = calcMonthly(
        it.total_amount,
        it.months,
        it.interest_percent,
      );
      const share = ownerIds.length > 0 ? monthly / ownerIds.length : monthly;
      const ownerNames = ownerIds
        .map((id) => ownersById.get(id)?.username || ownersById.get(id)?.name)
        .filter(Boolean);

      // Determine if this installment is finished overall
      const startYYYYMM = it.start_month
        ? /^\d{4}-\d{2}$/.test(String(it.start_month))
          ? String(it.start_month)
          : dayjs(it.start_month).tz(tz).format("YYYY-MM")
        : "";
      const totalMonths = Number(it.months || 0);
      const start = dayjs(`${startYYYYMM}-01`).tz(tz).startOf("month");

      const finished =
        startYYYYMM && totalMonths
          ? start
            .add(totalMonths - 1, "month")
            .endOf("month")
            .isBefore(dayjs().tz(tz), "day")
          : false;

      const target = dayjs(`${year}-${String(m).padStart(2, "0")}-01`).tz(tz).startOf("month");
      const monthIdx = target.diff(start, "month") + 1;

      // Calculate remaining after this month's payment
      const T = Number(String(it.total_amount ?? 0).replace(/,/g, "")) || 0;
      const R = Number(it.interest_percent || 0);
      const totalDebt = T * (1 + R / 100);
      const remainingTotal = Math.max(0, totalDebt - (monthly * monthIdx));
      const remainingShare = ownerIds.length > 0 ? remainingTotal / ownerIds.length : remainingTotal;

      const payName =
        paymentMethods.find(
          (pm) => Number(pm.id) === Number(it.payment_method_id),
        )?.name || "";

      out.push({
        id: it.id,
        title: String(it.title || "-"),
        ownersText: ownerNames.length ? ownerNames.join(", ") : "-",
        amount: Number(share) || 0,
        amountText: Number(share).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        remainingText: Number(remainingShare).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        finished,
        monthIdx,
        totalMonths,
        payName,
      });
    }
    return out;
  };

  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const currentYear = dayjs().tz(tz).year();
  const currentMonth = dayjs().tz(tz).month() + 1; // 1-12

  React.useEffect(() => {
    if (year !== currentYear) return;
    setSelectedMonth((prev) => (prev == null ? currentMonth : prev));
  }, [year, currentYear, currentMonth]);

  const getSummaryData = (month) => {
    const entries = monthEntries(month);
    if (!entries.length) return { byMethod: [], byOwner: [], total: 0 };

    const byMethodMap = {};
    const byOwnerMap = {};
    let total = 0;

    entries.forEach((e) => {
      if (e.finished) return; // Only count ongoing for summary
      total += e.amount;

      // By Method
      if (e.payName) {
        byMethodMap[e.payName] = (byMethodMap[e.payName] || 0) + e.amount;
      }

      // By Owner
      const owners = e.ownersText.split(", ");
      const share = e.amount / (owners.length || 1);
      owners.forEach(o => {
        byOwnerMap[o] = (byOwnerMap[o] || 0) + share;
      });
    });

    return {
      byMethod: Object.entries(byMethodMap).map(([name, amount]) => ({ name, amount })),
      byOwner: Object.entries(byOwnerMap).map(([name, amount]) => ({ name, amount })),
      total,
    };
  };

  return (
    <div className="w-full">
      <div className="flex flex-col gap-4">
        {/* Top: months grid */}
        <div className="w-full">
          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-1.5 sm:gap-2">
            {months.map((m) => {
              const entries = monthEntries(m);
              const total = entries.reduce((s, e) => s + Number(e.amount || 0), 0);
              const isSelected = selectedMonth === m;
              const isCurrentMonth = year === currentYear && m === currentMonth;

              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setSelectedMonth((prev) => (prev === m ? null : m))}
                  className={`group relative text-left p-1.5 sm:p-2 rounded-xl flex flex-col justify-between transition-all duration-300 border backdrop-blur-sm min-h-[45px] sm:min-h-[55px] ${isSelected
                    ? "bg-white border-2 border-[#f87171] shadow-md -translate-y-0.5"
                    : isCurrentMonth
                      ? "bg-white border-sky-200 shadow-sm hover:border-sky-400"
                      : "bg-white border-slate-100 shadow-sm hover:border-slate-300"
                    }`}
                  aria-pressed={isSelected}
                >
                  <div className="flex items-center justify-between w-full mb-0.5">
                    <span className={`text-[9px] font-bold tracking-tight ${isSelected
                      ? 'text-[#f87171]'
                      : 'text-slate-400'}`}>
                      {dayjs(`${year}-${m}-01`).format("MMM")}
                    </span>
                    {entries.length > 0 && (
                      <span className={`text-[8px] font-bold px-1 py-0.5 rounded leading-none ${isSelected
                        ? 'bg-red-50 text-[#f87171]'
                        : 'bg-slate-100 text-slate-500'}`}>
                        {entries.length}
                      </span>
                    )}
                  </div>

                  <div className="mt-auto w-full">
                    {total > 0 ? (
                      <div className={`text-[10px] font-black tabular-nums text-right truncate ${isSelected
                        ? 'text-[#f87171]'
                        : 'text-slate-500'}`}>
                        {Number(total).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })} ฿
                      </div>
                    ) : (
                      <div className={`text-[9px] font-medium text-right ${isSelected
                        ? 'text-red-200'
                        : 'text-slate-200'}`}>-</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Bottom: details panel */}
        <div className="w-full">
          <div className="border border-slate-100 rounded-2xl bg-white shadow-lg shadow-slate-100/30 flex flex-col overflow-hidden transition-all duration-500">
            <div className="bg-slate-50/50 px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-xs font-bold text-slate-800 tracking-tight leading-tight">รายละเอียดการผ่อนรายเดือน</h3>
                {selectedMonth ? (
                  <p className="text-[9px] text-slate-400 font-bold tracking-tight mt-0.5">
                    {dayjs(`${year}-${selectedMonth}-01`).format("MMMM YYYY")}
                  </p>
                ) : (
                  <p className="text-[9px] text-slate-400 tracking-tight mt-0.5">
                    เลือกเดือนเพื่อดูสรุป
                  </p>
                )}
              </div>
            </div>

            <div className="p-4">
              {selectedMonth ? (
                (() => {
                  const entries = monthEntries(selectedMonth);
                  if (entries.length === 0)
                    return (
                      <div className="flex flex-col items-center justify-center py-10 opacity-30 text-slate-400">
                        <Info className="w-10 h-10 mb-2" />
                        <p className="text-xs tracking-tight text-center">ไม่มีรายการผ่อนชำระเดือนนี้</p>
                      </div>
                    );

                  const summary = getSummaryData(selectedMonth);
                  const ongoing = entries.filter((e) => !e.finished);
                  const done = entries.filter((e) => e.finished);

                  const toTitleCase = (str) => {
                    if (!str) return "";
                    return str.toLowerCase().replace(/\b\w/g, s => s.toUpperCase());
                  };

                  return (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                      {/* Left: Summaries */}
                      <div className="lg:col-span-4 space-y-4">
                        {/* By Payment Method */}
                        <div className="bg-slate-50/30 rounded-xl p-3 border border-slate-100/50 shadow-sm">
                          <h4 className="text-[9px] font-black text-slate-400 tracking-wider mb-2.5 flex items-center gap-1.5">
                            <CreditCard className="w-2.5 h-2.5" />
                            ตามวิธีชำระ
                          </h4>
                          {summary.byMethod.length === 0 ? (
                            <p className="text-[10px] text-slate-300 font-bold italic py-1 text-center">ไม่มีข้อมูล</p>
                          ) : (
                            <div className="space-y-1.5">
                              {summary.byMethod.map((item, idx) => (
                                <div key={idx} className="flex justify-between items-center group">
                                  <span className="text-[11px] text-slate-600 tracking-tight">{toTitleCase(item.name)}</span>
                                  <span className="text-[11px] text-slate-800 tabular-nums">
                                    {Number(item.amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ฿
                                  </span>
                                </div>
                              ))}
                              <div className="flex justify-between items-center pt-2 mt-2 border-t border-slate-200">
                                <span className="text-[11px] font-black text-slate-800">รวม</span>
                                <span className="text-[11px] font-black tabular-nums">
                                  {Number(summary.total).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ฿
                                </span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* By Owner */}
                        <div className="bg-slate-50/30 rounded-xl p-3 border border-slate-100/50 shadow-sm">
                          <h4 className="text-[9px] font-black text-slate-400 tracking-wider mb-2.5 flex items-center gap-1.5">
                            <User className="w-2.5 h-2.5" />
                            ตามเจ้าของ
                          </h4>
                          {summary.byOwner.length === 0 ? (
                            <p className="text-[10px] text-slate-300 font-bold italic py-1 text-center">ไม่มีข้อมูล</p>
                          ) : (
                            <div className="space-y-1.5">
                              {summary.byOwner.map((item, idx) => (
                                <div key={idx} className="flex justify-between items-center group">
                                  <span className="text-[11px] text-slate-600 tracking-tight">{toTitleCase(item.name)}</span>
                                  <span className="text-[11px] text-slate-800 tabular-nums">
                                    {Number(item.amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ฿
                                  </span>
                                </div>
                              ))}
                              <div className="flex justify-between items-center pt-2 mt-2 border-t border-slate-200">
                                <span className="text-[11px] font-black text-slate-800">รวม</span>
                                <span className="text-[11px] font-black tabular-nums">
                                  {Number(summary.total).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ฿
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right: Individual Items */}
                      <div className="lg:col-span-8 space-y-6 lg:border-l lg:border-slate-100 lg:pl-6">
                        {ongoing.length > 0 && (() => {
                          const groupedByOwner = {};
                          ongoing.forEach(item => {
                            const ownersList = item.ownersText.split(", ");
                            ownersList.forEach(o => {
                              if (!groupedByOwner[o]) groupedByOwner[o] = [];
                              groupedByOwner[o].push(item);
                            });
                          });

                          return (
                            <div className="space-y-6">
                              {Object.entries(groupedByOwner).map(([owner, items]) => (
                                <div key={owner}>
                                  <div className="text-[9px] font-black text-slate-400 tracking-widest mb-3 px-1 flex items-center gap-1.5">
                                    <div className="w-1 h-1 rounded-full bg-blue-500 animate-pulse"></div>
                                    {owner}
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                                    {items.map((e) => (
                                      <div key={`${owner}-${e.id}`} className="group relative bg-white border border-slate-100 p-3 rounded-xl shadow-sm hover:shadow-md hover:border-blue-100 transition-all duration-300 overflow-hidden text-left">
                                        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                        <div className="flex justify-between items-start gap-3">
                                          <div className="flex gap-2.5 min-w-0 flex-1">
                                            <div className="p-2 bg-slate-50 rounded-lg group-hover:bg-blue-50 transition-colors shrink-0 flex items-center justify-center">
                                              <CreditCard className="w-3.5 h-3.5 text-slate-400 group-hover:text-emerald-600" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                              <h4 className="text-[11px] font-black text-slate-800 truncate mb-1 group-hover:text-sky-600 transition-colors tracking-tight">
                                                {e.title}
                                              </h4>
                                              <div className="flex flex-col gap-0.5">
                                                <span className="text-[8px] text-slate-400 truncate">{e.payName}</span>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="text-right shrink-0">
                                            <div className="text-[11px] font-black tabular-nums leading-none mb-1.5 pt-0.5">{e.amountText} ฿</div>
                                            <div className="flex flex-col gap-1">
                                              <div className="text-[8px] text-slate-300 tracking-tighter uppercase whitespace-nowrap">
                                                งวด: <span className="text-slate-500 font-bold"> {e.monthIdx}/{e.totalMonths}</span>
                                              </div>
                                              <div className="text-[8px] text-slate-300 tracking-tighter uppercase whitespace-nowrap">
                                                คงเหลือ: <span className="text-slate-500 font-bold">{e.remainingText} ฿</span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}

                        {done.length > 0 && (
                          <div>
                            <p className="text-[9px] font-black text-slate-400 tracking-widest mb-3 px-1">ผ่อนครบแล้ว</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                              {done.map((e) => (
                                <div key={e.id} className="group relative bg-slate-50/50 border border-transparent p-3 rounded-xl flex flex-col transition-all opacity-60 hover:opacity-100 overflow-hidden text-left">
                                  <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>

                                  <div className="flex justify-between items-start gap-3 mb-1">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <div className="p-1 px-1.5 bg-emerald-50 rounded flex items-center gap-1 shrink-0">
                                        <CheckCircle className="w-2.5 h-2.5 text-emerald-500" />
                                        <span className="text-[7px] font-black text-emerald-600 tracking-widest uppercase">Done</span>
                                      </div>
                                      <h4 className="text-[11px] font-bold text-slate-500 truncate line-through decoration-slate-300">
                                        {e.title}
                                      </h4>
                                    </div>
                                    <div className="text-[10px] font-black text-slate-400 tabular-nums shrink-0 pt-0.5">{e.amountText} ฿</div>
                                  </div>
                                  <div className="flex items-center gap-2 mt-1">
                                    {/* <span className="text-[8px] text-slate-300 font-bold truncate">{e.payName}</span> */}
                                    {/* <span className="text-[7px] text-slate-200">•</span> */}
                                    <div className="flex items-center gap-1 opacity-60">
                                      <User className="w-2 h-2 text-slate-300" />
                                      <span className="text-[8px] text-slate-400 truncate">{e.ownersText}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-slate-200">
                  <div className="p-4 bg-slate-50 rounded-2xl mb-3 animate-bounce-slow">
                    <CalendarClock className="w-10 h-10 text-slate-300" />
                  </div>
                  <p className="text-xs text-slate-400 tracking-tight">เลือกเดือนเพื่อดูรายละเอียด</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CalendarView = ({ transactions, year, month }) => {
  const djs = dayjs().tz("Asia/Bangkok").year(year).month(month - 1);
  const daysInMonth = djs.daysInMonth();
  const firstDayOfWeek = djs.startOf("month").day(); // 0 is Sunday, 1 is Monday

  // Group transactions by date
  const expensesByDate = {};
  transactions.forEach((tx) => {
    if (tx.type === "expense") {
      const txStart = dayjs(tx.transaction_date).tz("Asia/Bangkok");
      if (txStart.year() === year && txStart.month() + 1 === month) {
        const date = txStart.date();
        expensesByDate[date] = (expensesByDate[date] || 0) + Number(tx.amount || 0);
      }
    }
  });

  const calendarCells = [];
  for (let i = 0; i < firstDayOfWeek; i++) {
    calendarCells.push(<div key={`empty-${i}`} className="p-1 md:p-2 border border-slate-100 bg-slate-50 min-h-[50px] sm:min-h-[60px] md:min-h-[80px]"></div>);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const expense = expensesByDate[day] || 0;
    const isToday = dayjs().tz("Asia/Bangkok").isSame(djs.date(day), "day");
    calendarCells.push(
      <div key={`day-${day}`} className={`p-1 md:p-2 border border-slate-100 min-h-[50px] sm:min-h-[60px] md:min-h-[80px] flex flex-col ${isToday ? 'bg-sky-50' : 'bg-white'}`}>
        <div className={`text-xs md:text-sm font-medium mb-1 ${isToday ? 'text-sky-700' : 'text-slate-500'}`}>{day}</div>
        {expense > 0 && (
          <div className="text-[10px] sm:text-xs md:text-sm text-red-400 tabular-nums mt-auto text-right truncate" title={`-${expense.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}>
            -{expense.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        )}
      </div>
    );
  }

  const weekdays = ["อา.", "จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส."];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6 mt-4 w-full xl:w-1/2 lg:w-[60%] md:w-3/4 mr-auto">
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-100">
        {weekdays.map(day => (
          <div key={day} className="py-2 md:py-3 text-center text-[10px] sm:text-xs font-semibold text-slate-500 tracking-wider">
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 bg-slate-200 gap-[1px]">
        {calendarCells}
      </div>
    </div>
  );
};

// --- Navbar Component ---// --- Helpers for Manage Data & Installments ---
const calcMonthly = (total, months, interest) => {
  const T = Number(String(total).replace(/,/g, ""));
  const M = Number(months);
  const R = Number(interest);
  if (!T || !M) return 0;
  const withInterest = T * (1 + (isNaN(R) ? 0 : R) / 100);
  return withInterest / M;
};

const calcEndMonth = (yyyyMM, months) => {
  if (!yyyyMM || !months) return "";
  const d = dayjs(`${yyyyMM}-01`).tz("Asia/Bangkok");
  return d.add(Number(months) - 1, "month").format("YYYY-MM");
};


// ==============================================


const ManageDataPage = ({
  onNavigate,
  onRequestLogout,
  categories,
  owners,
  budget,
  transactions,
  onCategoriesUpdate,
  onOwnersUpdate,
  onBudgetUpdate,
  recurrings,
  onRecurringsUpdate,
  paymentMethods,
  installments,
  onInstallmentsUpdate,
}) => {
  const [manageTab, setManageTab] = useState("recurring"); // 'categories' | 'owners' | 'budgets' | 'recurring' | 'installments'
  const [managePeriod, setManagePeriod] = useState(dayjs().format("YYYY-MM"));
  const [searchTerm, setSearchTerm] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [manageLoading, setManageLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formInputs, setFormInputs] = useState({
    name: "",
    type: "expense",
    username: "",
    amount: "",
    owner_id: "",
    month: dayjs().month() + 1,
    year: dayjs().year(),
    // Recurring fields
    title: "",
    frequency: "monthly",
    execution_date: "",
    execution_day_of_week: "",
    execution_day_of_month: "",
    execution_month: "",
    start_date: dayjs().add(1, 'day').format("YYYY-MM-DD"),
    end_date: "",
    is_active: true,
    category_id: "",
    payment_method_id: "",
    // Installment fields
    total_amount: "",
    months: "12",
    interest_percent: "0.00",
    start_month: dayjs().format("YYYY-MM"),
    owner_ids: [],
  });
  const [manageError, setManageError] = useState("");
  const [manageFieldErrors, setManageFieldErrors] = useState({});
  const [manageConfirmOpen, setManageConfirmOpen] = useState(false);
  const [managePendingDeleteId, setManagePendingDeleteId] = useState(null);
  const [recurringConfirmOpen, setRecurringConfirmOpen] = useState(false);
  const [pendingProcessItem, setPendingProcessItem] = useState(null);

  const endpointOf = (tab) => {
    if (tab === "categories") return "/categories";
    if (tab === "owners") return "/owners";
    if (tab === "recurring") return "/recurring";
    if (tab === "installments") return "/installments";
    return "/budgets";
  };
  const labelOf = (tab) => {
    if (tab === "categories") return "หมวดหมู่";
    if (tab === "owners") return "เจ้าของ";
    if (tab === "recurring") return "รายการประจำ";
    if (tab === "installments") return "รายการผ่อน";
    return "งบประมาณ";
  };

  // Summary Calculations
  const summary = useMemo(() => {
    const cats = Array.isArray(categories) ? categories : [];
    const expenseCount = cats.filter(c => c.type === "expense").length;
    const incomeCount = cats.filter(c => c.type === "income").length;
    const ownerCount = Array.isArray(owners) ? owners : [];
    const budgetList = Array.isArray(budget) ? budget : [];
    const totalBudget = budgetList.reduce((sum, b) => sum + Number(b.amount || 0), 0);

    return {
      totalCategories: cats.length,
      expenseCount,
      incomeCount,
      totalOwners: ownerCount.length,
      totalBudgets: budgetList.length,
      totalBudgetAmount: totalBudget
    };
  }, [categories, owners, budget]);

  const pendingRecurringCount = useMemo(() => {
    if (manageTab !== "recurring") return 0;
    const todayStr = dayjs().tz('Asia/Bangkok').format('YYYY-MM-DD');
    return (recurrings || []).filter(it => {
      if (!it.is_active) return false;
      if (!it.next_execution_date) return false;
      return dayjs(it.next_execution_date).tz('Asia/Bangkok').format('YYYY-MM-DD') === todayStr;
    }).length;
  }, [recurrings, manageTab]);

  const loadManageItems = useCallback(async (tab = manageTab) => {
    try {
      setManageLoading(true);
      let params = {};
      if (tab === "budgets") {
        const [y, m] = managePeriod.split("-");
        params = { month: Number(m), year: Number(y) };
      }
      const res = await api.get(endpointOf(tab), { params });
      // Sync global state which updates categories/owners props via App.js
      if (tab === "categories") onCategoriesUpdate(res.data || []);
      else if (tab === "owners") onOwnersUpdate(res.data || []);
      else if (tab === "recurring") onRecurringsUpdate(res.data || []);
      else if (tab === "installments") onInstallmentsUpdate(res.data || []);
      else onBudgetUpdate(res.data || []);
    } catch (e) {
      console.error("loadManageItems failed", e);
      setManageError("โหลดข้อมูลไม่สำเร็จ");
    } finally {
      setManageLoading(false);
    }
  }, [manageTab, managePeriod, onCategoriesUpdate, onOwnersUpdate, onBudgetUpdate]);

  useEffect(() => {
    loadManageItems(manageTab);
  }, [manageTab, managePeriod, loadManageItems]);

  const openCreateModal = () => {
    setEditingItem(null);
    setFormInputs({
      name: "",
      type: "expense",
      username: "",
      amount: "",
      owner_id: owners?.[0]?.id || "",
      month: dayjs().month() + 1,
      year: dayjs().year(),
      title: "",
      frequency: "monthly",
      execution_date: "",
      execution_day_of_week: "",
      execution_day_of_month: "",
      execution_month: "",
      start_date: dayjs().add(1, 'day').format("YYYY-MM-DD"),
      end_date: "",
      is_active: true,
      category_id: "",
      payment_method_id: "",
      // Installment fields
      total_amount: "",
      months: "12",
      interest_percent: "0.00",
      start_month: dayjs().format("YYYY-MM"),
      owner_ids: owners?.[0]?.id ? [owners[0].id] : [],
    });
    setManageError("");
    setFormOpen(true);
  };

  const startEdit = (item) => {
    setEditingItem(item);
    if (manageTab === "categories") {
      setFormInputs({
        name: item.name || "",
        type: item.type || "expense",
        username: "",
      });
    } else if (manageTab === "owners") {
      setFormInputs({
        name: "",
        type: "expense",
        username: item.username || "",
        amount: "",
        owner_id: "",
        month: dayjs().month() + 1,
        year: dayjs().year(),
      });
    } else if (manageTab === "recurring") {
      setFormInputs({
        title: item.title || "",
        amount: Number(item.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        type: item.type || "expense",
        category_id: String(item.category_id || ""),
        payment_method_id: String(item.payment_method_id || ""),
        owner_id: String(item.owner_id || ""),
        frequency: item.frequency || "monthly",
        execution_date: item.execution_date ? dayjs(item.execution_date).format('YYYY-MM-DD') : "",
        execution_day_of_week: item.execution_day_of_week !== null ? String(item.execution_day_of_week) : "",
        execution_day_of_month: item.execution_day_of_month !== null ? String(item.execution_day_of_month) : "",
        execution_month: item.execution_month !== null ? String(item.execution_month) : "",
        start_date: item.start_date ? dayjs(item.start_date).format('YYYY-MM-DD') : "",
        end_date: item.end_date ? dayjs(item.end_date).format('YYYY-MM-DD') : "",
        is_active: item.is_active,
      });
    } else if (manageTab === "installments") {
      setFormInputs({
        title: item.title || "",
        total_amount: Number(item.total_amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        months: String(item.months || "12"),
        interest_percent: Number(item.interest_percent || 0).toFixed(2),
        start_month: item.start_month ? dayjs(item.start_month).format("YYYY-MM") : dayjs().format("YYYY-MM"),
        owner_ids: item.owner_ids || [],
        payment_method_id: String(item.payment_method_id || ""),
      });
    } else {
      // Budgets
      setFormInputs({
        name: "",
        type: "expense",
        username: "",
        amount: Number(item.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        owner_id: String(item.user_id || ""),
        month: Number(item.month || dayjs().month() + 1),
        year: Number(item.year || dayjs().year()),
      });
    }
    setManageError("");
    setFormOpen(true);
  };

  const cancelEdit = () => {
    setFormOpen(false);
    setEditingItem(null);
    setFormInputs({
      name: "",
      type: "expense",
      username: "",
      amount: "",
      owner_id: "",
      month: dayjs().month() + 1,
      year: dayjs().year(),
    });
    setManageError("");
  };

  const onChangeInput = (e) => {
    const { name, value } = e.target;
    setFormInputs((p) => ({ ...p, [name]: value }));
    setManageError("");
    setManageFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const clone = { ...prev };
      delete clone[name];
      return clone;
    });
  };

  const submitManage = async (e) => {
    e?.preventDefault();
    setManageError("");
    setManageFieldErrors({});
    try {
      setManageLoading(true);
      const errors = {};

      if (manageTab === "categories") {
        const payload = { name: formInputs.name.trim(), type: formInputs.type };
        if (!payload.name) errors.name = "กรุณากรอกชื่อหมวดหมู่";
        if (Object.keys(errors).length > 0) {
          setManageFieldErrors(errors);
          return;
        }
        if (editingItem) {
          await api.put(`${endpointOf(manageTab)}/${editingItem.id}`, payload);
          showToast("อัปเดตหมวดหมู่เรียบร้อยแล้ว", "success");
        } else {
          await api.post(endpointOf(manageTab), payload);
          showToast("เพิ่มหมวดหมู่เรียบร้อยแล้ว", "success");
        }
      } else if (manageTab === "owners") {
        const payload = { username: formInputs.username.trim() };
        if (!payload.username) errors.username = "กรุณากรอกชื่อเจ้าของรายการ";
        if (Object.keys(errors).length > 0) {
          setManageFieldErrors(errors);
          return;
        }
        if (editingItem) {
          await api.put(`${endpointOf(manageTab)}/${editingItem.id}`, payload);
          showToast("อัปเดตเจ้าของรายการเรียบร้อยแล้ว", "success");
        } else {
          await api.post(endpointOf(manageTab), payload);
          showToast("เพิ่มเจ้าของรายการเรียบร้อยแล้ว", "success");
        }
      } else if (manageTab === "recurring") {
        const payload = {
          title: formInputs.title.trim(),
          amount: Number(String(formInputs.amount).replace(/,/g, "")),
          type: formInputs.type,
          category_id: Number(formInputs.category_id),
          payment_method_id: formInputs.payment_method_id ? Number(formInputs.payment_method_id) : null,
          owner_id: formInputs.owner_id ? Number(formInputs.owner_id) : null,
          frequency: formInputs.frequency,
          execution_date: formInputs.frequency === 'once' ? (formInputs.execution_date || null) : null,
          execution_day_of_week: formInputs.frequency === 'weekly' ? (formInputs.execution_day_of_week !== "" ? Number(formInputs.execution_day_of_week) : null) : null,
          execution_day_of_month: (formInputs.frequency === 'monthly' || formInputs.frequency === 'yearly') ? (formInputs.execution_day_of_month !== "" ? Number(formInputs.execution_day_of_month) : null) : null,
          execution_month: formInputs.frequency === 'yearly' ? (formInputs.execution_month !== "" ? Number(formInputs.execution_month) : null) : null,
          start_date: formInputs.frequency === 'once' ? (formInputs.execution_date || null) : (formInputs.start_date || null),
          end_date: formInputs.frequency === 'once' ? null : (formInputs.end_date || null),
          is_active: formInputs.is_active,
        };

        if (!payload.title) errors.title = "กรุณากรอกชื่อรายการ";
        if (isNaN(payload.amount) || payload.amount <= 0) errors.amount = "กรุณากรอกจำนวนเงินมากกว่า 0.00";
        if (!payload.category_id) errors.category_id = "กรุณาเลือกหมวดหมู่";
        if (!payload.payment_method_id) errors.payment_method_id = "กรุณาเลือกวิธีชำระ";

        const todayStr = dayjs().format("YYYY-MM-DD");
        if (formInputs.frequency === 'once') {
          if (!payload.execution_date) errors.execution_date = "กรุณาเลือกวันที่";
          else if (payload.execution_date <= todayStr && (!editingItem || payload.execution_date !== dayjs(editingItem.execution_date).format("YYYY-MM-DD"))) {
            errors.execution_date = "ห้ามเลือกวันปัจจุบันหรือย้อนหลัง";
          }
        } else {
          // เพิ่ม Validation สำหรับความถี่แต่ละแบบ
          if (formInputs.frequency === 'weekly' && payload.execution_day_of_week === null) {
            errors.execution_day_of_week = "กรุณาเลือกวันในสัปดาห์";
          }
          if ((formInputs.frequency === 'monthly' || formInputs.frequency === 'yearly') && payload.execution_day_of_month === null) {
            errors.execution_day_of_month = "กรุณาเลือกวันที่";
          }
          if (formInputs.frequency === 'yearly' && payload.execution_month === null) {
            errors.execution_month = "กรุณาเลือกเดือน";
          }

          if (!payload.start_date) errors.start_date = "กรุณาเลือกวันเริ่มต้น";
          else if (payload.start_date <= todayStr && (!editingItem || payload.start_date !== dayjs(editingItem.start_date).format("YYYY-MM-DD"))) {
            errors.start_date = "ห้ามเลือกวันปัจจุบันหรือย้อนหลัง";
          }

          if (payload.end_date) {
            if (payload.end_date <= todayStr && (!editingItem || payload.end_date !== dayjs(editingItem.end_date).format("YYYY-MM-DD"))) {
              errors.end_date = "วันสิ้นสุดห้ามเป็นวันปัจจุบันหรืออดีต";
            } else if (payload.end_date < payload.start_date) {
              errors.end_date = "วันสิ้นสุดห้ามก่อนวันเริ่มต้น";
            }
          }
        }

        if (Object.keys(errors).length > 0) {
          setManageFieldErrors(errors);
          return;
        }

        if (editingItem) {
          await api.put(`${endpointOf(manageTab)}/${editingItem.id}`, payload);
          showToast("อัปเดตรายการประจำเรียบร้อยแล้ว", "success");
        } else {
          await api.post(endpointOf(manageTab), payload);
          showToast("เพิ่มรายการประจำเรียบร้อยแล้ว", "success");
        }
      } else if (manageTab === "installments") {
        const payload = {
          title: formInputs.title.trim(),
          total_amount: Number(String(formInputs.total_amount || "0").replace(/,/g, "")),
          months: Number(formInputs.months),
          interest_percent: Number(formInputs.interest_percent || 0),
          start_month: formInputs.start_month,
          owner_ids: formInputs.owner_ids || [],
          payment_method_id: Number(formInputs.payment_method_id)
        };

        if (!payload.title) errors.title = "กรุณากรอกชื่อรายการ";
        if (isNaN(payload.total_amount) || payload.total_amount <= 0) errors.total_amount = "กรุณากรอกยอดรวมมากกว่า 0.00";
        if (!payload.payment_method_id) errors.payment_method_id = "กรุณาเลือกวิธีชำระ";
        if (!payload.owner_ids || payload.owner_ids.length === 0) errors.owner_ids = "กรุณาเลือกเจ้าของอย่างน้อย 1 ท่าน";

        if (Object.keys(errors).length > 0) {
          setManageFieldErrors(errors);
          return;
        }

        if (editingItem) {
          await api.put(`${endpointOf(manageTab)}/${editingItem.id}`, payload);
          showToast("อัปเดตรายการผ่อนเรียบร้อยแล้ว", "success");
        } else {
          await api.post(endpointOf(manageTab), payload);
          showToast("เพิ่มรายการผ่อนเรียบร้อยแล้ว", "success");
        }
      } else {
        // Budget
        const payload = {
          amount: parseMoney(formInputs.amount),
          month: Number(formInputs.month),
          year: Number(formInputs.year),
          owner_id: formInputs.owner_id
        };
        if (!payload.owner_id) errors.owner_id = "กรุณาเลือกเจ้าของ";
        if (isNaN(payload.amount) || payload.amount <= 0) errors.amount = "กรุณากรอกงบประมาณมากกว่า 0.00";

        if (Object.keys(errors).length > 0) {
          setManageFieldErrors(errors);
          return;
        }

        // Budgets use POST for both create and update (ON DUPLICATE KEY)
        await api.post(endpointOf(manageTab), payload);
        if (editingItem) {
          showToast("อัปเดตงบประมาณเรียบร้อยแล้ว", "success");
        } else {
          showToast("เพิ่มงบประมาณเรียบร้อยแล้ว", "success");
        }
      }
      await loadManageItems();
      cancelEdit();
    } catch (e) {
      console.error("submitManage failed", e);
      setManageError("บันทึกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setManageLoading(false);
    }
  };

  const requestManageDelete = (id) => {
    if (manageTab !== "budgets") {
      const txs = Array.isArray(transactions) ? transactions : [];
      let count = 0;
      if (manageTab === "categories") {
        count = txs.filter((t) => Number(t.category_id) === Number(id)).length;
      } else {
        count = txs.filter((t) => Number(t.owner_id) === Number(id)).length;
      }
      if (count > 0) {
        showToast(`${labelOf(manageTab)}นี้ถูกใช้งานในรายการ ${count} รายการ ไม่สามารถลบได้`, "error");
        return;
      }
    }
    setManagePendingDeleteId(id);
    setManageConfirmOpen(true);
  };

  const confirmManageDelete = async () => {
    if (!managePendingDeleteId) {
      setManageConfirmOpen(false);
      return;
    }
    try {
      setManageLoading(true);
      await api.delete(`${endpointOf(manageTab)}/${managePendingDeleteId}`);
      showToast(`ลบ${labelOf(manageTab)}เรียบร้อยแล้ว`, "success");
      await loadManageItems();
    } catch (e) {
      console.error("deleteManage failed", e);
      showToast("ลบไม่สำเร็จ", "error");
    } finally {
      setManageLoading(false);
      setManagePendingDeleteId(null);
      setManageConfirmOpen(false);
    }
  };

  const [manageSort, setManageSort] = useState({ key: "id", direction: "desc" });
  const INITIAL_VISIBLE_COUNT = 10;
  const [visibleManageCount, setVisibleManageCount] = useState(INITIAL_VISIBLE_COUNT);
  const [filterOwnerId, setFilterOwnerId] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPaymentMethodId, setFilterPaymentMethodId] = useState("all");
  const [filterCategoryId, setFilterCategoryId] = useState("all");
  const [filterFrequency, setFilterFrequency] = useState("all");
  const [filterExecStatus, setFilterExecStatus] = useState("all");
  const [showManageFilters, setShowManageFilters] = useState(false);

  const toggleManageSort = (key) => {
    setManageSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const renderManageSortIndicator = (key) => {
    if (manageSort.key !== key) return <span className="ml-1 text-[10px] text-slate-300 opacity-50">↕</span>;
    return (
      <span className="ml-1 text-[10px] text-blue-600 font-bold">
        {manageSort.direction === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  const [isProcessingRecurring, setIsProcessingRecurring] = useState(false);
  const handleProcessRecurring = async () => {
    if (isProcessingRecurring) return;
    setPendingProcessItem(null);
    setRecurringConfirmOpen(true);
  };

  const confirmProcessRecurring = async () => {
    setRecurringConfirmOpen(false);
    setIsProcessingRecurring(true);
    try {
      if (pendingProcessItem) {
        const res = await api.post(`/recurring/process/${pendingProcessItem.id}`);
        showToast(res.data.message, "success");
      } else {
        const res = await api.post("/recurring/process");
        showToast(res.data.message, "success");
      }
      await loadManageItems();
    } catch (err) {
      showToast(pendingProcessItem ? "รันรายการไม่สำเร็จ" : "ประมวลผลไม่สำเร็จ", "error");
    } finally {
      setIsProcessingRecurring(false);
      setPendingProcessItem(null);
    }
  };

  const monthNames = [
    "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
    "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"
  ];

  const sortedItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    let baseList;
    if (manageTab === "categories") baseList = categories || [];
    else if (manageTab === "owners") baseList = owners || [];
    else if (manageTab === "recurring") baseList = recurrings || [];
    else if (manageTab === "installments") {
      baseList = (installments || []).map(it => {
        const total = Number(it.total_amount || 0);
        const months = Number(it.months || 1);
        const interest = Number(it.interest_percent || 0);
        const monthly_payment = (total / months) + (total * (interest / 100));

        const start = dayjs(it.start_month || dayjs()).tz('Asia/Bangkok').startOf('month');
        const end = start.add(months - 1, 'month');

        const today = dayjs().tz('Asia/Bangkok').startOf('month');
        const isBefore = today.isBefore(start);
        const isAfter = today.isAfter(end);
        const diff = today.diff(start, 'month') + 1;

        let currentInst = 0;
        let remainingMonths = 0;

        if (isBefore) {
          currentInst = 0;
          remainingMonths = months;
        } else if (isAfter) {
          currentInst = months;
          remainingMonths = 0;
        } else {
          currentInst = Math.min(Math.max(1, diff), months);
          remainingMonths = months - currentInst + 1;
        }

        const progressText = `${currentInst}/${months}`;
        const remaining_balance = monthly_payment * remainingMonths;
        const isCompleted = isAfter;

        const ownersList = Array.isArray(it.owners) ? it.owners : [];
        const ownersName = ownersList.map(o => o.username || o.name).join(', ') || "-";

        return {
          ...it,
          monthly_payment,
          remaining_balance,
          start_month_display: start.format('MMM YYYY'),
          end_month_display: end.format('MMM YYYY'),
          progressText,
          ownersName,
          isCompleted,
          owner_ids: ownersList.map(o => o.id)
        };
      });
    } else baseList = budget || [];

    // First, Filter
    let items = baseList.filter(it => {
      // Search term filter
      let hay;
      if (manageTab === "categories") hay = it.name;
      else if (manageTab === "owners") hay = it.username;
      else if (manageTab === "recurring") hay = it.title;
      else if (manageTab === "installments") hay = it.title;
      else hay = it.username;
      const matchesSearch = String(hay || "").toLowerCase().includes(term);

      if (!matchesSearch) return false;

      // Additional filters for Recurring
      if (manageTab === "recurring") {
        if (filterOwnerId !== "all" && Number(it.owner_id) !== Number(filterOwnerId)) return false;
        if (filterStatus !== "all") {
          const isActive = !!it.is_active;
          if (filterStatus === "active" && !isActive) return false;
          if (filterStatus === "inactive" && isActive) return false;
        }
        if (filterPaymentMethodId !== "all" && Number(it.payment_method_id) !== Number(filterPaymentMethodId)) return false;
        if (filterCategoryId !== "all" && Number(it.category_id) !== Number(filterCategoryId)) return false;
        if (filterFrequency !== "all" && it.frequency !== filterFrequency) return false;
        if (filterExecStatus !== "all" && it.last_execution_status !== filterExecStatus) return false;
      }

      if (manageTab === "installments") {
        if (filterOwnerId !== "all") {
          const ownerIds = Array.isArray(it.owner_ids) ? it.owner_ids : [];
          if (!ownerIds.some(id => Number(id) === Number(filterOwnerId))) return false;
        }
        if (filterPaymentMethodId !== "all" && Number(it.payment_method_id) !== Number(filterPaymentMethodId)) return false;
        if (filterStatus !== "all") {
          if (filterStatus === "completed" && !it.isCompleted) return false;
          if (filterStatus === "paying" && it.isCompleted) return false;
        }
      }

      return true;
    });

    // Then, Sort
    items = [...items].sort((a, b) => {
      const { key, direction } = manageSort;
      const isAsc = direction === "asc";

      // 1. Get raw values with smart fallbacks
      let valA = a[key];
      let valB = b[key];

      // Handle specific tab mappings
      if (manageTab === "categories" && key === "name") { valA = a.name; valB = b.name; }
      if (manageTab === "owners" && (key === "username" || key === "name")) { valA = a.username || a.name; valB = b.username || b.name; }
      if (manageTab === "budgets" && (key === "username" || key === "name")) { valA = a.username || a.name; valB = b.username || b.name; }
      if (manageTab === "installments" && key === "title") { valA = a.title; valB = b.title; }
      if (manageTab === "recurring" && key === "title") { valA = a.title; valB = b.title; }

      // 2. Numeric Sort Logic
      const isNumeric = ["id", "total_amount", "remaining_balance", "amount", "monthly_payment", "months"].includes(key);
      if (isNumeric) {
        const nA = Number(valA || 0);
        const nB = Number(valB || 0);
        return isAsc ? nA - nB : nB - nA;
      }

      // 3. Date Sort Logic
      if (key === "start_month" || key === "end_month" || key === "next_execution_date") {
        const dA = valA ? dayjs(valA).valueOf() : 0;
        const dB = valB ? dayjs(valB).valueOf() : 0;
        return isAsc ? dA - dB : dB - dA;
      }

      // 4. Boolean Sort Logic
      if (key === "is_active" || key === "isCompleted") {
        const bA = valA ? 1 : 0;
        const bB = valB ? 1 : 0;
        return isAsc ? bA - bB : bB - bA;
      }

      // 4. String Sort Logic (Default)
      const strA = String(valA || "").trim();
      const strB = String(valB || "").trim();

      // Use Thai locale with numeric option for natural sorting
      return isAsc
        ? strA.localeCompare(strB, 'th', { numeric: true, sensitivity: 'variant' })
        : strB.localeCompare(strA, 'th', { numeric: true, sensitivity: 'variant' });
    });

    return items;
  }, [manageTab, categories, owners, budget, recurrings, installments, searchTerm, manageSort, filterOwnerId, filterStatus, filterPaymentMethodId, filterCategoryId, filterFrequency, filterExecStatus]);

  useEffect(() => {
    const handlePlus = () => openCreateModal();
    window.addEventListener("app-plus-clicked", handlePlus);
    return () => window.removeEventListener("app-plus-clicked", handlePlus);
  }, [owners]);

  return (
    <div className="min-h-screen bg-gray-50 text-slate-800">
      <div className="px-4 pt-2 pb-24 sm:px-6 sm:py-3 lg:px-8 lg:py-4">
        <main className="w-full space-y-6 pt-4">
          {/* Main Content Section */}
          <section className="w-full bg-white rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6">
            <header className="flex flex-col gap-4 mb-6">
              {/* Row 1: Tabs */}
              <div className="relative -mx-4 px-4 sm:mx-0 sm:px-0 min-w-0">
                <div className="flex w-full gap-1 p-1 bg-gray-100 rounded-xl overflow-x-auto overscroll-x-contain scrollbar-hide no-scrollbar mask-fade-right">
                  <button
                    type="button"
                    onClick={() => {
                      setManageTab("recurring");
                      setSearchTerm("");
                      setManageSort({ key: "id", direction: "desc" });
                      setVisibleManageCount(INITIAL_VISIBLE_COUNT);
                      setShowManageFilters(false);
                    }}
                    className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap flex-shrink-0 ${manageTab === "recurring" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  >
                    รายการประจำ
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setManageTab("installments");
                      setSearchTerm("");
                      setManageSort({ key: "id", direction: "desc" });
                      setVisibleManageCount(INITIAL_VISIBLE_COUNT);
                      setShowManageFilters(false);
                    }}
                    className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap flex-shrink-0 ${manageTab === "installments" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  >
                    รายการผ่อน
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setManageTab("budgets");
                      setSearchTerm("");
                      setManageSort({ key: "id", direction: "desc" });
                      setVisibleManageCount(INITIAL_VISIBLE_COUNT);
                      setShowManageFilters(false);
                    }}
                    className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap flex-shrink-0 ${manageTab === "budgets" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  >
                    งบประมาณ
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setManageTab("categories");
                      setSearchTerm("");
                      setManageSort({ key: "id", direction: "desc" });
                      setVisibleManageCount(INITIAL_VISIBLE_COUNT);
                      setShowManageFilters(false);
                    }}
                    className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap flex-shrink-0 ${manageTab === "categories" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  >
                    หมวดหมู่
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setManageTab("owners");
                      setSearchTerm("");
                      setManageSort({ key: "id", direction: "desc" });
                      setVisibleManageCount(INITIAL_VISIBLE_COUNT);
                      setShowManageFilters(false);
                    }}
                    className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap flex-shrink-0 ${manageTab === "owners" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  >
                    เจ้าของ
                  </button>
                </div>
              </div>

              {/* Row 2: Title & Count */}
              <div className="flex flex-col">
                <h2 className="text-sm font-bold text-slate-800 leading-tight">
                  จัดการข้อมูล{labelOf(manageTab)}
                </h2>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                  <p className="text-[10px] font-medium text-slate-400">
                    ทั้งหมด {sortedItems.length} รายการ
                  </p>
                  {(manageTab === "budgets" || manageTab === "recurring" || manageTab === "installments") && (
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-[1px] bg-slate-200 hidden sm:block"></div>
                      <p className="text-[10px] font-medium text-slate-400 whitespace-nowrap">
                        {manageTab === "budgets" ? (
                          <span className="flex items-center gap-2">
                            <span className="text-slate-400">งบรวม:</span>
                            <span className="text-slate-400 font-medium">{sortedItems.reduce((sum, it) => sum + Number(it.amount || 0), 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ฿</span>
                          </span>
                        ) : manageTab === "installments" ? (
                          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            {(() => {
                              const activeItems = sortedItems.filter(it => !it.isCompleted);
                              return (
                                <>
                                  <span className="flex items-center gap-1.5">
                                    <span className="text-slate-400">รวม:</span>
                                    <span className="text-slate-400 font-medium">{activeItems.reduce((sum, it) => sum + Number(it.total_amount || 0), 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ฿</span>
                                  </span>
                                  <div className="w-px h-2.5 bg-slate-200 hidden sm:block"></div>
                                  <span className="flex items-center gap-1.5">
                                    <span className="text-slate-400">คงเหลือ:</span>
                                    <span className="text-slate-400 font-medium">{activeItems.reduce((sum, it) => sum + Number(it.remaining_balance || 0), 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ฿</span>
                                  </span>
                                  <div className="w-px h-2.5 bg-slate-200 hidden sm:block"></div>
                                  <span className="flex items-center gap-1.5">
                                    <span className="text-slate-400">ต่องวด:</span>
                                    <span className="text-slate-400 font-medium">{activeItems.reduce((sum, it) => sum + Number(it.monthly_payment || 0), 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ฿</span>
                                  </span>
                                </>
                              );
                            })()}
                          </span>
                        ) : (
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <span className="flex items-center gap-1.5">
                              <span className="text-slate-400">รายรับ:</span>
                              <span className="text-slate-400 font-medium">{sortedItems.filter(it => it.type === 'income').reduce((sum, it) => sum + Number(it.amount || 0), 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ฿</span>
                            </span>
                            <div className="w-px h-2.5 bg-slate-200 hidden sm:block"></div>
                            <span className="flex items-center gap-1.5">
                              <span className="text-slate-400">รายจ่าย:</span>
                              <span className="text-slate-400 font-medium">{sortedItems.filter(it => it.type === 'expense').reduce((sum, it) => sum + Number(it.amount || 0), 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ฿</span>
                            </span>
                          </div>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Row 3: Controls (Filter, Search, Actions) */}
              <div className="flex flex-col gap-3">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowManageFilters(!showManageFilters)}
                      className={`flex items-center gap-1.5 py-1 px-2.5 rounded-lg border text-[11px] sm:text-xs font-bold transition-all active:scale-95 shrink-0 ${showManageFilters
                        ? "bg-slate-100 border-slate-300 text-slate-800 shadow-sm"
                        : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                        }`}
                    >
                      <Filter className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Filter</span>
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showManageFilters ? "rotate-180" : ""}`} />
                    </button>

                    <div className="flex items-center gap-2">
                      {manageTab === "recurring" && (
                        <button
                          type="button"
                          onClick={handleProcessRecurring}
                          disabled={isProcessingRecurring || pendingRecurringCount === 0}
                          className={`flex items-center justify-center gap-1.5 py-1 px-2.5 rounded-lg font-bold text-[11px] sm:text-xs active:scale-95 transition-all whitespace-nowrap shrink-0 ${isProcessingRecurring || pendingRecurringCount === 0
                            ? 'bg-slate-50 border border-slate-200 text-slate-400 cursor-not-allowed opacity-60'
                            : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-md'
                            }`}
                          title={pendingRecurringCount === 0 ? "ไม่มีรายการที่ต้องประมวลผลวันนี้" : "ประมวลผลรายการประจำของวันนี้ทันที"}
                        >
                          {isProcessingRecurring ? (
                            <div className="h-3 w-3 border-2 border-emerald-600/30 border-t-emerald-600 rounded-full animate-spin"></div>
                          ) : (
                            <Play className={`h-3.5 w-3.5 ${pendingRecurringCount === 0 ? 'text-slate-300' : ''}`} />
                          )}
                          <span>Run {pendingRecurringCount > 0 ? `(${pendingRecurringCount})` : ''}</span>
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={openCreateModal}
                        className="flex items-center justify-center gap-1.5 py-1 px-2.5 rounded-lg bg-gradient-to-r from-sky-500 to-blue-600 text-white font-bold text-[11px] sm:text-xs hover:from-sky-600 hover:to-blue-700 active:scale-95 transition-all whitespace-nowrap shrink-0 shadow-md"
                        title={`เพิ่ม${labelOf(manageTab)}`}
                      >
                        <Plus className="h-3.5 w-3.5" />
                        <span>เพิ่มข้อมูล</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Inline Filters for Desktop (New Row) */}
                {showManageFilters && (
                  <div className="hidden md:flex flex-wrap items-center gap-3 text-[11px] animate-in fade-in slide-in-from-top-2 duration-200">
                    {/* Owner Filter (Shared for Recurring and Budgets) */}
                    {/* Owner Filter (Shared for Recurring, Budgets, and Installments) */}
                    {(manageTab === "recurring" || manageTab === "budgets" || manageTab === "installments") && (
                      <div className="w-32">
                        <TailwindDropdown
                          options={[{ id: "all", username: "เจ้าของ" }, ...owners]}
                          value={filterOwnerId}
                          onChange={(e) => setFilterOwnerId(e.target.value)}
                          optionLabel="username"
                          optionValue="id"
                          placeholder="เจ้าของ"
                          searchable={owners.length > 5}
                          className="text-[11px]"
                        />
                      </div>
                    )}

                    {/* Recurring Specific Filters */}
                    {/* Recurring or Installments Specific Filters */}
                    {(manageTab === "recurring" || manageTab === "installments") && (
                      <>
                        {(manageTab === "recurring" || manageTab === "installments") && (
                          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm h-7 items-center overflow-hidden">
                            {(manageTab === "recurring"
                              ? [
                                { id: "all", name: "ทั้งหมด" },
                                { id: "active", name: "Active" },
                                { id: "inactive", name: "Inactive" },
                              ]
                              : [
                                { id: "all", name: "ทั้งหมด" },
                                { id: "paying", name: "กำลังผ่อน" },
                                { id: "completed", name: "ครบแล้ว" },
                              ]
                            ).map((opt) => (
                              <button
                                key={opt.id}
                                type="button"
                                onClick={() => setFilterStatus(opt.id)}
                                className={`
                                  flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] sm:text-xs font-medium transition-all
                                  ${filterStatus === opt.id
                                    ? "bg-blue-50 text-blue-700 shadow-sm"
                                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                                  }
                                `}
                              >
                                {opt.name}
                              </button>
                            ))}
                          </div>
                        )}

                        <div className="w-32">
                          <TailwindDropdown
                            options={[{ id: "all", name: "วิธีชำระ" }, ...paymentMethods]}
                            value={filterPaymentMethodId}
                            onChange={(e) => setFilterPaymentMethodId(e.target.value)}
                            placeholder="วิธีชำระ"
                            searchable={paymentMethods.length > 5}
                            className="text-[11px]"
                          />
                        </div>

                        {manageTab === "recurring" && (
                          <>
                            <div className="w-32">
                              <TailwindDropdown
                                options={[{ id: "all", name: "หมวดหมู่" }, ...categories]}
                                value={filterCategoryId}
                                onChange={(e) => setFilterCategoryId(e.target.value)}
                                placeholder="หมวดหมู่"
                                searchable={categories.length > 5}
                                className="text-[11px]"
                              />
                            </div>

                            <div className="w-28">
                              <TailwindDropdown
                                options={[
                                  { id: "all", name: "ความถี่" },
                                  { id: "once", name: "ครั้งเดียว" },
                                  { id: "daily", name: "ทุกวัน" },
                                  { id: "weekly", name: "ทุกสัปดาห์" },
                                  { id: "monthly", name: "ทุกเดือน" },
                                  { id: "yearly", name: "ทุกปี" },
                                ]}
                                value={filterFrequency}
                                onChange={(e) => setFilterFrequency(e.target.value)}
                                placeholder="ความถี่"
                                searchable={false}
                                className="text-[11px]"
                              />
                            </div>

                            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm h-7 items-center overflow-hidden">
                              {[
                                { id: "all", name: "ทั้งหมด" },
                                { id: "success", name: "สำเร็จ" },
                                { id: "error", name: "ล้มเหลว" },
                                { id: "pending", name: "รอ" },
                              ].map((opt) => (
                                <button
                                  key={opt.id}
                                  type="button"
                                  onClick={() => setFilterExecStatus(opt.id)}
                                  className={`
                                    flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] sm:text-xs font-medium transition-all
                                    ${filterExecStatus === opt.id
                                      ? "bg-blue-50 text-blue-700 shadow-sm"
                                      : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                                    }
                                  `}
                                >
                                  {opt.name}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {/* Budgets Specific Filters */}
                    {manageTab === "budgets" && (
                      <MonthYearPicker
                        value={managePeriod}
                        onChange={(e) => setManagePeriod(e.target.value)}
                        className="w-36"
                        variant="form"
                      />
                    )}

                    {/* Search Bar */}
                    <div className="w-48">
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={`ค้นหา${labelOf(manageTab)}...`}
                        className="w-full px-3 h-8 bg-white border rounded-lg text-xs focus:outline-none shadow-sm"
                      />
                    </div>
                  </div>
                )}

                {/* Mobile Filters & Search (Stacked - Only visible when Filter is active) */}
                {showManageFilters && (
                  <div className="md:hidden flex flex-col gap-3 w-full animate-in fade-in slide-in-from-top-2 duration-200 pt-1 border-t border-gray-100">
                    {/* Recurring Specific Filters */}
                    {(manageTab === "recurring" || manageTab === "installments") && (
                      <div className="flex flex-col gap-2">
                        <TailwindDropdown
                          options={[{ id: "all", username: "เจ้าของ" }, ...owners]}
                          value={filterOwnerId}
                          onChange={(e) => setFilterOwnerId(e.target.value)}
                          optionLabel="username"
                          optionValue="id"
                          placeholder="เจ้าของ"
                          searchable={owners.length > 5}
                          direction="auto"
                        />

                        {(manageTab === "recurring" || manageTab === "installments") && (
                          <div className="inline-flex w-full rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm h-7 items-center overflow-hidden mb-1">
                            {(manageTab === "recurring"
                              ? [
                                { id: "all", name: "ทั้งหมด" },
                                { id: "active", name: "Active" },
                                { id: "inactive", name: "Inactive" },
                              ]
                              : [
                                { id: "all", name: "ทั้งหมด" },
                                { id: "paying", name: "กำลังผ่อน" },
                                { id: "completed", name: "ครบแล้ว" },
                              ]
                            ).map((opt) => (
                              <button
                                key={opt.id}
                                type="button"
                                onClick={() => setFilterStatus(opt.id)}
                                className={`
                                  flex-1 flex items-center justify-center gap-1 rounded-md px-2 py-0.5 text-[10px] sm:text-xs font-medium transition-all
                                  ${filterStatus === opt.id
                                    ? "bg-blue-50 text-blue-700 shadow-sm"
                                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                                  }
                                `}
                              >
                                {opt.name}
                              </button>
                            ))}
                          </div>
                        )}

                        <TailwindDropdown
                          options={[{ id: "all", name: "วิธีชำระ" }, ...paymentMethods]}
                          value={filterPaymentMethodId}
                          onChange={(e) => setFilterPaymentMethodId(e.target.value)}
                          placeholder="วิธีชำระ"
                          searchable={paymentMethods.length > 5}
                          direction="auto"
                        />

                        {manageTab === "recurring" && (
                          <>
                            <TailwindDropdown
                              options={[{ id: "all", name: "หมวดหมู่" }, ...categories]}
                              value={filterCategoryId}
                              onChange={(e) => setFilterCategoryId(e.target.value)}
                              placeholder="หมวดหมู่"
                              searchable={categories.length > 5}
                              direction="auto"
                            />

                            <TailwindDropdown
                              options={[
                                { id: "all", name: "ความถี่" },
                                { id: "once", name: "ครั้งเดียว" },
                                { id: "daily", name: "ทุกวัน" },
                                { id: "weekly", name: "ทุกสัปดาห์" },
                                { id: "monthly", name: "ทุกเดือน" },
                                { id: "yearly", name: "ทุกปี" },
                              ]}
                              value={filterFrequency}
                              onChange={(e) => setFilterFrequency(e.target.value)}
                              placeholder="ความถี่"
                              searchable={false}
                              direction="auto"
                            />

                            <div className="inline-flex w-full rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm h-7 items-center overflow-hidden">
                              {[
                                { id: "all", name: "ทั้งหมด" },
                                { id: "success", name: "สำเร็จ" },
                                { id: "error", name: "ล้มเหลว" },
                                { id: "pending", name: "รอ" },
                              ].map((opt) => (
                                <button
                                  key={opt.id}
                                  type="button"
                                  onClick={() => setFilterExecStatus(opt.id)}
                                  className={`
                                    flex-1 flex items-center justify-center gap-1 rounded-md px-2 py-0.5 text-[10px] sm:text-xs font-medium transition-all
                                    ${filterExecStatus === opt.id
                                      ? "bg-blue-50 text-blue-700 shadow-sm"
                                      : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                                    }
                                  `}
                                >
                                  {opt.name}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Budgets Specific Filters */}
                    {manageTab === "budgets" && (
                      <div className="flex flex-col gap-1">
                        <MonthYearPicker
                          value={managePeriod}
                          onChange={(e) => setManagePeriod(e.target.value)}
                          className="w-full"
                          variant="form"
                        />
                      </div>
                    )}

                    {/* Unified Search Bar for Mobile (At the bottom) */}
                    <div className="relative w-full">
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={`ค้นหา${labelOf(manageTab)}...`}
                        className="w-full px-3 h-10 bg-white border rounded-xl text-sm focus:outline-none shadow-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </header>

            {/* Desktop Table View */}
            <div className="hidden md:block rounded-xl border border-gray-100 overflow-x-auto bg-white">
              <table className="w-full text-sm table-auto">
                <colgroup>
                  {manageTab === "budgets" ? (
                    <>
                      <col className="w-[150px]" />
                      <col className="w-[150px]" />
                      <col className="w-[180px]" />
                      <col />
                    </>
                  ) : manageTab === "categories" ? (
                    <>
                      <col className="w-[250px]" />
                      <col className="w-[120px]" />
                      <col />
                    </>
                  ) : manageTab === "recurring" ? (
                    <>
                      <col className="w-[180px]" />
                      <col className="w-[100px]" />
                      <col className="w-[100px]" />
                      <col className="w-[110px]" />
                      <col className="w-[90px]" />
                      <col className="w-[130px]" />
                      <col className="w-[100px]" />
                      <col className="w-[100px]" />
                      <col className="w-[100px]" />
                      <col className="w-[90px]" />
                      <col className="w-[80px]" />
                    </>
                  ) : manageTab === "installments" ? (
                    <>
                      <col className="w-[180px]" />
                      <col className="w-[150px]" />
                      <col className="w-[120px]" />
                      <col className="w-[80px]" />
                      <col className="w-[100px]" />
                      <col className="w-[100px]" />
                      <col className="w-[100px]" />
                      <col className="w-[100px]" />
                      <col />
                    </>
                  ) : (
                    <>
                      <col className="w-[250px]" />
                      <col />
                    </>
                  )}
                </colgroup>
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-slate-600 text-sm">
                    <th className={`px-2 py-1 text-left font-semibold ${manageTab === "budgets" ? "w-[20%]" : ""}`}>
                      <button
                        type="button"
                        onClick={() => toggleManageSort(manageTab === "categories" ? "name" : (manageTab === "recurring" || manageTab === "installments" ? "title" : "username"))}
                        className="flex items-center gap-1.5"
                      >
                        <span>{manageTab === "budgets" ? "เจ้าของ" : (manageTab === "recurring" ? "รายการ" : labelOf(manageTab))}</span>
                        {renderManageSortIndicator(manageTab === "categories" ? "name" : (manageTab === "recurring" || manageTab === "installments" ? "title" : "username"))}
                      </button>
                    </th>
                    {manageTab === "recurring" && (
                      <>
                        <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">
                          <button type="button" onClick={() => toggleManageSort("payment_method_name")} className="flex items-center gap-1.5">
                            <span>วิธีชำระ</span>
                            {renderManageSortIndicator("payment_method_name")}
                          </button>
                        </th>
                        <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">
                          <button type="button" onClick={() => toggleManageSort("category_name")} className="flex items-center gap-1.5">
                            <span>หมวดหมู่</span>
                            {renderManageSortIndicator("category_name")}
                          </button>
                        </th>
                        <th className="px-2 py-1 text-right font-semibold whitespace-nowrap">
                          <button type="button" onClick={() => toggleManageSort("amount")} className="flex items-center gap-1.5 justify-end">
                            <span>จำนวนเงิน</span>
                            {renderManageSortIndicator("amount")}
                          </button>
                        </th>
                        <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">
                          <button type="button" onClick={() => toggleManageSort("username")} className="flex items-center gap-1.5">
                            <span>เจ้าของ</span>
                            {renderManageSortIndicator("username")}
                          </button>
                        </th>
                        <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">
                          <button type="button" onClick={() => toggleManageSort("frequency")} className="flex items-center gap-1.5">
                            <span>รายการล่วงหน้า</span>
                            {renderManageSortIndicator("frequency")}
                          </button>
                        </th>
                        <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">
                          <button type="button" onClick={() => toggleManageSort("start_date")} className="flex items-center gap-1.5">
                            <span>วันเริ่มต้น</span>
                            {renderManageSortIndicator("start_date")}
                          </button>
                        </th>
                        <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">
                          <button type="button" onClick={() => toggleManageSort("end_date")} className="flex items-center gap-1.5">
                            <span>วันสิ้นสุด</span>
                            {renderManageSortIndicator("end_date")}
                          </button>
                        </th>
                        <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">
                          <button type="button" onClick={() => toggleManageSort("last_execution_status")} className="flex items-center gap-1.5">
                            <span>สถานะการรันล่าสุด</span>
                            {renderManageSortIndicator("last_execution_status")}
                          </button>
                        </th>
                        <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">
                          <button type="button" onClick={() => toggleManageSort("next_execution_date")} className="flex items-center gap-1.5">
                            <span>รันครั้งถัดไป</span>
                            {renderManageSortIndicator("next_execution_date")}
                          </button>
                        </th>
                      </>
                    )}
                    {manageTab === "installments" && (
                      <>
                        <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">
                          <button type="button" onClick={() => toggleManageSort("payment_method_name")} className="flex items-center gap-1.5">
                            <span>วิธีชำระ</span>
                            {renderManageSortIndicator("payment_method_name")}
                          </button>
                        </th>
                        <th className="px-2 py-1 text-right font-semibold whitespace-nowrap">
                          <button type="button" onClick={() => toggleManageSort("total_amount")} className="flex items-center gap-1.5 justify-end w-full">
                            <span>ยอดรวม</span>
                            {renderManageSortIndicator("total_amount")}
                          </button>
                        </th>
                        <th className="px-2 py-1 text-right font-semibold whitespace-nowrap">
                          <button type="button" onClick={() => toggleManageSort("remaining_balance")} className="flex items-center gap-1.5 justify-end w-full">
                            <span>ยอดคงเหลือ</span>
                            {renderManageSortIndicator("remaining_balance")}
                          </button>
                        </th>
                        <th className="px-2 py-1 text-right font-semibold whitespace-nowrap">
                          <button type="button" onClick={() => toggleManageSort("months")} className="flex items-center gap-1.5 justify-end w-full">
                            <span>งวด</span>
                            {renderManageSortIndicator("months")}
                          </button>
                        </th>
                        <th className="px-2 py-1 text-right font-semibold whitespace-nowrap">
                          <button type="button" onClick={() => toggleManageSort("monthly_payment")} className="flex items-center gap-1.5 justify-end w-full">
                            <span>ต่องวด</span>
                            {renderManageSortIndicator("monthly_payment")}
                          </button>
                        </th>
                        <th className="px-2 py-1 text-right font-semibold whitespace-nowrap">
                          <button type="button" onClick={() => toggleManageSort("start_month")} className="flex items-center gap-1.5 justify-end w-full">
                            <span>เริ่ม</span>
                            {renderManageSortIndicator("start_month")}
                          </button>
                        </th>
                        <th className="px-2 py-1 text-right font-semibold whitespace-nowrap">
                          <button type="button" onClick={() => toggleManageSort("end_month")} className="flex items-center gap-1.5 justify-end w-full">
                            <span>สิ้นสุด</span>
                            {renderManageSortIndicator("end_month")}
                          </button>
                        </th>
                        <th className="px-2 py-1 text-center font-semibold whitespace-nowrap">
                          <button type="button" onClick={() => toggleManageSort("isCompleted")} className="flex items-center gap-1.5 justify-center w-full">
                            <span>ความคืบหน้า</span>
                            {renderManageSortIndicator("isCompleted")}
                          </button>
                        </th>
                        <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">
                          <button type="button" onClick={() => toggleManageSort("ownersName")} className="flex items-center gap-1.5">
                            <span>เจ้าของ</span>
                            {renderManageSortIndicator("ownersName")}
                          </button>
                        </th>
                      </>
                    )}
                    {(manageTab === "categories" || manageTab === "budgets") && (
                      <th className={`px-2 py-1 text-left font-semibold ${manageTab === "budgets" ? "w-[25%]" : ""}`}>
                        {manageTab === "categories" ? (
                          <button
                            type="button"
                            onClick={() => toggleManageSort("type")}
                            className="flex items-center gap-1.5"
                          >
                            <span>ประเภท</span>
                            {renderManageSortIndicator("type")}
                          </button>
                        ) : (
                          <span>เดือน / ปี</span>
                        )}
                      </th>
                    )}
                    {manageTab === "budgets" && (
                      <th className={`px-2 py-1 text-left font-semibold ${manageTab === "budgets" ? "w-[30%]" : ""}`}>
                        <button
                          type="button"
                          onClick={() => toggleManageSort("amount")}
                          className="flex items-center gap-1.5 justify-start"
                        >
                          <span>งบประมาณ</span>
                          {renderManageSortIndicator("amount")}
                        </button>
                      </th>
                    )}
                    {manageTab === "recurring" && (
                      <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">
                        <button type="button" onClick={() => toggleManageSort("is_active")} className="flex items-center gap-1.5">
                          <span>สถานะ</span>
                          {renderManageSortIndicator("is_active")}
                        </button>
                      </th>
                    )}
                    <th className="px-2 py-1 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-slate-700">
                  {sortedItems.length === 0 ? (
                    <tr>
                      <td colSpan={manageTab === "recurring" ? 12 : (manageTab === "budgets" ? 4 : (manageTab === "categories" ? 3 : 2))} className="px-2 py-12 text-center text-slate-400">
                        <div className="flex flex-col items-center gap-3">
                          <Settings className="h-10 w-10 opacity-20" />
                          <p className="font-medium text-sm">ไม่พบข้อมูล{labelOf(manageTab)}</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    sortedItems.slice(0, visibleManageCount).map((it) => (
                      <tr key={it.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-2 py-1.5 font-medium text-slate-800">
                          <div className="truncate max-w-[180px] text-[11px]" title={manageTab === "recurring" || manageTab === "installments" ? it.title : (manageTab === "owners" || manageTab === "budgets" ? it.username : it.name)}>
                            {manageTab === "recurring" || manageTab === "installments" ? (it.title || "-") : (manageTab === "owners" || manageTab === "budgets" ? (it.username || "-") : (it.name || "-"))}
                          </div>
                        </td>
                        {manageTab === "categories" && (
                          <td className="px-2 py-1.5 text-slate-700">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${it.type === "income" ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
                              {it.type === "income" ? "รายรับ" : "รายจ่าย"}
                            </span>
                          </td>
                        )}
                        {manageTab === "budgets" && (
                          <td className="px-2 py-1.5 text-slate-700 text-[11px]">
                            {monthNames[(it.month || 1) - 1]} {it.year}
                          </td>
                        )}
                        {manageTab === "budgets" && (
                          <td className="px-2 py-1.5 text-left font-bold text-slate-900 tabular-nums text-[11px]">
                            {Number(it.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ฿
                          </td>
                        )}
                        {manageTab === "recurring" && (
                          <>
                            <td className="px-2 py-1.5 text-[11px] text-slate-600 whitespace-nowrap">{it.payment_method_name || "-"}</td>
                            <td className="px-2 py-1.5 text-[11px] text-slate-600 whitespace-nowrap">{it.category_name || "-"}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">
                              <span className={`text-[11px] ${it.type === "income" ? "text-green-400" : "text-red-400"}`}>
                                {it.type === "income" ? "+" : "-"}{Number(it.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </td>
                            <td className="px-2 py-1.5 text-[11px] text-slate-600 whitespace-nowrap">{it.username || "-"}</td>
                            <td className="px-2 py-1.5 text-[11px] text-slate-600 whitespace-nowrap">
                              {(() => {
                                if (it.frequency === 'once') return `ครั้งเดียว (${it.execution_date ? dayjs(it.execution_date).format('DD/MM/YYYY') : '-'})`;
                                if (it.frequency === 'daily') return 'ทุกวัน';
                                if (it.frequency === 'weekly') {
                                  const days = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];
                                  return `ทุกวัน${days[it.execution_day_of_week] || ''}`;
                                }
                                if (it.frequency === 'monthly') return `ทุกวันที่ ${it.execution_day_of_month}`;
                                if (it.frequency === 'yearly') {
                                  const months = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
                                  return `ทุกวันที่ ${it.execution_day_of_month} ${months[it.execution_month] || ''}`;
                                }
                                return it.frequency;
                              })()}
                            </td>
                            <td className="px-2 py-1.5 text-[11px] text-slate-600 whitespace-nowrap">{it.frequency === 'once' ? "-" : (it.start_date ? dayjs(it.start_date).format('DD/MM/YYYY') : "-")}</td>
                            <td className="px-2 py-1.5 text-[11px] text-slate-600 whitespace-nowrap">{it.frequency === 'once' ? "-" : (it.end_date ? dayjs(it.end_date).format('DD/MM/YYYY') : "-")}</td>
                            <td className="px-2 py-1.5 text-sm text-slate-600 whitespace-nowrap">
                              {it.last_executed_date ? (
                                <div className="flex items-center gap-1.5 whitespace-nowrap text-[11px]">
                                  <div className="flex items-center">
                                    {it.last_execution_status === 'success' ? (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold">
                                        สำเร็จ
                                      </span>
                                    ) : it.last_execution_status === 'error' ? (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[9px] font-bold">
                                        ล้มเหลว
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[9px] font-bold">
                                        รอดำเนินการ
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-slate-500">{dayjs(it.last_executed_date).format('DD/MM/YYYY HH:mm')}</span>
                                </div>
                              ) : (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[9px] font-bold">
                                  รอดำเนินการ
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-1.5 text-[11px] text-slate-600 whitespace-nowrap font-medium">
                              {it.is_active && it.next_execution_date ? (
                                <span className="text-slate-500">{dayjs(it.next_execution_date).format('DD/MM/YYYY')}</span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="px-2 py-1.5 text-slate-700">
                              {it.is_active ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[11px]">Active</span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-[11px]">Inactive</span>
                              )}
                            </td>
                          </>
                        )}
                        {manageTab === "installments" && (
                          <>
                            <td className="px-2 py-1.5 text-[11px] text-slate-600 whitespace-nowrap">{it.payment_method_name || "-"}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-[11px] text-slate-600">
                              {Number(it.total_amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-[11px] text-red-400">
                              {Number(it.remaining_balance || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-2 py-1.5 text-right text-[11px] text-slate-600 whitespace-nowrap">{it.months > 0 ? it.months : "-"}</td>
                            <td className="px-2 py-1.5 text-right text-[11px] text-red-400 whitespace-nowrap">
                              {Number(it.monthly_payment || (it.total_amount / it.months) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-2 py-1.5 text-right text-[11px] text-slate-600 whitespace-nowrap">{it.start_month_display || "-"}</td>
                            <td className="px-2 py-1.5 text-right text-[11px] text-slate-600 whitespace-nowrap">{it.end_month_display || "-"}</td>
                            <td className="px-2 py-1.5 text-center text-[10px] whitespace-nowrap">
                              {it.isCompleted ? (
                                <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-bold border border-emerald-100">
                                  ผ่อนครบ ({it.progressText})
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold">
                                  กำลังผ่อน ({it.progressText})
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-1.5 text-[10px] text-slate-500 whitespace-nowrap">{it.ownersName || "-"}</td>
                          </>
                        )}
                        <td className="px-2 py-2 text-right">
                          <div className="flex justify-end gap-1">
                            {manageTab === "recurring" && (
                              <button
                                type="button"
                                onClick={() => {
                                  setPendingProcessItem(it);
                                  setRecurringConfirmOpen(true);
                                }}
                                className="p-1 text-slate-400 hover:text-emerald-600 rounded-lg transition-all"
                                title="รันรายการนี้ทันที"
                              >
                                <Play className="h-[14px] w-[14px]" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => startEdit(it)}
                              className="p-1 text-slate-400 hover:text-blue-600 rounded-lg transition-all"
                              title="แก้ไข"
                            >
                              <SquarePen className="h-[14px] w-[14px]" />
                            </button>
                            <button
                              type="button"
                              onClick={() => requestManageDelete(it.id)}
                              className="p-1 text-slate-400 hover:text-rose-600 rounded-lg transition-all"
                              title="ลบ"
                            >
                              <Trash className="h-[14px] w-[14px]" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {sortedItems.length === 0 ? (
                <div className="py-12 bg-white rounded-xl border border-dashed border-slate-200 text-center text-slate-400">
                  <p className="font-medium text-sm">ไม่พบข้อมูล{labelOf(manageTab)}</p>
                </div>
              ) : (
                sortedItems.slice(0, visibleManageCount).map((it) => (
                  <div key={it.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-slate-900 truncate">
                          {manageTab === "recurring" || manageTab === "installments" ? (it.title || "-") : (manageTab === "owners" || manageTab === "budgets" ? (it.username || "-") : (it.name || "-"))}
                        </p>
                        {manageTab === "recurring" && (
                          <p className="text-[11px] text-slate-400 font-medium mt-0.5 truncate">
                            {it.category_name || "-"} {it.payment_method_name ? `• ${it.payment_method_name}` : ""}
                          </p>
                        )}
                        {manageTab === "installments" && (
                          <p className="text-[11px] text-slate-400 font-medium mt-0.5 truncate">
                            {it.payment_method_name || "-"}
                          </p>
                        )}
                        {manageTab === "categories" && (
                          <p className="mt-1">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${it.type === "income" ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
                              {it.type === "income" ? "รายรับ" : "รายจ่าย"}
                            </span>
                          </p>
                        )}
                        {manageTab === "budgets" && (
                          <div className="mt-1 flex flex-col gap-1">
                            <p className="text-[11px] text-slate-500">{monthNames[(it.month || 1) - 1]} {it.year}</p>
                            <p className="text-xs font-bold text-slate-900 tabular-nums">{Number(it.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ฿</p>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {manageTab === "recurring" && (
                          <button
                            type="button"
                            onClick={() => {
                              setPendingProcessItem(it);
                              setRecurringConfirmOpen(true);
                            }}
                            className="p-1 text-slate-400 hover:text-emerald-600 rounded-lg transition-all"
                            title="รันรายการนี้ทันที"
                          >
                            <Play className="h-[14px] w-[14px]" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => startEdit(it)}
                          className="p-1 text-slate-400 hover:text-blue-600"
                        >
                          <SquarePen className="h-[14px] w-[14px]" />
                        </button>
                        <button
                          type="button"
                          onClick={() => requestManageDelete(it.id)}
                          className="p-1 text-slate-400 hover:text-rose-600"
                        >
                          <Trash className="h-[14px] w-[14px]" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3 pt-1">
                      {manageTab === "recurring" ? (
                        <div className="space-y-3">
                          <div className="flex items-baseline justify-between">
                            <p className="text-sm font-bold tabular-nums">
                              <span className={it.type === "income" ? "text-emerald-600" : "text-rose-600"}>
                                {it.type === "income" ? "+" : "-"}{Number(it.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </p>
                          </div>

                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-bold">
                              {(() => {
                                if (it.frequency === 'once') return `ครั้งเดียว (${it.execution_date ? dayjs(it.execution_date).format('DD/MM/YYYY') : '-'})`;
                                if (it.frequency === 'daily') return 'ทุกวัน';
                                if (it.frequency === 'weekly') {
                                  const days = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];
                                  return `ทุกวัน${days[it.execution_day_of_week] || ''}`;
                                }
                                if (it.frequency === 'monthly') return `ทุกวันที่ ${it.execution_day_of_month}`;
                                if (it.frequency === 'yearly') {
                                  const months = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
                                  return `ทุกวันที่ ${it.execution_day_of_month} ${months[it.execution_month] || ''}`;
                                }
                                return it.frequency;
                              })()}
                            </span>
                            {it.is_active ? (
                              <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold border border-emerald-100">Active</span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold border border-slate-200">Inactive</span>
                            )}
                          </div>
                        </div>
                      ) : manageTab === "installments" ? (
                        <div className="space-y-3">
                          <div className="flex items-baseline justify-between">
                            <p className="text-sm font-medium text-slate-600 tabular-nums">
                              {Number(it.total_amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              <span className="text-[10px] text-slate-400 font-normal ml-1">(ยอดรวม)</span>
                            </p>
                            <p className="text-xs font-medium text-red-400 tabular-nums">
                              {Number(it.monthly_payment || (it.total_amount / it.months) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              <span className="text-[10px] ml-0.5">/งวด</span>
                            </p>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-baseline gap-1">
                              <span className="text-[10px] text-slate-400">คงเหลือ</span>
                              <span className="text-sm font-medium text-red-400 tabular-nums">
                                {Number(it.remaining_balance || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                            <div className="flex gap-1.5">
                              {it.isCompleted ? (
                                <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-600 text-[10px] font-bold border border-emerald-100">
                                  ผ่อนครบ ({it.progressText})
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 rounded bg-slate-50 text-slate-500 text-[10px] font-bold border border-slate-100">
                                  กำลังผ่อน ({it.progressText})
                                </span>
                              )}
                              {Number(it.months) > 0 && (
                                <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-bold">
                                  {it.months} งวด
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : null}

                      {Boolean(it.start_date || it.end_date || it.start_month_display || it.end_month_display || ((it.username || it.ownersName) && manageTab !== "budgets" && manageTab !== "owners")) && (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-500 font-medium">
                          {Boolean((it.username || it.ownersName) && manageTab !== "budgets" && manageTab !== "owners") && (
                            <div className="flex items-center gap-1">
                              <User size={10} className="text-slate-400" />
                              <span>{it.username || it.ownersName}</span>
                            </div>
                          )}
                          {Boolean(it.start_date || it.start_month_display) && (
                            <div className="flex items-center gap-1">
                              <span className="text-slate-400">เริ่ม:</span>
                              <span>{it.start_month_display || dayjs(it.start_date).format('DD/MM/YYYY')}</span>
                            </div>
                          )}
                          {Boolean(it.end_date || it.end_month_display) && (
                            <div className="flex items-center gap-1">
                              <span className="text-slate-400">สิ้นสุด:</span>
                              <span>{it.end_month_display || dayjs(it.end_date).format('DD/MM/YYYY')}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {Boolean(it.last_executed_date || it.next_execution_date) && (
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-2 border-t border-gray-50">
                          {Boolean(it.last_executed_date) && (
                            <div className="flex items-center gap-1.5 mr-1">
                              {it.last_execution_status === 'success' ? (
                                <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold whitespace-nowrap">สำเร็จ</span>
                              ) : it.last_execution_status === 'error' ? (
                                <span className="px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[9px] font-bold whitespace-nowrap">ล้มเหลว</span>
                              ) : (
                                <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[9px] font-bold whitespace-nowrap">รอดำเนินการ</span>
                              )}
                              <span className="text-[10px] text-slate-400 font-medium whitespace-nowrap">{dayjs(it.last_executed_date).format('DD/MM/YYYY HH:mm')}</span>
                            </div>
                          )}
                          {Boolean(it.is_active && it.next_execution_date) && (
                            <div className="flex items-center gap-1">
                              <CalendarClock size={10} className="text-blue-400" />
                              <span className="text-[10px] text-slate-400 font-medium whitespace-nowrap">ถัดไป: {dayjs(it.next_execution_date).format('DD/MM/YYYY')}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {sortedItems.length > visibleManageCount && (
              <div className="mt-4 flex justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setVisibleManageCount(p => Math.min(p + INITIAL_VISIBLE_COUNT, sortedItems.length))}
                  className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white text-slate-700 hover:bg-gray-50"
                >
                  Load more
                </button>
                <button
                  type="button"
                  onClick={() => setVisibleManageCount(sortedItems.length)}
                  className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white text-slate-700 hover:bg-gray-50"
                >
                  All ({sortedItems.length})
                </button>
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-slate-500 font-medium">
              {/* <p>แสดงทั้งหมด {Math.min(visibleManageCount, sortedItems.length)} จาก {sortedItems.length} รายการ</p> */}
            </div>
          </section>
        </main>
      </div>

      {/* Add/Edit Modal */}
      {formOpen && (
        <Modal
          isOpen={formOpen}
          onClose={cancelEdit}
          title={editingItem ? `แก้ไข${labelOf(manageTab)}` : `เพิ่ม${labelOf(manageTab)}`}
          size="lg"
          variant="bottom"
        >
          <form onSubmit={submitManage} className="px-1 md:px-2 pt-0 pb-3 space-y-4" noValidate>

            {manageTab === "recurring" ? (
              <div className="space-y-4">
                {/* Type Selector */}
                <div className="flex bg-slate-100 rounded-xl p-1 border border-slate-200 max-w-[240px] mx-auto select-none">
                  <button
                    type="button"
                    onClick={() => setFormInputs(p => ({ ...p, type: "expense" }))}
                    className={`w-1/2 py-1 rounded-lg transition-all duration-200 text-xs font-bold ${formInputs.type === "expense"
                      ? "bg-white text-rose-600 shadow-sm"
                      : "bg-transparent text-slate-500 hover:text-slate-800"
                      }`}
                  >
                    รายจ่าย
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormInputs(p => ({ ...p, type: "income" }))}
                    className={`w-1/2 py-1 rounded-lg transition-all duration-200 text-xs font-bold ${formInputs.type === "income"
                      ? "bg-white text-emerald-600 shadow-sm"
                      : "bg-transparent text-slate-500 hover:text-slate-800"
                      }`}
                  >
                    รายรับ
                  </button>
                </div>

                {/* Amount & Title */}
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                      ชื่อรายการ
                    </label>
                    <div className="relative mt-1">
                      <input
                        type="text"
                        name="title"
                        autoComplete="off"
                        value={formInputs.title}
                        onChange={onChangeInput}
                        className={`w-full rounded-lg border bg-white px-4 h-8 text-base md:text-sm text-slate-800 outline-none transition-all shadow-sm ${manageFieldErrors.title ? 'border-rose-500 focus:ring-1 focus:ring-rose-500/20' : 'border-gray-300'}`}
                        placeholder="ระบุชื่อรายการ"
                        required
                      />
                    </div>
                    {manageFieldErrors.title && (
                      <p className="mt-1 text-[10px] font-bold text-rose-500 leading-none">{manageFieldErrors.title}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                      จำนวนเงิน
                    </label>
                    <div className="relative mt-1">
                      <input
                        type="text"
                        name="amount"
                        autoComplete="off"
                        inputMode="decimal"
                        value={formInputs.amount}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.,]/g, "");
                          setFormInputs(p => ({ ...p, amount: val }));
                          setManageFieldErrors(prev => {
                            const c = { ...prev };
                            delete c.amount;
                            return c;
                          });
                        }}
                        onBlur={(e) => {
                          const n = parseMoney(e.target.value);
                          if (n > 0) {
                            setFormInputs(p => ({ ...p, amount: n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }));
                          }
                        }}
                        className={`w-full rounded-lg border bg-white px-4 h-8 text-base md:text-sm text-slate-800 outline-none transition-all shadow-sm tabular-nums ${manageFieldErrors.amount ? 'border-rose-500 focus:ring-1 focus:ring-rose-500/20' : 'border-gray-300'}`}
                        placeholder="0.00"
                        required
                      />
                    </div>
                    {manageFieldErrors.amount && (
                      <p className="mt-1 text-[10px] font-bold text-rose-500 leading-none">{manageFieldErrors.amount}</p>
                    )}
                  </div>
                </div>

                {/* Payment Method & Category */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <TailwindDropdown
                      label="วิธีชำระ"
                      options={paymentMethods}
                      value={formInputs.payment_method_id}
                      onChange={(e) => setFormInputs(p => ({ ...p, payment_method_id: e.target.value }))}
                      placeholder="เลือกวิธีชำระ"
                      className={`payment_method_id ${manageFieldErrors.payment_method_id ? 'border-rose-500' : ''}`}
                      optionLabel="name"
                      optionValue="id"
                    />
                    {manageFieldErrors.payment_method_id && (
                      <p className="mt-1 text-[10px] font-bold text-rose-500 leading-none">{manageFieldErrors.payment_method_id}</p>
                    )}
                  </div>
                  <div>
                    <TailwindDropdown
                      label="หมวดหมู่"
                      options={categories.filter(c => c.type === formInputs.type)}
                      value={formInputs.category_id}
                      onChange={(e) => setFormInputs(p => ({ ...p, category_id: e.target.value }))}
                      placeholder="เลือกหมวดหมู่"
                      className={`category_id ${manageFieldErrors.category_id ? 'border-rose-500' : ''}`}
                      optionLabel="name"
                      optionValue="id"
                      required
                    />
                    {manageFieldErrors.category_id && (
                      <p className="mt-1 text-[10px] font-bold text-rose-500 leading-none">{manageFieldErrors.category_id}</p>
                    )}
                  </div>
                </div>

                {/* Owner & Frequency */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <TailwindDropdown
                      label="เจ้าของรายการ"
                      options={owners}
                      value={formInputs.owner_id}
                      onChange={(e) => setFormInputs(p => ({ ...p, owner_id: e.target.value }))}
                      placeholder="เลือกเจ้าของ"
                      className="owner_id"
                      optionLabel="username"
                      optionValue="id"
                    />
                  </div>
                  <div>
                    <TailwindDropdown
                      label="รายการล่วงหน้า"
                      options={[
                        { id: "once", name: "ครั้งเดียว" },
                        { id: "daily", name: "รายวัน" },
                        { id: "weekly", name: "รายสัปดาห์" },
                        { id: "monthly", name: "รายเดือน" },
                        { id: "yearly", name: "รายปี" },
                      ]}
                      value={formInputs.frequency}
                      onChange={(e) => setFormInputs(p => ({ ...p, frequency: e.target.value }))}
                      placeholder="เลือกความถี่"
                      className="frequency"
                      optionLabel="name"
                      optionValue="id"
                    />
                  </div>
                </div>

                {/* Execution Details (Conditional) */}
                {(formInputs.frequency === 'once' || formInputs.frequency === 'weekly' || formInputs.frequency === 'monthly' || formInputs.frequency === 'yearly') && (
                  <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                    {formInputs.frequency === 'once' && (
                      <div className="col-span-2">
                        <TailwindDatepicker
                          label="วันที่"
                          name="execution_date"
                          value={formInputs.execution_date}
                          onChange={onChangeInput}
                          minDate={dayjs().add(1, "day").format("YYYY-MM-DD")}
                        />
                        {manageFieldErrors.execution_date && (
                          <p className="mt-1 text-[10px] font-bold text-rose-500 leading-none">{manageFieldErrors.execution_date}</p>
                        )}
                      </div>
                    )}

                    {formInputs.frequency === 'weekly' && (
                      <div className="col-span-2">
                        <TailwindDropdown
                          label="ทุกวัน"
                          options={[
                            { id: "0", name: "วันอาทิตย์" },
                            { id: "1", name: "วันจันทร์" },
                            { id: "2", name: "วันอังคาร" },
                            { id: "3", name: "วันพุธ" },
                            { id: "4", name: "วันพฤหัสบดี" },
                            { id: "5", name: "วันศุกร์" },
                            { id: "6", name: "วันเสาร์" },
                          ]}
                          value={formInputs.execution_day_of_week}
                          onChange={(e) => setFormInputs(p => ({ ...p, execution_day_of_week: e.target.value }))}
                          placeholder="เลือกวัน"
                          className="execution_day_of_week"
                          optionLabel="name"
                          optionValue="id"
                          searchable={false}
                        />
                        {manageFieldErrors.execution_day_of_week && (
                          <p className="mt-1 text-[10px] font-bold text-rose-500 leading-none">{manageFieldErrors.execution_day_of_week}</p>
                        )}
                      </div>
                    )}

                    {(formInputs.frequency === 'monthly' || formInputs.frequency === 'yearly') && (
                      <div className={formInputs.frequency === 'yearly' ? 'col-span-1' : 'col-span-2'}>
                        <TailwindDropdown
                          label="ทุกวันที่"
                          options={Array.from({ length: 31 }, (_, i) => ({ id: String(i + 1), name: String(i + 1) }))}
                          value={String(formInputs.execution_day_of_month)}
                          onChange={(e) => setFormInputs(p => ({ ...p, execution_day_of_month: e.target.value }))}
                          placeholder="เลือกวันที่"
                          className="execution_day_of_month"
                          optionLabel="name"
                          optionValue="id"
                          searchable={false}
                        />
                        {manageFieldErrors.execution_day_of_month && (
                          <p className="mt-1 text-[10px] font-bold text-rose-500 leading-none">{manageFieldErrors.execution_day_of_month}</p>
                        )}
                      </div>
                    )}

                    {formInputs.frequency === 'yearly' && (
                      <div className="col-span-1">
                        <TailwindDropdown
                          label="เดือน"
                          options={[
                            { id: "1", name: "มกราคม" },
                            { id: "2", name: "กุมภาพันธ์" },
                            { id: "3", name: "มีนาคม" },
                            { id: "4", name: "เมษายน" },
                            { id: "5", name: "พฤษภาคม" },
                            { id: "6", name: "มิถุนายน" },
                            { id: "7", name: "กรกฎาคม" },
                            { id: "8", name: "สิงหาคม" },
                            { id: "9", name: "กันยายน" },
                            { id: "10", name: "ตุลาคม" },
                            { id: "11", name: "พฤศจิกายน" },
                            { id: "12", name: "ธันวาคม" },
                          ]}
                          value={String(formInputs.execution_month)}
                          onChange={(e) => setFormInputs(p => ({ ...p, execution_month: e.target.value }))}
                          placeholder="เลือกเดือน"
                          className="execution_month"
                          optionLabel="name"
                          optionValue="id"
                          searchable={false}
                        />
                        {manageFieldErrors.execution_month && (
                          <p className="mt-1 text-[10px] font-bold text-rose-500 leading-none">{manageFieldErrors.execution_month}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Start Date & End Date */}
                {/* Start Date & End Date */}
                {formInputs.frequency !== 'once' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <TailwindDatepicker
                        label="วันเริ่มต้น"
                        name="start_date"
                        value={formInputs.start_date}
                        onChange={onChangeInput}
                        minDate={dayjs().add(1, "day").format("YYYY-MM-DD")}
                        className={manageFieldErrors.start_date ? 'border-rose-500' : ''}
                      />
                      {manageFieldErrors.start_date && (
                        <p className="mt-1 text-[10px] font-bold text-rose-500 leading-none">{manageFieldErrors.start_date}</p>
                      )}
                    </div>
                    <div>
                      <div className="flex justify-between items-center h-4 mb-1">
                        <label className="text-xs font-semibold text-slate-400 select-none">
                          วันสิ้นสุด
                        </label>
                        {formInputs.end_date && (
                          <button
                            type="button"
                            onClick={() => setFormInputs(p => ({ ...p, end_date: "" }))}
                            className="text-[10px] font-bold text-rose-500 hover:text-rose-600 transition-colors"
                          >
                            ไม่มีวันสิ้นสุด (ล้างค่า)
                          </button>
                        )}
                      </div>
                      <TailwindDatepicker
                        label=""
                        name="end_date"
                        value={formInputs.end_date}
                        onChange={onChangeInput}
                        minDate={dayjs().add(1, "day").format("YYYY-MM-DD")}
                      />
                    </div>
                  </div>
                )}

                {/* Status / Is Active */}
                <div className="pt-1">
                  <label className="flex items-center gap-2.5 cursor-pointer py-1.5 px-0.5 group select-none">
                    <div className="relative flex items-center">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formInputs.is_active}
                        onChange={e => setFormInputs(p => ({ ...p, is_active: e.target.checked }))}
                        className="peer w-4.5 h-4.5 text-blue-600 rounded border-gray-300 focus:ring-sky-500 transition-all"
                      />
                    </div>
                    <div className="flex flex-col">
                      <p className="text-xs font-bold text-slate-700 group-hover:text-sky-600 transition-colors">เปิดใช้งานรายการนี้</p>
                      <p className="text-[9px] text-slate-400">รายการนี้จะรันอัตโนมัติเมื่อถึงกำหนด</p>
                    </div>
                  </label>
                </div>
              </div>
            ) : manageTab === "installments" ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                      ชื่อรายการ
                    </label>
                    <div className="relative mt-1">
                      <input
                        type="text"
                        name="title"
                        value={formInputs.title}
                        onChange={onChangeInput}
                        className={`w-full rounded-lg border bg-white px-4 h-8 text-base md:text-sm text-slate-800 outline-none transition-all shadow-sm ${manageFieldErrors.title ? 'border-rose-500 focus:ring-1 focus:ring-rose-500/20' : 'border-gray-300'}`}
                        placeholder="ระบุชื่อรายการผ่อน"
                        autoComplete="off"
                      />
                    </div>
                    {manageFieldErrors.title && <p className="mt-1 text-[10px] font-bold text-rose-500 leading-none">{manageFieldErrors.title}</p>}
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                      ยอดรวม (บาท)
                    </label>
                    <div className="relative mt-1">
                      <input
                        type="text"
                        name="total_amount"
                        inputMode="decimal"
                        value={formInputs.total_amount}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.,]/g, "");
                          setFormInputs(p => ({ ...p, total_amount: val }));
                        }}
                        onBlur={(e) => {
                          const n = parseMoney(e.target.value);
                          if (n > 0) {
                            setFormInputs(p => ({ ...p, total_amount: n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }));
                          }
                        }}
                        className={`w-full rounded-lg border bg-white px-4 h-8 text-base md:text-sm text-slate-800 outline-none transition-all shadow-sm ${manageFieldErrors.total_amount ? 'border-rose-500' : 'border-gray-300'}`}
                        placeholder="0.00"
                        autoComplete="off"
                      />
                    </div>
                    {manageFieldErrors.total_amount && <p className="mt-1 text-[10px] font-bold text-rose-500 leading-none">{manageFieldErrors.total_amount}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <TailwindDropdown
                      label="จำนวนงวด"
                      options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 18, 24, 36, 48, 60].map(n => ({ id: String(n), name: `${n} งวด` }))}
                      value={formInputs.months}
                      onChange={(e) => setFormInputs(p => ({ ...p, months: e.target.value }))}
                      placeholder="เลือกจำนวนงวด"
                      className="months"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                      ดอกเบี้ยต่อเดือน (%)
                    </label>
                    <div className="relative mt-1">
                      <input
                        type="text"
                        name="interest_percent"
                        inputMode="decimal"
                        value={formInputs.interest_percent}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.]/g, "");
                          setFormInputs(p => ({ ...p, interest_percent: val }));
                        }}
                        className="w-full rounded-lg border border-gray-300 bg-white px-4 h-8 text-base md:text-sm text-slate-800 outline-none shadow-sm"
                        placeholder="0.00"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                      เดือนที่เริ่ม
                    </label>
                    <div className="relative mt-1">
                      <MonthYearPicker
                        value={formInputs.start_month}
                        onChange={(e) => setFormInputs(p => ({ ...p, start_month: e.target.value }))}
                        className="w-full"
                        variant="form"
                      />
                    </div>
                  </div>
                  <div>
                    <TailwindDropdown
                      label="วิธีชำระ"
                      options={paymentMethods}
                      value={formInputs.payment_method_id}
                      onChange={(e) => setFormInputs(p => ({ ...p, payment_method_id: e.target.value }))}
                      placeholder="เลือกวิธีชำระ"
                      className="payment_method_id"
                      optionLabel="name"
                      optionValue="id"
                    />
                    {manageFieldErrors.payment_method_id && <p className="mt-1 text-[10px] font-bold text-rose-500 leading-none">{manageFieldErrors.payment_method_id}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 select-none mb-1">
                    เจ้าของรายการ (เลือกได้มากกว่า 1)
                  </label>
                  <div className="flex flex-row items-center gap-2 p-2 bg-slate-50 rounded-xl border border-slate-100 overflow-x-auto no-scrollbar">
                    {owners.map(o => {
                      const isSelected = formInputs.owner_ids.includes(o.id);
                      return (
                        <button
                          key={o.id}
                          type="button"
                          onClick={() => {
                            setFormInputs(p => {
                              const next = isSelected
                                ? p.owner_ids.filter(id => id !== o.id)
                                : [...p.owner_ids, o.id];
                              return { ...p, owner_ids: next };
                            });
                          }}
                          className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all border shrink-0 ${isSelected
                            ? "bg-sky-50 border-sky-200 text-sky-600"
                            : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                            }`}
                        >
                          {o.username}
                        </button>
                      );
                    })}
                  </div>
                  {manageFieldErrors.owner_ids && <p className="mt-1 text-[10px] font-bold text-rose-500 leading-none">{manageFieldErrors.owner_ids}</p>}
                </div>

                {/* Installment Summary Preview */}
                <div className="mt-4 p-4 bg-sky-50/50 rounded-2xl border border-sky-100/50 space-y-3">
                  <div className="flex justify-between items-center text-[10px] font-bold text-sky-500/60 uppercase tracking-widest">
                    <span>สรุปรายการผ่อน</span>
                    <div className="h-[1px] flex-1 ml-3 bg-sky-100/30"></div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-1">
                      <p className="text-[10px] text-slate-400 font-bold">ผ่อนต่อเดือน</p>
                      <p className="text-xs sm:text-sm font-black text-sky-600 tabular-nums">
                        ฿{(() => {
                          const t = Number(String(formInputs.total_amount || "0").replace(/,/g, ""));
                          const i = Number(formInputs.interest_percent || 0);
                          const m = Number(formInputs.months || 1);
                          const monthlyInterest = t * (i / 100);
                          const monthlyPrincipal = t / m;
                          return (monthlyPrincipal + monthlyInterest).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        })()}
                      </p>
                    </div>
                    <div className="space-y-1 text-center">
                      <p className="text-[10px] text-slate-400 font-bold">ดอกเบี้ยรวม</p>
                      <p className="text-xs sm:text-sm font-black text-rose-500 tabular-nums">
                        ฿{(() => {
                          const t = Number(String(formInputs.total_amount || "0").replace(/,/g, ""));
                          const i = Number(formInputs.interest_percent || 0);
                          const m = Number(formInputs.months || 1);
                          return (t * (i / 100) * m).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        })()}
                      </p>
                    </div>
                    <div className="space-y-1 text-right">
                      <p className="text-[10px] text-slate-400 font-bold">ผ่อนครบเมื่อ</p>
                      <p className="text-xs sm:text-sm font-black text-slate-700">
                        {(() => {
                          const m = Number(formInputs.months || 1);
                          const start = dayjs(formInputs.start_month).tz('Asia/Bangkok').startOf('month');
                          return start.add(m - 1, 'month').format("MMM YYYY");
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : manageTab === "categories" ? (
              <>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                    ชื่อหมวดหมู่
                  </label>
                  <div className="relative mt-1.5">
                    <input
                      type="text"
                      name="name"
                      value={formInputs.name}
                      onChange={onChangeInput}
                      placeholder="ระบุชื่อหมวดหมู่..."
                      className={`w-full rounded-lg border bg-white px-4 h-8 text-base md:text-sm text-slate-800 outline-none transition-all shadow-sm ${manageFieldErrors.name ? 'border-rose-500 focus:ring-1 focus:ring-rose-500/20' : 'border-gray-300'}`}
                      autoFocus
                    />
                  </div>
                  {manageFieldErrors.name && (
                    <p className="mt-1 text-[10px] font-bold text-rose-500 leading-none">{manageFieldErrors.name}</p>
                  )}
                </div>
                <div>
                  <TailwindDropdown
                    label="ประเภท"
                    options={[
                      { id: "expense", name: "รายจ่าย" },
                      { id: "income", name: "รายรับ" },
                    ]}
                    value={formInputs.type}
                    onChange={onChangeInput}
                    className="type"
                    optionLabel="name"
                    optionValue="id"
                  />
                </div>
              </>
            ) : manageTab === "owners" ? (
              <div>
                <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                  ชื่อเจ้าของรายการ
                </label>
                <div className="relative mt-1.5">
                  <input
                    type="text"
                    name="username"
                    value={formInputs.username}
                    onChange={onChangeInput}
                    placeholder="ระบุชื่อเจ้าของ..."
                    className={`w-full rounded-lg border bg-white px-4 h-8 text-base md:text-sm text-slate-800 outline-none transition-all shadow-sm ${manageFieldErrors.username ? 'border-rose-500 focus:ring-1 focus:ring-rose-500/20' : 'border-gray-300'}`}
                    autoFocus
                  />
                </div>
                {manageFieldErrors.username && (
                  <p className="mt-1 text-[10px] font-bold text-rose-500 leading-none">{manageFieldErrors.username}</p>
                )}
              </div>
            ) : (
              // Budgets Form
              // Budgets Form
              <div className="space-y-5 pb-32">
                <div className="grid grid-cols-2 gap-3 md:gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                      เจ้าของ
                    </label>
                    <div className="relative mt-1.5">
                      <TailwindDropdown
                        options={owners || []}
                        value={formInputs.owner_id}
                        onChange={(e) => setFormInputs(p => ({ ...p, owner_id: e.target.value }))}
                        className="owner"
                        optionLabel="username"
                        optionValue="id"
                        direction="down"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                      เดือน / ปี
                    </label>
                    <div className="relative mt-1.5">
                      <MonthYearPicker
                        label=""
                        value={`${formInputs.year}-${String(formInputs.month).padStart(2, '0')}`}
                        onChange={(e) => {
                          const [y, m] = e.target.value.split("-");
                          setFormInputs(p => ({ ...p, month: Number(m), year: Number(y) }));
                        }}
                        className="w-full"
                        variant="form"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 select-none h-4 flex items-center">
                    งบประมาณ (บาท)
                  </label>
                  <div className="relative mt-1.5">
                    <input
                      type="text"
                      name="amount"
                      inputMode="decimal"
                      autoComplete="off"
                      placeholder="ระบุงบประมาณ..."
                      value={formInputs.amount}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9.,]/g, "");
                        setFormInputs(p => ({ ...p, amount: val }));
                        setManageFieldErrors(prev => {
                          const c = { ...prev };
                          delete c.amount;
                          return c;
                        });
                      }}
                      onBlur={(e) => {
                        const n = parseMoney(e.target.value);
                        if (n > 0) {
                          setFormInputs(p => ({ ...p, amount: n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }));
                        }
                      }}
                      className={`w-full rounded-lg border bg-white px-4 h-8 text-base md:text-sm text-slate-800 outline-none transition-all shadow-sm tabular-nums ${manageFieldErrors.amount ? 'border-rose-500 focus:ring-1 focus:ring-rose-500/20' : 'border-gray-300'}`}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-row gap-2.5 pt-1">
              <button
                type="button"
                onClick={cancelEdit}
                className="flex-1 py-2.5 rounded-xl bg-slate-100 border border-slate-200 text-slate-600 font-bold hover:bg-slate-200 transition-all active:scale-95 text-xs"
              >
                ยกเลิก
              </button>
              <button
                type="submit"
                disabled={manageLoading}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 text-white font-bold hover:from-sky-600 hover:to-blue-700 transition-all shadow-md active:scale-95 flex items-center justify-center disabled:opacity-50 text-xs"
              >
                {manageLoading ? (
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  editingItem ? "บันทึก" : "บันทึก"
                )}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Recurring Process Confirmation Modal */}
      {recurringConfirmOpen && (
        <Modal
          isOpen={recurringConfirmOpen}
          onClose={() => {
            setRecurringConfirmOpen(false);
            setPendingProcessItem(null);
          }}
          hideHeader
          size="sm"
          variant="center"
        >
          <div className="space-y-4">
            <div>
              <p className="text-base font-semibold text-slate-900 pr-8">
                {pendingProcessItem ? 'คุณต้องการประมวลผลรายการนี้หรือไม่?' : 'คุณต้องการประมวลผลรายการทั้งหมดหรือไม่?'}
              </p>
              <p className="mt-1 text-sm text-slate-600">
                {pendingProcessItem
                  ? <span>รายการ <span className="font-bold text-slate-800">"{pendingProcessItem.title}"</span> จะถูกสร้างเป็นธุรกรรมทันที</span>
                  : 'รายการประจำที่ถึงกำหนดทั้งหมดจะถูกสร้างเป็นธุรกรรมทันที'}
              </p>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  setRecurringConfirmOpen(false);
                  setPendingProcessItem(null);
                }}
                className="px-5 py-2 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-900 hover:bg-gray-50 transition-all"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={confirmProcessRecurring}
                className="px-6 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-all shadow-sm shadow-emerald-100"
              >
                {pendingProcessItem ? 'ตกลง' : 'ตกลง'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {manageConfirmOpen && (
        <Modal
          isOpen={manageConfirmOpen}
          onClose={() => setManageConfirmOpen(false)}
          hideHeader
          size="sm"
          variant="center"
        >
          <div className="space-y-4">
            <div>
              <p className="text-base font-semibold text-slate-900 pr-8">
                คุณต้องการลบรายการนี้หรือไม่?
              </p>
              <p className="mt-1 text-sm text-slate-600">
                การลบจะไม่สามารถย้อนกลับได้
              </p>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setManageConfirmOpen(false)}
                className="px-5 py-2 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-900 hover:bg-gray-50 transition-all"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={confirmManageDelete}
                className="px-6 py-2 rounded-xl bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 transition-all shadow-sm shadow-rose-100"
              >
                ลบ
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

const Navbar = ({
  currentPage,
  onNavigate,
  onRequestLogout,
  onPlusClick,
}) => {
  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "payment-methods", label: "Payments", icon: CreditCard },
    { id: "plus", isPlus: true },
    { id: "savings", label: "Savings", icon: PiggyBank },
    { id: "manage-data", label: "Manage", icon: Settings },
  ];

  return (
    <>
      {/* Desktop Top Navbar */}
      <header className="hidden md:block sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <h1 className="text-xl font-bold bg-gradient-to-r from-sky-600 to-blue-600 bg-clip-text text-transparent">
              Expense Tracker
            </h1>
            <nav className="flex items-center gap-1">
              {navItems.filter(item => !item.isPlus).map((item) => (
                <button
                  key={item.id}
                  onClick={() => onNavigate(item.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${currentPage === item.id
                    ? "bg-blue-50 text-blue-700 shadow-sm"
                    : "text-slate-600 hover:bg-gray-100 hover:text-slate-900"
                    }`}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <div className="h-6 w-px bg-gray-200 mx-1" />
            <button
              onClick={onRequestLogout}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-rose-600 hover:bg-rose-50 transition font-medium text-sm"
            >
              <LogOut className="h-4 w-4" />
              {/* Logout */}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Top Header */}
      <header className="md:hidden sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-4 h-14 flex items-center justify-between">
          <h1 className="text-lg font-bold bg-gradient-to-r from-sky-600 to-blue-600 bg-clip-text text-transparent">
            Expense Tracker
          </h1>
          <button
            onClick={onRequestLogout}
            className="p-2 rounded-lg text-rose-600 hover:bg-rose-50 transition"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-100 pb-safe shadow-[0_-1px_10px_rgba(0,0,0,0.05)]">
        <div className="flex items-center justify-around h-16 relative">
          {navItems.map((item) => {
            if (item.isPlus) {
              return (
                <div key="plus-button" className="relative -mt-8">
                  <button
                    onClick={onPlusClick}
                    className="flex items-center justify-center bg-gradient-to-tr from-sky-600 to-blue-600 text-white rounded-full w-14 h-14 shadow-lg shadow-blue-200 border-4 border-white active:scale-95 transition-all duration-200"
                    aria-label="Add Item"
                  >
                    <Plus className="h-7 w-7" />
                  </button>
                </div>
              );
            }
            return (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id)}
                className={`flex flex-col items-center justify-center gap-1 w-full h-full text-center relative transition-colors ${currentPage === item.id ? "text-blue-600" : "text-slate-400"
                  }`}
              >
                <item.icon className={`h-5 w-5 ${currentPage === item.id ? "scale-110" : ""} transition-transform`} />
                <span className="text-[10px] font-medium leading-none">{item.label}</span>
                {currentPage === item.id && (
                  <div className="absolute top-0 w-8 h-1 bg-blue-600 rounded-b-full" />
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

// Dashboard Page Component
const DashboardPage = ({
  user,
  onRequestLogout,
  onNavigate,
  data,
  filterMonth,
  filterYear,
  setFilterMonth,
  setFilterYear,
  ownerFilterId,
  setOwnerFilterId,
}) => {
  const handleCopyToClipboard = (text, message) => {
    if (!navigator.clipboard) {
      showToast("เบราว์เซอร์ของคุณไม่รองรับการคัดลอก", "warning");
      return;
    }
    const textToCopy = String(text || "");
    if (!textToCopy) return;

    navigator.clipboard.writeText(textToCopy).then(() => {
      showToast(message || `คัดลอก "${textToCopy}" แล้ว`, "success");
    }).catch(err => {
      showToast("ไม่สามารถคัดลอกได้", "error");
      console.error('Could not copy text: ', err);
    });
  };
  const { transactions, categories, paymentMethods, owners, budget, installments } = data;
  const [viewMode, setViewMode] = useState("list");
  // --- Local budget state & helpers ---
  const [budgetLocal, setBudgetLocal] = useState(budget);
  useEffect(() => {
    setBudgetLocal(budget);
  }, [budget]);
  const budgetUse = budgetLocal ?? budget;

  const [budgetInput, setBudgetInput] = useState("");
  const [budgetSaving, setBudgetSaving] = useState(false);

  const handlePrevMonth = () => {
    let m = filterMonth - 1;
    let y = filterYear;
    if (m < 1) {
      m = 12;
      y -= 1;
    }
    setFilterYear(y);
    setFilterMonth(m);
  };

  const handleNextMonth = () => {
    let m = filterMonth + 1;
    let y = filterYear;
    if (m > 12) {
      m = 1;
      y += 1;
    }
    setFilterYear(y);
    setFilterMonth(m);
  };

  // ----------------- ฟิลเตอร์ใหม่สำหรับตารางรายการล่าสุด (moved earlier) -----------------
  const [filterPaymentMethod, setFilterPaymentMethod] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterMemo, setFilterMemo] = useState("");
  const [filterInstallPaymentMethod, setFilterInstallPaymentMethod] = useState("");
  const [filterInstallText, setFilterInstallText] = useState("");
  const parseMoney = (s) => {
    if (s === null || s === undefined) return 0;
    const v = String(s).replace(/,/g, "");
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  };

  const formatBudgetTyping = (val) => {
    const raw = String(val || "").replace(/,/g, "");
    // keep only digits and dot
    let out = raw.replace(/[^0-9.]/g, "");
    // keep only the first dot and limit to 2 decimals
    const firstDot = out.indexOf(".");
    if (firstDot !== -1) {
      const intPart = out.slice(0, firstDot).replace(/\./g, "");
      let decPart = out.slice(firstDot + 1).replace(/\./g, "");
      decPart = decPart.slice(0, 2);
      out = intPart + "." + decPart;
    } else {
      // no dot allowed elsewhere
      out = out.replace(/\./g, "");
    }
    return out;
  };

  const saveBudget = async () => {
    try {
      setBudgetSaving(true);
      const amount = parseMoney(budgetInput);
      const ownerIdStr = String(ownerFilterId || "");
      if (!ownerIdStr || ownerIdStr === "all") {
        alert("กรุณาเลือกเจ้าของก่อนตั้งงบ");
        setBudgetSaving(false);
        return;
      }
      if (amount <= 0) {
        alert("กรุณากรอกงบประมาณมากกว่า 0");
        setBudgetSaving(false);
        return;
      }
      await api.post("/budgets", {
        amount,
        month: filterMonth,
        year: filterYear,
        owner_id: Number(ownerIdStr),
      });
      const res = await api.get("/budgets", {
        params: { month: filterMonth, year: filterYear },
      });
      setBudgetLocal(res.data);
      setBudgetInput(
        amount.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      );
    } catch (e) {
      console.error(e);
      alert("บันทึกงบประมาณไม่สำเร็จ");
    } finally {
      setBudgetSaving(false);
    }
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [prefillTransaction, setPrefillTransaction] = useState(null);
  const [showTxFilters, setShowTxFilters] = useState(false);
  const [showInstallFilters, setShowInstallFilters] = useState(false);
  const [showToTop, setShowToTop] = useState(false);
  const [showBudgetForm, setShowBudgetForm] = useState(false);
  useEffect(() => {
    setShowBudgetForm(false);
  }, [ownerFilterId, filterMonth, filterYear]);
  useEffect(() => {
    const handleScroll = () => {
      setShowToTop(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  // --- Confirm Delete Modal State ---
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const scrollLockRef = React.useRef({ scrollY: 0, paddingRight: "" });
  const [overBudgetOpen, setOverBudgetOpen] = useState(false);

  // --- Installments (รายการผ่อน) state ---
  const [installOpen, setInstallOpen] = useState(false);
  const [installItems, setInstallItems] = useState(installments || []);
  const [installLoading, setInstallLoading] = useState(false);
  const [installEditing, setInstallEditing] = useState(null); // the item object
  const [installError, setInstallError] = useState("");
  const [installConfirmOpen, setInstallConfirmOpen] = useState(false);
  const [installPendingDeleteId, setInstallPendingDeleteId] = useState(null);
  const [installForm, setInstallForm] = useState({
    title: "",
    total_amount: "",
    months: "",
    interest_percent: "0.00",
    start_month: dayjs().tz("Asia/Bangkok").format("YYYY-MM"),
    owner_ids: [],
    payment_method_id: "",
  });

  // Helpers for installments
  // Predefined month choices for installments
  const INSTALLMENT_MONTH_OPTIONS = [
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 18, 24, 36, 60,
  ].map((n) => ({ id: n, name: String(n) }));
  const isInstallmentFinished = useCallback((it) => {
    const tz = "Asia/Bangkok";
    const startYYYYMM = it.start_month
      ? /^\d{4}-\d{2}$/.test(String(it.start_month))
        ? String(it.start_month)
        : dayjs(it.start_month).tz(tz).format("YYYY-MM")
      : "";
    const months = Number(it.months || 0);
    if (!startYYYYMM || !months) return false;
    const end = dayjs(`${startYYYYMM}-01`)
      .tz(tz)
      .add(months - 1, "month")
      .endOf("month");
    return dayjs().tz(tz).isAfter(end, "day");
  }, []);
  // --- Installments: amount format helpers ---
  const formatInstallAmountInput = (val) => {
    if (val === "" || val === null || val === undefined) return "";
    let cleaned = String(val).replace(/,/g, ""); // ลบ comma ออกก่อน
    cleaned = cleaned.replace(/[^\d.]/g, ""); // เหลือไว้แค่ตัวเลขและจุด

    const firstDot = cleaned.indexOf(".");
    if (firstDot !== -1) {
      const before = cleaned.slice(0, firstDot + 1);
      const after = cleaned.slice(firstDot + 1).replace(/\./g, ""); // เอาจุดที่เกินออก
      cleaned = before + after;
    }
    return cleaned;
  };

  const parseInstallAmountNumber = (val) => {
    if (val === "" || val === null || val === undefined) return 0;
    const s = String(val).replace(/,/g, "");
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  };

  const normalizeInstallAmountOnBlur = (val) => {
    if (val === "" || val === null || val === undefined) return "";
    const num = parseInstallAmountNumber(val);
    if (!Number.isFinite(num)) return "";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // --- Installments: interest format helpers ---
  const formatInstallInterestInput = (val) => {
    if (val === "" || val == null) return "";
    let raw = String(val).replace(/[^0-9.]/g, "");
    // ถ้าเริ่มด้วยจุด -> เติม 0 นำหน้า
    if (raw.startsWith(".")) raw = "0" + raw;
    const firstDot = raw.indexOf(".");
    if (firstDot !== -1) {
      const intPart = raw.slice(0, firstDot).replace(/\./g, "");
      let decPartRaw = raw.slice(firstDot + 1).replace(/\./g, "");
      // อนุญาตให้ค้างที่จุดท้าย เช่น 1.
      if (decPartRaw.length === 0) return intPart + ".";
      const decPart = decPartRaw.slice(0, 2);
      return `${intPart}.${decPart}`;
    }
    // ยังไม่มีจุด: คืนเฉพาะตัวเลข
    return raw.replace(/\./g, "");
  };

  const normalizeInstallInterestOnBlur = (val) => {
    const s = String(val || "").replace(/[^0-9.]/g, "");
    if (s === "" || s === ".") return "0.00";
    const n = Number(s);
    if (!Number.isFinite(n)) return "0.00";
    return n.toFixed(2);
  };

  const loadInstallments = useCallback(async () => {
    try {
      setInstallLoading(true);
      const res = await api.get("/installments");
      console.log(
        "Installments loaded:",
        Array.isArray(res.data) ? res.data.length : res.data,
      );
      const raw = res.data || [];
      const sorted = raw.slice().sort((a, b) => {
        const aDone = isInstallmentFinished(a);
        const bDone = isInstallmentFinished(b);
        if (aDone === bDone) return 0;
        return aDone ? 1 : -1; // finished at the bottom
      });
      setInstallItems(sorted);
    } catch (e) {
      console.error("loadInstallments failed", e);
      setInstallError("โหลดข้อมูลไม่สำเร็จ");
    } finally {
      setInstallLoading(false);
    }
  }, [isInstallmentFinished]);
  useEffect(() => {
    setInstallItems(installments || []);
  }, [installments]);
  // Preload installments for the dashboard section (only if not provided or to ensure fresh data)
  useEffect(() => {
    if (!installments || installments.length === 0) {
      loadInstallments();
    }
  }, [loadInstallments, installments]);
  const openInstallments = async () => {
    setInstallEditing(null);
    setInstallError("");
    setInstallForm({
      title: "",
      total_amount: "",
      months: "",
      interest_percent: "0.00",
      start_month: dayjs().tz("Asia/Bangkok").format("YYYY-MM"),
      owner_ids: [],
      payment_method_id: "",
    });
    setInstallOpen(true);
    await loadInstallments();
  };
  const startEditInstallment = (it) => {
    setInstallEditing(it);
    setInstallError("");

    // Normalize owner IDs from multiple possible shapes
    const ownerIds =
      Array.isArray(it.owner_ids) && it.owner_ids.length
        ? it.owner_ids.map((x) => Number(x)).filter(Boolean)
        : Array.isArray(it.owners) && it.owners.length
          ? it.owners.map((o) => Number(o.id)).filter(Boolean)
          : it.owner_id
            ? [Number(it.owner_id)]
            : [];

    setInstallForm({
      title: it.title || "",
      total_amount: normalizeInstallAmountOnBlur(it.total_amount),
      months: String(it.months ?? ""),
      interest_percent: Number.isFinite(Number(it.interest_percent))
        ? Number(it.interest_percent).toFixed(2)
        : "0.00",
      // Accept either YYYY-MM or ISO; always format to YYYY-MM for the month input
      start_month: it.start_month
        ? /^\d{4}-\d{2}$/.test(String(it.start_month))
          ? String(it.start_month)
          : dayjs(it.start_month).tz("Asia/Bangkok").format("YYYY-MM")
        : "",
      owner_ids: ownerIds,
      payment_method_id: it.payment_method_id
        ? String(it.payment_method_id)
        : "",
    });
    setInstallOpen(true);
  };
  const cancelEditInstallment = () => {
    setInstallEditing(null);
    setInstallError("");
    setInstallForm({
      title: "",
      total_amount: "",
      months: "",
      interest_percent: "0.00",
      start_month: dayjs().tz("Asia/Bangkok").format("YYYY-MM"),
      owner_ids: [],
      payment_method_id: "",
    });
  };
  const submitInstallment = async () => {
    try {
      setInstallLoading(true);
      const title = String(installForm.title || "").trim();
      const total_amount = Number(
        String(installForm.total_amount || "").replace(/,/g, ""),
      );
      const months = Number(installForm.months || 0);
      const interest_percent = Number(
        normalizeInstallInterestOnBlur(installForm.interest_percent),
      );
      const start_month = installForm.start_month;
      const owner_ids = Array.isArray(installForm.owner_ids)
        ? installForm.owner_ids.map(Number).filter(Boolean)
        : [];
      const payment_method_id = installForm.payment_method_id
        ? Number(installForm.payment_method_id)
        : 0;
      if (!title) {
        setInstallError("กรุณากรอกชื่อรายการผ่อน");
        return;
      }
      if (!total_amount || total_amount <= 0) {
        setInstallError("กรุณากรอกจำนวนเงินทั้งหมดให้ถูกต้อง");
        return;
      }
      if (!months || months <= 0) {
        setInstallError("กรุณากรอกจำนวนเดือนที่ผ่อนให้ถูกต้อง");
        return;
      }
      if (!start_month) {
        setInstallError("กรุณาเลือกเดือนที่เริ่มผ่อน");
        return;
      }
      if (!owner_ids.length) {
        setInstallError("กรุณาเลือกเจ้าของอย่างน้อย 1 คน");
        return;
      }
      if (!payment_method_id) {
        setInstallError("กรุณาเลือกวิธีการชำระเงิน");
        return;
      }

      const payload = {
        title,
        total_amount,
        months,
        interest_percent,
        start_month,
        owner_ids,
        payment_method_id,
      };
      if (installEditing) {
        await api.put(`/installments/${installEditing.id}`, payload);
      } else {
        await api.post("/installments", payload);
      }
      await loadInstallments();
      cancelEditInstallment();
      setInstallOpen(false);
    } catch (e) {
      console.error("submitInstallment failed", e);
      setInstallError("บันทึกไม่สำเร็จ กรุณาลองใหม่");
    } finally {
      setInstallLoading(false);
    }
  };
  const requestInstallDelete = (id) => {
    setInstallPendingDeleteId(id);
    setInstallConfirmOpen(true);
  };
  const confirmInstallDelete = async () => {
    try {
      if (!installPendingDeleteId) return;
      setInstallLoading(true);
      await api.delete(`/installments/${installPendingDeleteId}`);
      await loadInstallments();
    } catch (e) {
      console.error("delete installment failed", e);
      alert("ลบไม่สำเร็จ");
    } finally {
      setInstallPendingDeleteId(null);
      setInstallConfirmOpen(false);
      setInstallLoading(false);
    }
  };
  const cancelInstallDelete = () => {
    setInstallPendingDeleteId(null);
    setInstallConfirmOpen(false);
  };

  // Lock body scroll (no background movement) when any modal is open
  useEffect(() => {
    const anyPopupOpen =
      isModalOpen ||
      confirmOpen ||
      overBudgetOpen ||
      installOpen ||
      installConfirmOpen;
    const body = document.body;
    const docEl = document.documentElement;

    if (anyPopupOpen) {
      // Save current scroll position
      const scrollY = window.scrollY || window.pageYOffset || 0;
      scrollLockRef.current.scrollY = scrollY;
      // Compensate for scrollbar to avoid content shift
      const scrollBarWidth = window.innerWidth - docEl.clientWidth;
      scrollLockRef.current.paddingRight = body.style.paddingRight;

      body.style.overflow = "hidden";
      if (scrollBarWidth > 0) body.style.paddingRight = `${scrollBarWidth}px`;
      // Fix the body to prevent iOS/Android background scroll
      body.style.position = "fixed";
      body.style.top = `-${scrollY}px`;
      body.style.width = "100%";
    } else {
      // Restore styles and scroll position
      const y = scrollLockRef.current.scrollY || 0;
      body.style.overflow = "";
      body.style.paddingRight = scrollLockRef.current.paddingRight || "";
      body.style.position = "";
      body.style.top = "";
      body.style.width = "";
      window.scrollTo(0, y);
    }

    return () => {
      // Cleanup on unmount
      body.style.overflow = "";
      body.style.paddingRight = "";
      body.style.position = "";
      body.style.top = "";
      body.style.width = "";
    };
  }, [
    isModalOpen,
    confirmOpen,
    overBudgetOpen,
    installOpen,
    installConfirmOpen,
  ]);

  const [allTransactions, setAllTransactions] = useState(transactions);
  // Sync allTransactions when transactions prop changes
  useEffect(() => {
    setAllTransactions(transactions);
  }, [transactions]);

  // --- Prefill budget input with existing value for selected owner/month/year ---
  const getSelectedBudgetAmount = useCallback(() => {
    const arr = Array.isArray(budgetUse)
      ? budgetUse
      : budgetUse
        ? [budgetUse]
        : [];
    const match = arr.find(
      (b) =>
        String(b.user_id) === String(ownerFilterId) &&
        (b.month ? String(b.month) === String(filterMonth) : true) &&
        (b.year ? String(b.year) === String(filterYear) : true),
    );
    return match ? Number(match.amount || 0) : 0;
  }, [budgetUse, ownerFilterId, filterMonth, filterYear]);

  useEffect(() => {
    const amt = getSelectedBudgetAmount();
    if (amt > 0) {
      setBudgetInput(
        amt.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      );
    } else {
      setBudgetInput("");
    }
  }, [getSelectedBudgetAmount]);

  // Summary filters
  const [showPaySummary, setShowPaySummary] = useState(false);
  const [showCategorySummary, setShowCategorySummary] = useState(false);
  const [showInstallSection, setShowInstallSection] = useState(false);
  const [showInstallSummary, setShowInstallSummary] = useState(true);
  const [showCompletedInstallments, setShowCompletedInstallments] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showAllPayMethods, setShowAllPayMethods] = useState(false);

  // Pagination for "รายการล่าสุด"

  // ===== สรุปผ่อนชำระตามวิธีชำระ (ต่อเดือน) – Compact Cards =====
  const [visibleCount, setVisibleCount] = useState(10);
  const [installVisibleCount, setInstallVisibleCount] = useState(10);


  // Async save to API
  const handleSaveTransaction = async (transaction) => {
    try {
      const ownerIds = Array.isArray(transaction.owner_ids)
        ? transaction.owner_ids.map(Number).filter(Boolean)
        : transaction.owner_id
          ? [Number(transaction.owner_id)]
          : [];
      const basePayload = {
        transaction_date: transaction.transaction_date,
        amount: parseFloat(transaction.amount),
        category_id: Number(transaction.category_id),
        payment_method_id: Number(transaction.payment_method_id),
        type: transaction.type,
        status: transaction.status,
        notes: transaction.notes ?? "",
      };
      if (editingTransaction) {
        let selectedOwnerId;
        if (
          Array.isArray(transaction.owner_ids) &&
          transaction.owner_ids.length &&
          Number(transaction.owner_ids[0])
        ) {
          selectedOwnerId = Number(transaction.owner_ids[0]);
        } else if (transaction.owner_id) {
          selectedOwnerId = Number(transaction.owner_id);
        } else {
          selectedOwnerId = Number(editingTransaction.owner_id);
        }
        const payload = { ...basePayload, owner_id: selectedOwnerId };
        await api.put(`/transactions/${transaction.id}`, payload);
        setAllTransactions(
          allTransactions.map((t) =>
            t.id === transaction.id ? { ...payload, id: transaction.id } : t,
          ),
        );
      } else {
        const idsToCreate = ownerIds.length ? ownerIds : [];
        if (!idsToCreate.length) throw new Error("NO_OWNER_SELECTED");
        const createdRows = [];
        for (const oid of idsToCreate) {
          const res = await api.post("/transactions", {
            ...basePayload,
            owner_id: Number(oid),
          });
          createdRows.push({
            ...basePayload,
            owner_id: Number(oid),
            id: res.data.id,
          });
        }
        setAllTransactions([...createdRows, ...allTransactions]);
      }
    } catch (err) {
      alert("เกิดข้อผิดพลาดในการบันทึกรายการ");
      console.error(err);
    }
    setIsModalOpen(false);
    setEditingTransaction(null);
  };

  const toggleTransactionStatus = async (tx) => {
    if (!tx || tx.id === undefined) return;
    const newStatus = tx.status === 'paid' ? 'unpaid' : 'paid';

    try {
      // Optimistic UI update
      setAllTransactions(prev => prev.map(t => t.id === tx.id ? { ...t, status: newStatus } : t));

      await api.put(`/transactions/${tx.id}`, { status: newStatus });

      showToast(`อัปเดตสถานะเป็น "${newStatus === 'paid' ? 'จ่ายแล้ว' : 'ยังไม่จ่าย'}" เรียบร้อย`, 'success');
    } catch (error) {
      // Revert on error
      setAllTransactions(prev => prev.map(t => t.id === tx.id ? { ...t, status: tx.status } : t));
      console.error("Failed to toggle transaction status", error);
      showToast('อัปเดตสถานะไม่สำเร็จ', 'error');
    }
  };

  // --- Delete with confirm modal ---
  const handleDeleteTransaction = (id) => {
    setPendingDeleteId(id);
    setConfirmOpen(true);
  };

  const deleteTransaction = async (id) => {
    try {
      await api.delete(`/transactions/${id}`);
      setAllTransactions(allTransactions.filter((t) => t.id !== id));
      showToast("ลบรายการเรียบร้อยแล้ว", "success");
    } catch (error) {
      const msg =
        error?.response?.data?.message || "ลบรายการไม่สำเร็จ กรุณาลองใหม่";
      showToast(msg, "error");
      console.error(error);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDeleteId) {
      setConfirmOpen(false);
      return;
    }
    await deleteTransaction(pendingDeleteId);
    setPendingDeleteId(null);
    setConfirmOpen(false);
  };

  const cancelDelete = () => {
    setPendingDeleteId(null);
    setConfirmOpen(false);
  };

  // Filtered transactions by ownerFilterId

  // Keep table owner filter aligned with the primary owner selector



  const filteredTransactions = useMemo(() => {
    if (!allTransactions) return [];
    return allTransactions.filter((tx) => {
      // Owner Filter (Unified)
      if (ownerFilterId !== "" && String(tx.owner_id) !== String(ownerFilterId)) return false;

      // Other Filters
      if (filterPaymentMethod && String(tx.payment_method_id) !== String(filterPaymentMethod)) return false;
      if (filterCategory && String(tx.category_id) !== String(filterCategory)) return false;
      if (filterStatus && tx.status !== filterStatus) return false;
      if (filterMemo && !(tx.notes ?? "").toLowerCase().includes(filterMemo.toLowerCase())) return false;

      return true;
    });
  }, [
    allTransactions,
    ownerFilterId,
    filterPaymentMethod,
    filterCategory,
    filterStatus,
    filterMemo,
  ]);

  // Reset visible items only when the filters change, not when the data itself changes.
  // This preserves the "Load More" state after adding/editing/deleting transactions.
  useEffect(() => {
    setVisibleCount(10);
  }, [
    filterPaymentMethod,
    filterCategory,
    filterStatus,
    filterMemo,
  ]);

  // Filtered transactions by ownerFilterId and filter values

  // รายได้/รายจ่าย/คงเหลือ
  const { monthlyIncome, monthlyExpense, balance } = useMemo(() => {
    const income = filteredTransactions
      .filter((t) => t.type === "income")
      .reduce((sum, t) => sum + parseFloat(t.amount), 0);
    const expense = filteredTransactions
      .filter((t) => t.type === "expense")
      .reduce((sum, t) => sum + parseFloat(t.amount), 0);
    return {
      monthlyIncome: income,
      monthlyExpense: expense,
      balance: income - expense,
    };
  }, [filteredTransactions]);

  // --- Over-budget detection for the selected owner ---
  const overBudgetInfo = React.useMemo(() => {
    const budgets = Array.isArray(budgetUse)
      ? budgetUse
      : budgetUse
        ? [budgetUse]
        : [];
    const isAllOwners = ownerFilterId === "" || ownerFilterId === "all";
    if (!budgets.length || isAllOwners) {
      return {
        show: false,
        exceed: 0,
        username: "",
        totalExpense: 0,
        limit: 0,
      };
    }
    const selectedBudget = budgets.find(
      (b) => String(b.user_id) === String(ownerFilterId),
    );
    if (!selectedBudget)
      return {
        show: false,
        exceed: 0,
        username: "",
        totalExpense: 0,
        limit: 0,
      };

    const expenses = filteredTransactions.filter(
      (t) =>
        t.type === "expense" && String(t.owner_id) === String(ownerFilterId),
    );
    const totalExpense = expenses.reduce(
      (sum, t) => sum + Number(t.amount || 0),
      0,
    );
    const exceed = totalExpense - Number(selectedBudget.amount || 0);
    const username =
      owners.find((o) => String(o.id) === String(ownerFilterId))?.username ||
      "";
    return {
      show: exceed > 0,
      exceed,
      username,
      totalExpense,
      limit: Number(selectedBudget.amount || 0),
    };
  }, [budgetUse, filteredTransactions, ownerFilterId, owners]);

  useEffect(() => {
    const today = new Date().getDate();
    const key = `overBudgetDismissed:${ownerFilterId}:${filterYear}-${filterMonth}-${today}`;
    if (overBudgetInfo.show && !sessionStorage.getItem(key)) {
      setOverBudgetOpen(true);
    }
  }, [overBudgetInfo.show, ownerFilterId, filterYear, filterMonth]);

  const dismissOverBudget = (persist = false) => {
    if (persist) {
      const today = new Date().getDate();
      const key = `overBudgetDismissed:${ownerFilterId}:${filterYear}-${filterMonth}-${today}`;
      sessionStorage.setItem(key, "1");
    }
    setOverBudgetOpen(false);
  };

  // ---- Installments rows (filtered & formatted for display) ----
  const installmentRows = useMemo(() => {
    const list = Array.isArray(installItems) ? installItems : [];
    const ownersById = new Map(
      (Array.isArray(owners) ? owners : []).map((o) => [Number(o.id), o]),
    );
    const referenceMonth = (() => {
      const monthNum = Number(filterMonth);
      const yearNum = Number(filterYear);
      if (
        Number.isFinite(monthNum) &&
        monthNum >= 1 &&
        monthNum <= 12 &&
        Number.isFinite(yearNum)
      ) {
        return dayjs(`${yearNum}-${String(monthNum).padStart(2, "0")}-01`)
          .tz("Asia/Bangkok")
          .startOf("month");
      }
      return dayjs().tz("Asia/Bangkok").startOf("month");
    })();
    const lastCompletedMonth = referenceMonth.subtract(1, "month"); // ไม่นับเดือนปัจจุบัน

    const mapRow = (it) => {
      const startM = it.start_month
        ? dayjs(it.start_month).tz("Asia/Bangkok").format("MMM YYYY")
        : "-";
      const endIso = calcEndMonth(
        // accept either 'YYYY-MM' or ISO string from API
        it.start_month && /\d{4}-\d{2}$/.test(String(it.start_month))
          ? String(it.start_month)
          : dayjs(it.start_month).tz("Asia/Bangkok").format("YYYY-MM"),
        it.months,
      );
      const endM = endIso
        ? dayjs(`${endIso}-01`).tz("Asia/Bangkok").format("MMM YYYY")
        : "-";
      const monthly = calcMonthly(
        it.total_amount,
        it.months,
        it.interest_percent,
      );
      // Calculate elapsed months (inclusive) from start_month to today/end
      const startYYYYMM =
        it.start_month && /\d{4}-\d{2}$/.test(String(it.start_month))
          ? String(it.start_month)
          : dayjs(it.start_month).tz("Asia/Bangkok").format("YYYY-MM");
      const startStart = startYYYYMM
        ? dayjs(`${startYYYYMM}-01`).tz("Asia/Bangkok").startOf("month")
        : null;
      const endStart = endIso
        ? dayjs(`${endIso}-01`).tz("Asia/Bangkok").startOf("month")
        : null;

      // ถ้าผ่อนครบแล้ว ให้นับถึงเดือนสิ้นสุด (หากสิ้นสุด = เดือนนี้ ให้นับถึงเดือนก่อนหน้า)
      let clampTo = lastCompletedMonth;
      if (endStart && lastCompletedMonth.isAfter(endStart)) {
        clampTo = endStart;
      }

      let elapsed = 0;
      // นับเฉพาะเมื่อจุด clampTo ไม่อยู่ก่อนเดือนเริ่มต้น
      if (startStart && !clampTo.isBefore(startStart)) {
        elapsed = clampTo.diff(startStart, "month") + 1; // รวมเดือนเริ่มต้น
      }
      const totalMonths = Number(it.months || 0);
      elapsed = Math.max(0, Math.min(elapsed, totalMonths));
      const progressText = `${elapsed}/${totalMonths}`;
      // คำนวณยอดคงเหลือ = (ยอดรวมพร้อมดอกเบี้ย) - (จ่ายไปแล้ว)
      const totalWithInterest =
        Number(
          String(it.total_amount ?? 0)
            .toString()
            .replace(/,/g, ""),
        ) *
        (1 + Number(it.interest_percent ?? 0) / 100);
      const paidSoFar = Number(monthly || 0) * Number(elapsed || 0);
      const remaining = Math.max(0, totalWithInterest - paidSoFar);
      const remainingText = Number(remaining).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      let ownersName = "-";
      // Case 1: API returns owner_ids: number[]
      if (Array.isArray(it.owner_ids) && it.owner_ids.length) {
        ownersName = it.owner_ids
          .map((id) => {
            const o = ownersById.get(Number(id));
            return o?.username || o?.name || null;
          })
          .filter(Boolean)
          .join(", ");
        // Case 2: API returns owners: {id, username|name}[]
      } else if (Array.isArray(it.owners) && it.owners.length) {
        ownersName = it.owners
          .map((o) => o?.username || o?.name)
          .filter(Boolean)
          .join(", ");
        // Case 3: Single owner_id
      } else if (it.owner_id != null) {
        const o = ownersById.get(Number(it.owner_id));
        ownersName = o?.username || o?.name || "-";
      }
      const payName =
        paymentMethods.find(
          (p) => Number(p.id) === Number(it.payment_method_id),
        )?.name || "-";
      return {
        ...it,
        startM,
        endM,
        monthly,
        ownersName,
        payName,
        progressText,
        elapsedMonths: elapsed,
        remaining,
        remainingText,
      };
    };

    const filtered = list.filter((it) => {
      // Use the installments' local owner filter if set; otherwise fall back to the global ownerFilterId
      const effOwner = String(ownerFilterId || "");
      if (!effOwner || effOwner === "all") return true;

      // Check multiple possible shapes
      const oid = String(effOwner);
      if (Array.isArray(it.owner_ids) && it.owner_ids.length) {
        return it.owner_ids.map((x) => String(x)).includes(oid);
      }
      if (Array.isArray(it.owners) && it.owners.length) {
        return it.owners.map((o) => String(o.id)).includes(oid);
      }
      return String(it.owner_id) === oid;
    });

    const rows = filtered.map(mapRow);
    // Fallback: if filtering by owner yields empty, but there is data, show all installments
    if (rows.length === 0 && list.length > 0) {
      return list.map(mapRow);
    }
    return rows;
  }, [
    installItems,
    ownerFilterId,
    ownerFilterId,
    owners,
    paymentMethods,
    filterMonth,
    filterYear,
  ]);

  const filteredInstallmentRows = useMemo(() => {
    const list = Array.isArray(installmentRows) ? installmentRows : [];
    if (!filterInstallPaymentMethod && !ownerFilterId && !filterInstallText) return list;

    return list.filter((r) => {
      // Payment Method Filter
      if (filterInstallPaymentMethod && String(r.payment_method_id) !== String(filterInstallPaymentMethod)) return false;

      // Owner Filter (Unified)
      if (ownerFilterId) {
        const oid = String(ownerFilterId);
        let hasOwner = false;
        if (Array.isArray(r.owner_ids) && r.owner_ids.length) {
          hasOwner = r.owner_ids.some((x) => String(x) === oid);
        } else if (Array.isArray(r.owners) && r.owners.length) {
          hasOwner = r.owners.some((o) => String(o.id) === oid);
        } else {
          hasOwner = String(r.owner_id) === oid;
        }
        if (!hasOwner) return false;
      }

      // Text Filter
      if (filterInstallText) {
        if (!String(r.title || "").toLowerCase().includes(String(filterInstallText).toLowerCase())) return false;
      }

      return true;
    });
  }, [
    installmentRows,
    filterInstallPaymentMethod,
    ownerFilterId,
    filterInstallText,
  ]);

  const selectedDate = useMemo(() => {
    const monthNum = Number(filterMonth);
    const yearNum = Number(filterYear);
    if (Number.isFinite(monthNum) && monthNum >= 1 && monthNum <= 12 && Number.isFinite(yearNum)) {
      return dayjs(`${yearNum}-${String(monthNum).padStart(2, "0")}-01`).tz("Asia/Bangkok").startOf("month");
    }
    return dayjs().tz("Asia/Bangkok").startOf("month");
  }, [filterMonth, filterYear]);

  const installmentDashboardSummary = useMemo(() => {
    const rows = Array.isArray(filteredInstallmentRows)
      ? filteredInstallmentRows
      : [];

    const activeRows = rows.filter((row) => {
      if (!row.start_month || !row.months) return false;
      const startDate = dayjs(row.start_month).tz("Asia/Bangkok").startOf("month");
      const endDate = startDate.add(Number(row.months) - 1, "month").endOf("month");
      // รายการจะ active ถ้าเดือนที่เลือก อยู่ระหว่างเริ่มและจบ
      return (selectedDate.isSame(startDate, "month") || selectedDate.isAfter(startDate, "month")) &&
        (selectedDate.isSame(endDate, "month") || selectedDate.isBefore(endDate, "month"));
    });

    const resolveOwnerCount = (row) => {
      if (Array.isArray(row.owner_ids) && row.owner_ids.length)
        return row.owner_ids.length;
      if (Array.isArray(row.owners) && row.owners.length)
        return row.owners.length;
      if (row.owner_id != null) return 1;
      return 0;
    };

    const activeCount = activeRows.length;
    const monthlyCommitment = activeRows.reduce((sum, row) => {
      const monthly = Number(row.monthly || 0);
      if (
        ownerFilterId &&
        ownerFilterId !== "" &&
        ownerFilterId !== "all"
      ) {
        const ownerCount = resolveOwnerCount(row);
        return sum + (ownerCount > 0 ? monthly / ownerCount : monthly);
      }
      return sum + monthly;
    }, 0);
    const totalRemaining = activeRows.reduce(
      (sum, row) => sum + Number(row.remaining || 0),
      0,
    );

    return { activeCount, monthlyCommitment, totalRemaining };
  }, [filteredInstallmentRows, ownerFilterId, selectedDate]);

  useEffect(() => {
    setInstallVisibleCount(10);
  }, [filterInstallPaymentMethod, ownerFilterId, filterInstallText]);

  useEffect(() => {
    setInstallVisibleCount(10);
  }, [installmentRows]);

  // Category summary (amount, percent) based on current filters
  const categorySummary = useMemo(() => {
    const expenses = filteredTransactions.filter((t) => t.type === "expense");
    const totalAll = expenses.reduce(
      (sum, t) => sum + Number(t.amount || 0),
      0,
    );
    const byCat = {};
    for (const t of expenses) {
      const id = t.category_id;
      const name = categories.find((c) => c.id === id)?.name || "ไม่ระบุ";
      if (!byCat[id]) byCat[id] = { id, name, total: 0, count: 0 };
      byCat[id].total += Number(t.amount || 0);
      byCat[id].count += 1;
    }
    const items = Object.values(byCat).sort((a, b) => b.total - a.total);
    return { totalAll, items };
  }, [filteredTransactions, categories]);

  // Payment method summary (amount, count, percent) based on current filters + summaryStatus
  const paySummary = useMemo(() => {
    const expenses = filteredTransactions
      .filter((t) => t.type === "expense")
      .filter((t) => !filterStatus || t.status === filterStatus);
    const totalAll = expenses.reduce(
      (sum, t) => sum + Number(t.amount || 0),
      0,
    );
    const byId = {};
    for (const t of expenses) {
      const id = t.payment_method_id;
      if (!byId[id]) {
        byId[id] = {
          id,
          name: paymentMethods.find((p) => p.id === id)?.name || "-",
          total: 0,
          count: 0,
          totalPaid: 0,
          totalUnpaid: 0,
        };
      }
      const amount = Number(t.amount || 0);
      byId[id].total += amount;
      byId[id].count += 1;
      if (t.status === 'paid') byId[id].totalPaid += amount;
      else byId[id].totalUnpaid += amount;
    }
    const items = Object.values(byId).sort((a, b) => b.total - a.total);
    return { totalAll, items };
  }, [filteredTransactions, filterStatus, paymentMethods]);

  const handleExport = (format) => {
    const dataToExport = filteredTransactions.map((t) => ({
      วันที่: dayjs(t.transaction_date).tz("Asia/Bangkok").format("DD/MM/YYYY"),
      ประเภท: t.type === "income" ? "รายรับ" : "รายจ่าย",
      หมวดหมู่: categories.find((c) => c.id === t.category_id)?.name || "",
      จำนวนเงิน: parseFloat(t.amount),
      วิธีชำระ:
        paymentMethods.find((p) => p.id === t.payment_method_id)?.name || "",
      เจ้าของ: owners.find((o) => o.id === t.owner_id)?.username || "",
      สถานะ: t.status === "paid" ? "จ่ายแล้ว" : "ยังไม่จ่าย",
      บันทึก: t.notes,
    }));

    if (format === "excel") {
      // Build worksheet from data
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);

      // Auto-size columns by measuring header + cell string lengths
      const headers = Object.keys(
        dataToExport[0] || {
          วันที่: "",
          ประเภท: "",
          หมวดหมู่: "",
          จำนวนเงิน: "",
          วิธีชำระ: "",
          เจ้าของ: "",
          สถานะ: "",
          บันทึก: "",
        },
      );

      const colWidths = headers.map((h, i) => {
        const headerLen = String(h).length;
        const maxCellLen = dataToExport.reduce((max, row) => {
          const v = Object.values(row)[i];
          // Format numbers to string for a more readable width estimate
          const s =
            typeof v === "number"
              ? v.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
              : v == null
                ? ""
                : String(v);
          return Math.max(max, s.length);
        }, 0);
        // Add small padding so it doesn't feel cramped
        const wch = Math.max(headerLen, maxCellLen) + 2;
        return { wch };
      });
      worksheet["!cols"] = colWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");

      // Filename with Thai timezone timestamp (avoid colon in filenames)
      const stamp = dayjs().tz("Asia/Bangkok").format("YYYY-MM-DD_HH.mm");
      const filename = `transactions_${stamp}.xlsx`;
      XLSX.writeFile(workbook, filename);
    } else if (format === "pdf") {
      const doc = new jsPDF();
      doc.text("รายงานรายรับ-รายจ่าย", 14, 16);
      doc.autoTable({
        head: [
          [
            "วันที่",
            "ประเภท",
            "หมวดหมู่",
            "จำนวนเงิน",
            "วิธีชำระ",
            "เจ้าของ",
            "สถานะ",
          ],
        ],
        body: dataToExport.map(Object.values),
        startY: 20,
        styles: { font: "helvetica" }, // ควรเปลี่ยนเป็นฟอนต์ไทย
        headStyles: { fillColor: [22, 160, 133] },
      });
      doc.save("transactions.pdf");
    }
  };

  useEffect(() => {
    const handlePlus = () => setIsModalOpen(true);
    window.addEventListener("app-plus-clicked", handlePlus);
    return () => window.removeEventListener("app-plus-clicked", handlePlus);
  }, []);

  // --- เพิ่ม dropdown เลือกเดือนและปีด้านบน OwnerDropdown ---
  return (
    <div className="min-h-screen bg-gray-50 text-slate-800">
      <div className="px-4 pt-2 pb-24 sm:px-6 sm:py-3 lg:px-8 lg:py-4">
        <div className="sticky top-[56px] md:top-[64px] z-40 bg-white/90 backdrop-blur-md border-b border-slate-200/60 py-2.5 px-0.5">
          <div className="flex items-center gap-3 px-1">
            {/* Unified Segmented Month Navigator - Super Tiny */}
            <div className="flex items-center bg-zinc-100/50 p-0.5 rounded-2xl border border-zinc-200/30 flex-shrink-0">
              <button
                type="button"
                onClick={handlePrevMonth}
                className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all"
                aria-label="Previous Month"
              >
                <ChevronLeft size={14} />
              </button>

              <div className="w-14">
                <MonthYearPicker
                  value={`${filterYear}-${String(filterMonth).padStart(2, "0")}`}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value && /^\d{4}-\d{2}$/.test(value)) {
                      const [year, month] = value.split("-").map(Number);
                      setFilterYear(year);
                      setFilterMonth(month);
                    }
                  }}
                  className="month-year-picker-nav h-7"
                />
              </div>

              <button
                type="button"
                onClick={handleNextMonth}
                className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all"
                aria-label="Next Month"
              >
                <ChevronRight size={14} />
              </button>
            </div>

            {/* Owner Selection Pills - Only this part scrolls horizontally */}
            <div className="flex items-center gap-1.5 flex-nowrap overflow-x-auto no-scrollbar flex-1 py-0.5">
              {[{ id: "", username: "All" }, ...owners].map((owner) => {
                const isSelected = String(ownerFilterId) === String(owner.id);
                return (
                  <button
                    key={owner.id || 'all'}
                    type="button"
                    onClick={() => {
                      setOwnerFilterId(owner.id);
                    }}
                    className={`
                      flex items-center gap-1.5 px-4 h-7 rounded-full text-[10px] font-bold transition-all whitespace-nowrap border
                      ${isSelected
                        ? "bg-blue-50 text-blue-600 border-blue-200 shadow-sm"
                        : "bg-white text-slate-500 border-slate-100 hover:border-slate-200 hover:text-slate-700"
                      }
                    `}
                  >
                    {owner.username}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {installOpen && (
          <Modal
            isOpen={installOpen}
            onClose={() => setInstallOpen(false)}
            title="จัดการรายการผ่อน"
          >
            <div className="space-y-6">
              {installError && (
                <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 flex items-start justify-between gap-2">
                  <span>{installError}</span>
                  <button
                    type="button"
                    onClick={() => setInstallError("")}
                    className="text-red-700/80 hover:text-red-900 text-xs underline"
                    aria-label="ปิดข้อความผิดพลาด"
                  >
                    ปิด
                  </button>
                </div>
              )}

              {/* ฟอร์มจัดการรายการผ่อน: สไตล์เดียวกับป๊อปอัปเพิ่มรายการใหม่ */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  submitInstallment();
                }}
                noValidate
                autoComplete="off"
                className="space-y-6 text-slate-800 w-full"
              >
                {/* Title & Total */}
                <div className="flex flex-col space-y-2 md:flex-row md:space-y-0 md:space-x-4">
                  <div className="w-full md:w-1/2">
                    <label className="block text-sm font-medium text-slate-400">
                      ชื่อรายการ
                    </label>
                    <input
                      type="text"
                      value={installForm.title}
                      onChange={(e) =>
                        setInstallForm({ ...installForm, title: e.target.value })
                      }
                      className="w-full rounded-lg border border-gray-300 bg-white text-slate-800 text-base md:text-sm px-3 py-1 h-8 outline-none"
                      inputMode="text"
                      enterKeyHint="done"
                    />
                  </div>
                  <div className="w-full md:w-1/2">
                    <label className="block text-sm font-medium text-slate-400">
                      ยอดรวม
                    </label>
                    <input
                      type="text"
                      value={installForm.total_amount}
                      onChange={(e) =>
                        setInstallForm({
                          ...installForm,
                          total_amount: formatInstallAmountInput(e.target.value),
                        })
                      }
                      onBlur={(e) =>
                        setInstallForm({
                          ...installForm,
                          total_amount: normalizeInstallAmountOnBlur(e.target.value),
                        })
                      }
                      className="w-full rounded-lg border border-gray-300 bg-white text-slate-800 text-base md:text-sm px-3 py-1 h-8 outline-none"
                      inputMode="decimal"
                      pattern="[0-9.,]*"
                      enterKeyHint="done"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Months & Interest */}
                <div className="flex flex-col space-y-2 md:flex-row md:space-y-0 md:space-x-4">
                  <div className="w-full md:w-1/2">
                    <TailwindDropdown
                      label="จำนวนงวด (เดือน)"
                      options={INSTALLMENT_MONTH_OPTIONS}
                      value={installForm.months}
                      onChange={(e) =>
                        setInstallForm((p) => ({ ...p, months: e.target.value }))
                      }
                      placeholder="เลือกจำนวนงวด"
                      className="months"
                      required
                    />
                  </div>
                  <div className="w-full md:w-1/2">
                    <label className="block text-sm font-medium text-slate-400">
                      ดอกเบี้ย
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formatInstallInterestInput(
                          installForm.interest_percent,
                        )}
                        onChange={(e) => {
                          const next = formatInstallInterestInput(e.target.value);
                          setInstallForm((prev) => ({
                            ...prev,
                            interest_percent: next,
                          }));
                        }}
                        onBlur={(e) => {
                          const fixed = normalizeInstallInterestOnBlur(
                            e.target.value,
                          );
                          setInstallForm((prev) => ({
                            ...prev,
                            interest_percent: fixed,
                          }));
                        }}
                        inputMode="decimal"
                        pattern="[0-9.]*"
                        enterKeyHint="done"
                        className="w-full rounded-lg border border-gray-300 bg-white text-slate-800 text-base md:text-sm px-3 py-1 h-8 outline-none"
                        placeholder="0.00"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="none"
                        spellCheck={false}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm select-none">
                        %
                      </span>
                    </div>
                  </div>
                </div>

                {/* Start month & Payment method */}
                <div className="flex flex-col space-y-2 md:flex-row md:space-y-0 md:space-x-4">
                  <div className="w-full md:w-1/2">
                    <MonthYearPicker
                      label="เริ่มผ่อน (เดือน/ปี)"
                      value={installForm.start_month}
                      onChange={(e) =>
                        setInstallForm((prev) => ({
                          ...prev,
                          start_month: e.target.value,
                        }))
                      }
                      className="w-full"
                      variant="form"
                    />
                  </div>
                  <div className="w-full md:w-1/2">
                    <TailwindDropdown
                      label="วิธีชำระ"
                      options={paymentMethods}
                      value={installForm.payment_method_id}
                      onChange={(e) =>
                        setInstallForm((p) => ({
                          ...p,
                          payment_method_id: e.target.value,
                        }))
                      }
                      placeholder="เลือกวิธีชำระ"
                      className="payment_method_id"
                      required
                      disabled={!paymentMethods || paymentMethods.length === 0}
                      optionLabel="name"
                      optionValue="id"
                    />
                  </div>
                </div>
                {/* Preview: per-month and end month */}
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-slate-700">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div>
                      <span className="text-slate-500">ต่องวดโดยประมาณ:</span>{" "}
                      <strong>
                        {(() => {
                          const monthly = calcMonthly(
                            parseInstallAmountNumber(installForm.total_amount),
                            Number(installForm.months || 0),
                            Number(installForm.interest_percent || 0),
                          );
                          return Number(monthly || 0).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          });
                        })()}{" "}
                        บาท
                      </strong>
                    </div>
                    <div>
                      <span className="text-slate-500">สิ้นสุดเดือน:</span>{" "}
                      <strong>
                        {installForm.start_month && installForm.months
                          ? dayjs(
                            `${calcEndMonth(installForm.start_month, installForm.months)}-01`,
                          )
                            .tz("Asia/Bangkok")
                            .format("MMM YYYY")
                          : "-"}
                      </strong>
                    </div>
                  </div>
                </div>

                {/* Owners multi-select */}
                <div>
                  <label className="block text-sm font-medium text-slate-400">
                    เจ้าของรายการ
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
                    {(owners || [])
                      .filter((o) => o.username !== "admin")
                      .map((o) => {
                        const checked =
                          Array.isArray(installForm.owner_ids) &&
                          installForm.owner_ids
                            .map(Number)
                            .includes(Number(o.id));
                        return (
                          <label
                            key={o.id}
                            className="flex items-center gap-2 text-sm text-slate-700"
                          >
                            <input
                              type="checkbox"
                              className="rounded border-gray-300"
                              checked={checked}
                              onChange={() => {
                                setInstallForm((prev) => {
                                  const set = new Set(
                                    (prev.owner_ids || []).map(Number),
                                  );
                                  if (set.has(Number(o.id)))
                                    set.delete(Number(o.id));
                                  else set.add(Number(o.id));
                                  return { ...prev, owner_ids: Array.from(set) };
                                });
                              }}
                            />
                            <span>{o.username}</span>
                          </label>
                        );
                      })}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-row gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      cancelEditInstallment();
                      setInstallOpen(false);
                    }}
                    className="flex-1 px-3 py-2 rounded-lg bg-white border border-gray-300 text-slate-700 font-semibold hover:bg-gray-50 transition-all"
                  >
                    ยกเลิก
                  </button>
                  <button
                    type="submit"
                    disabled={installLoading}
                    className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-sky-500 to-blue-600 text-white font-semibold hover:from-sky-600 hover:to-blue-700 transition-all shadow-md focus:outline-none focus:ring-2 focus:ring-sky-400 disabled:opacity-50"
                  >
                    {installEditing ? "บันทึก" : "บันทึก"}
                  </button>
                </div>
              </form>
            </div>
          </Modal>
        )}


        {installConfirmOpen && (
          <Modal
            isOpen={installConfirmOpen}
            onClose={cancelInstallDelete}
            hideHeader
            size="sm"
            variant="center"
          >
            <div className="space-y-3">
              <div>
                <p className="text-base font-semibold text-slate-900">
                  คุณต้องการลบรายการผ่อนนี้หรือไม่?
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  การลบไม่สามารถย้อนกลับได้
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={cancelInstallDelete}
                  className="px-3 py-1 rounded-md border border-gray-200 bg-white text-sm"
                >
                  ยกเลิก
                </button>
                <button
                  type="button"
                  onClick={confirmInstallDelete}
                  className="px-3 py-1 rounded-md bg-red-600 text-white text-sm"
                >
                  ลบ
                </button>
              </div>
            </div>
          </Modal>
        )}

        {/* Overview Section */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 mb-6">
          <div className="xl:col-span-3 space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-1">
              <div className="bg-white p-3 rounded-2xl shadow-lg ring-1 ring-sky-100 flex items-center space-x-4">
                <div className="p-3 bg-emerald-100 rounded-full">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">รายรับเดือนนี้</p>
                  <p className="font-semibold text-green-400">
                    {typeof monthlyIncome === "number"
                      ? Number(monthlyIncome).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                      : "0.00"}{" "}
                    ฿
                  </p>
                </div>
              </div>
              <div className="bg-white p-3 rounded-2xl shadow-lg ring-1 ring-rose-100 flex items-center space-x-4">
                <div className="p-3 bg-rose-100 rounded-full">
                  <TrendingDown className="h-6 w-6 text-rose-600" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">รายจ่ายเดือนนี้</p>
                  <p className="font-semibold text-red-400">
                    {typeof monthlyExpense === "number"
                      ? Number(monthlyExpense).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                      : "0.00"}{" "}
                    ฿
                  </p>
                </div>
              </div>
              <div className="bg-white p-3 rounded-2xl shadow-lg ring-1 ring-blue-100 flex items-center space-x-4">
                <div
                  className={`p-3 rounded-full ${balance >= 0 ? "bg-sky-100" : "bg-amber-100"}`}
                >
                  <User className="h-6 w-6 text-sky-600" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">คงเหลือ</p>
                  <p
                    className={`font-semibold ${balance >= 0 ? "text-sky-600" : "text-amber-600"}`}
                  >
                    {typeof balance === "number"
                      ? Number(balance).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                      : "0.00"}{" "}
                    ฿
                  </p>
                </div>
              </div>
              <div className="bg-white p-3 rounded-2xl shadow-lg ring-1 ring-blue-100 flex items-center space-x-4 md:hidden">
                <div className="p-3 bg-blue-100 rounded-full">
                  <CreditCard className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">รายการผ่อน</p>
                  <p className="font-semibold text-blue-500">
                    {installmentDashboardSummary.activeCount} รายการ
                  </p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded-2xl shadow-lg ring-1 ring-blue-100 hidden md:flex items-center space-x-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <CreditCard className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">รายการผ่อน</p>
                  <p className="font-semibold text-blue-500">
                    {installmentDashboardSummary.activeCount} รายการ
                  </p>
                </div>
              </div>
              <div className="bg-white p-3 rounded-2xl shadow-lg ring-1 ring-amber-100 flex items-center space-x-4">
                <div className="p-3 bg-amber-100 rounded-full">
                  <CalendarClock className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">ยอดผ่อนต่อเดือน</p>
                  <p className="font-semibold text-amber-600">
                    {Number(installmentDashboardSummary.monthlyCommitment || 0).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    ฿
                  </p>
                </div>
              </div>
              <div className="bg-white p-3 rounded-2xl shadow-lg ring-1 ring-emerald-100 flex items-center space-x-4">
                <div className="p-3 bg-emerald-100 rounded-full">
                  <PiggyBank className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">ยอดผ่อนคงเหลือ</p>
                  <p className="font-semibold text-emerald-600">
                    {Number(installmentDashboardSummary.totalRemaining || 0).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    ฿
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="xl:col-span-1 mt-1">
            <div className="bg-white p-6 rounded-2xl shadow-lg h-full">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-base font-semibold text-slate-800">งบประมาณรายจ่าย</h3>
                {ownerFilterId && ownerFilterId !== "all" && (
                  <button
                    type="button"
                    onClick={() => setShowBudgetForm((v) => !v)}
                    aria-expanded={showBudgetForm}
                    aria-controls="budget-form-panel"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 bg-white hover:bg-gray-50"
                  >
                    <Settings className="h-4 w-4 text-slate-600" />
                  </button>
                )}
              </div>

              {ownerFilterId && ownerFilterId !== "all" && showBudgetForm && (
                <div
                  id="budget-form-panel"
                  className="mb-3 p-2 rounded-lg border border-gray-200 bg-gray-50"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1">
                    <div className="text-xs text-slate-600">
                      ตั้งงบสำหรับ{" "}
                      {owners.find((o) => String(o.id) === String(ownerFilterId))
                        ?.username || "-"}{" "}
                      เดือน {filterMonth}/{filterYear}
                    </div>
                    <div className="flex-1" />
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9.,]*"
                        value={budgetInput}
                        onChange={(e) =>
                          setBudgetInput(formatBudgetTyping(e.target.value))
                        }
                        onBlur={() => {
                          const amount = parseMoney(budgetInput);
                          if (
                            amount > 0 ||
                            budgetInput === "0" ||
                            budgetInput === "0." ||
                            /^0?$/g.test(budgetInput)
                          ) {
                            setBudgetInput(
                              amount.toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }),
                            );
                          }
                        }}
                        placeholder="เช่น 10,000.00"
                        className="w-32 rounded-md border border-gray-300 px-2.5 py-1 text-base md:text-xs text-slate-700 outline-none"
                      />
                      <button
                        type="button"
                        onClick={saveBudget}
                        disabled={budgetSaving}
                        className="px-2.5 py-1 text-xs rounded-md bg-gradient-to-r from-sky-500 to-blue-600 text-white font-semibold disabled:opacity-50"
                      >
                        {budgetSaving ? "กำลังบันทึก…" : "บันทึก"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {(() => {
                // Helper for formatting currency
                const formatCurrency = (n) =>
                  n !== undefined && n !== null && !isNaN(Number(n))
                    ? Number(n).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                    : "0.00";
                // Support multiple budgets: budgets = array, budget = array or object
                const budgets = Array.isArray(budgetUse)
                  ? budgetUse
                  : budgetUse
                    ? [budgetUse]
                    : [];
                const expenses = allTransactions.filter(
                  (t) => t.type === "expense",
                );
                // ownerFilterId: '' = all, otherwise user_id
                const isAllOwners =
                  ownerFilterId === "" || ownerFilterId === "all";
                // Find selected user's budget
                const selectedUserBudget = budgets.find(
                  (b) => String(b.user_id) === String(ownerFilterId),
                );
                const selectedUserAmount = selectedUserBudget
                  ? selectedUserBudget.amount
                  : 0;
                // Total expense for selected user
                const totalExpense = isAllOwners
                  ? 0 // not used in "all" case
                  : expenses
                    .filter((e) => String(e.owner_id) === String(ownerFilterId))
                    .reduce((sum, e) => sum + Number(e.amount), 0);
                // For progress bar
                const ProgressBar = ({ value, max }) => (
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${value > max ? "bg-amber-500" : "bg-emerald-500"}`}
                      style={{
                        width: `${max ? Math.min((value / max) * 100, 100) : 0}%`,
                      }}
                    ></div>
                  </div>
                );
                // If budgets is empty, show not set
                if (!budgets.length) {
                  return <p className="text-slate-400">ยังไม่ได้ตั้งงบประมาณ</p>;
                }
                // Show for all owners
                if (isAllOwners) {
                  return (
                    <>
                      {budgets.map((b) => {
                        const userExpense = expenses
                          .filter((e) => String(e.owner_id) === String(b.user_id))
                          .reduce((sum, e) => sum + Number(e.amount), 0);
                        const overBudget = userExpense > b.amount;
                        const username =
                          owners.find((o) => o.id === b.user_id)?.username ||
                          b.username ||
                          "-";
                        return (
                          <div key={b.user_id} className="mb-4">
                            <p className="text-sm font-medium mb-2">{username}</p>
                            <ProgressBar value={userExpense} max={b.amount} />
                            <p className="text-sm text-gray-600 mt-2">
                              ใช้ไป {formatCurrency(userExpense)} จาก{" "}
                              {formatCurrency(b.amount)} บาท
                            </p>
                            {overBudget && (
                              <p className="text-amber-600 text-xs mt-1 animate-pulse">
                                ใช้จ่ายเกินงบ! จำนวน{" "}
                                {formatCurrency(userExpense - b.amount)} บาท
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </>
                  );
                }
                // Show for selected user
                return (
                  <>
                    <ProgressBar value={totalExpense} max={selectedUserAmount} />
                    <p className="text-sm text-gray-600 mt-2">
                      ใช้ไป {formatCurrency(totalExpense)} จาก{" "}
                      {formatCurrency(selectedUserAmount)} บาท
                    </p>
                    {totalExpense > selectedUserAmount && (
                      <p className="text-amber-600 text-xs mt-1 animate-pulse">
                        ใช้จ่ายเกินงบ! จำนวน{" "}
                        {formatCurrency(totalExpense - selectedUserAmount)} บาท
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Expense Overview */}
        <div className="bg-white p-6 rounded-2xl shadow-lg mb-4">
          {/* Mobile: collapsible header */}
          <div className="md:hidden">
            <button
              type="button"
              onClick={() => setShowCategorySummary((v) => !v)}
              aria-expanded={showCategorySummary}
              aria-controls="category-summary-panel"
              // title={showCategorySummary ? 'ซ่อน' : 'แสดง'}
              className="group flex w-full items-center justify-between mb-2 text-left select-none"
            >
              <h2 className="text-base font-semibold text-slate-800 group-hover:text-slate-900">
                สรุปรายจ่ายตามหมวดหมู่
              </h2>
              <svg
                className={`h-5 w-5 transition-transform ${showCategorySummary ? "rotate-180" : ""}`}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.584l3.71-4.353a.75.75 0 111.14.976l-4.25 5a.75.75 0 01-1.14 0l-4.25-5a.75.75 0 01.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <div id="category-summary-panel">
              {showCategorySummary && (
                <>
                  {categorySummary.items.length === 0 ? (
                    <div className="text-slate-400 text-sm">
                      ไม่มีข้อมูลสำหรับสรุป
                    </div>
                  ) : (
                    <>
                      <div className="space-y-1">
                        {(showAllCategories ? categorySummary.items : categorySummary.items.slice(0, 5)).map((item) => {
                          const percent = categorySummary.totalAll
                            ? (item.total / categorySummary.totalAll) * 100
                            : 0;
                          const active =
                            String(filterCategory) === String(item.id);
                          return (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() =>
                                setFilterCategory(active ? "" : item.id)
                              }
                              className={`w-full text-left p-1.5 rounded-lg transition-colors ${active ? "bg-blue-50" : "hover:bg-gray-50"}`}
                            >
                              <div className="flex items-center gap-3 text-sm">
                                <span className="font-xs text-slate-700 truncate w-28 md:w-40" title={item.name}>{item.name}</span>
                                <span className="text-[10px] text-slate-500 w-8 text-right">x{item.count}</span>
                                <div className="flex-1" title={`${percent.toFixed(0)}%`}>
                                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                                    <div
                                      className={`h-1.5 rounded-full ${active ? "bg-blue-500" : "bg-gray-400"}`}
                                      style={{ width: `${percent.toFixed(2)}%` }}
                                    />
                                  </div>
                                </div>
                                <span className="font-semibold text-slate-800 tabular-nums w-24 text-right">
                                  {Number(item.total).toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      {categorySummary.items.length > 5 && (
                        <div className="text-center">
                          <button
                            type="button"
                            onClick={() => setShowAllCategories((v) => !v)}
                            className="mt-2 px-1.5 text-xs text-sky-600 hover:underline"
                          >
                            {showAllCategories
                              ? "แสดงน้อยลง"
                              : `ดูทั้งหมด ${categorySummary.items.length} รายการ`}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Desktop: always visible */}
          <div className="hidden md:block">
            <div className="flex items-start justify-between mb-2">
              <h2 className="text-base font-semibold">สรุปรายจ่ายตามหมวดหมู่</h2>
              {filterCategory !== "" && (
                <button
                  type="button"
                  onClick={() => setFilterCategory("")}
                  className="text-xs px-2 py-1 rounded border border-gray-200 text-slate-600 hover:bg-gray-50"
                >
                  ล้างตัวกรอง
                </button>
              )}
            </div>
            {categorySummary.items.length === 0 ? (
              <div className="text-slate-400 text-sm">ไม่มีข้อมูลสำหรับสรุป</div>
            ) : (
              <div className="grid grid-cols-3 gap-x-4 gap-y-1">
                {categorySummary.items.map((item) => {
                  const percent = categorySummary.totalAll
                    ? (item.total / categorySummary.totalAll) * 100
                    : 0;
                  const active = String(filterCategory) === String(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setFilterCategory(active ? "" : item.id)}
                      className={`w-full text-left p-1.5 rounded-lg transition-colors ${active ? "bg-blue-50" : "hover:bg-gray-50"}`}
                    >
                      <div className="flex items-center gap-3 text-sm">
                        <span className="font-xs text-slate-700 truncate w-28 md:w-40" title={item.name}>
                          {item.name}
                        </span>
                        <span className="text-[10px] text-slate-500 w-8 text-right">
                          x{item.count}
                        </span>
                        <div className="flex-1" title={`${percent.toFixed(0)}%`}>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full ${active ? "bg-blue-500" : "bg-gray-400"}`}
                              style={{ width: `${percent.toFixed(2)}%` }}
                            />
                          </div>
                        </div>
                        <span className="font-semibold text-slate-800 tabular-nums w-24 text-right">
                          {Number(item.total).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* สรุปรายจ่ายตามวิธีชำระ */}
        <div className="bg-white p-6 rounded-2xl shadow-lg mb-4">
          <div className="">
            <button
              type="button"
              onClick={() => setShowPaySummary((v) => !v)}
              aria-expanded={showPaySummary}
              aria-controls="pay-summary-panel"
              // title={showPaySummary ? 'ซ่อน' : 'แสดง'}
              className="group flex w-full items-center justify-between mb-2 text-left select-none"
            >
              <h3 className="text-base font-semibold text-slate-800 group-hover:text-slate-900">
                สรุปรายจ่ายตามวิธีชำระ
              </h3>
              <svg
                className={`h-5 w-5 transition-transform ${showPaySummary ? "rotate-180" : ""}`}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.584l3.71-4.353a.75.75 0 111.14.976l-4.25 5a.75.75 0 01-1.14 0l-4.25-5a.75.75 0 01.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <div id="pay-summary-panel">
              {showPaySummary && (
                <>
                  {/* ฟิลเตอร์สถานะเฉพาะส่วนสรุป (ซิงก์กับตาราง) */}
                  <div className="flex flex-wrap items-center gap-2 mb-4 duration-200 relative z-0">
                    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm h-7 items-center overflow-hidden">
                      {[
                        { id: "", name: "ทั้งหมด" },
                        { id: "unpaid", name: "ยังไม่จ่าย" },
                        { id: "paid", name: "จ่ายแล้ว" },
                      ].map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setFilterStatus(opt.id)}
                          className={`
                            flex items-center gap-1 rounded-md px-3 py-0.5 text-[10px] sm:text-xs font-medium transition-all
                            ${filterStatus === opt.id
                              ? "bg-blue-50 text-blue-700 shadow-sm"
                              : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                            }
                          `}
                        >
                          {opt.name}
                        </button>
                      ))}
                    </div>
                    {filterPaymentMethod !== "" && (
                      <button
                        type="button"
                        onClick={() => setFilterPaymentMethod("")}
                        className="text-xs px-2 py-1 rounded border border-gray-200 text-slate-600 hover:bg-gray-50"
                      >
                        ล้างตัวกรอง
                      </button>
                    )}
                  </div>

                  {/* การ์ดสรุปแบบอ่านง่ายและคลิกเลือกได้ */}
                  {paySummary.items.length === 0 ? (
                    <div className="text-slate-400 text-sm">
                      ไม่มีข้อมูลสำหรับสรุป
                    </div>
                  ) : (
                    <>
                      <div className="hidden md:block">
                        <div className="grid grid-cols-3 gap-x-4 gap-y-1">
                          {paySummary.items.map((item) => {
                            const percent = paySummary.totalAll
                              ? (item.total / paySummary.totalAll) * 100
                              : 0;
                            const active =
                              String(filterPaymentMethod) === String(item.id);
                            return (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() =>
                                  setFilterPaymentMethod(active ? "" : item.id)
                                }
                                className={`w-full text-left p-1.5 rounded-lg transition-colors ${active ? "bg-blue-50" : "hover:bg-gray-50"}`}
                              >
                                <div className="flex items-center gap-3 text-sm">
                                  <span
                                    className="font-xs text-slate-700 truncate w-28 md:w-40"
                                    title={item.name}
                                  >
                                    {item.name}
                                  </span>
                                  <span className="text-[10px] text-slate-500 w-8 text-right">
                                    x{item.count}
                                  </span>
                                  <div className="flex-1" title={`${percent.toFixed(0)}%`}>
                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                      <div
                                        className={`h-1.5 rounded-full ${active ? "bg-blue-500" : "bg-gray-400"}`}
                                        style={{ width: `${percent.toFixed(2)}%` }}
                                      />
                                    </div>
                                  </div>
                                  <div className="relative group w-24 text-right">
                                    <span className="font-semibold text-slate-800 tabular-nums">
                                      {Number(item.total).toLocaleString("en-US", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </span>
                                    <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-40 rounded-md bg-slate-800 text-white text-xs p-2 shadow-lg opacity-0 transition-opacity group-hover:opacity-100">
                                      <div className="flex justify-between">
                                        <span>จ่ายแล้ว</span>
                                        <span className="font-medium">
                                          {Number(item.totalPaid).toLocaleString("en-US", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span>ยังไม่จ่าย</span>
                                        <span className="font-medium">
                                          {Number(item.totalUnpaid).toLocaleString("en-US", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}
                                        </span>
                                      </div>
                                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 rotate-45 bg-slate-800" />
                                    </div>
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="md:hidden">
                        <div className="space-y-1">
                          {(showAllPayMethods
                            ? paySummary.items
                            : paySummary.items.slice(0, 5)
                          ).map((item) => {
                            const percent = paySummary.totalAll
                              ? (item.total / paySummary.totalAll) * 100
                              : 0;
                            const active =
                              String(filterPaymentMethod) === String(item.id);
                            return (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() =>
                                  setFilterPaymentMethod(active ? "" : item.id)
                                }
                                className={`w-full text-left p-1.5 rounded-lg transition-colors ${active ? "bg-blue-50" : "hover:bg-gray-50"}`}
                              >
                                <div className="flex items-center gap-3 text-sm">
                                  <span
                                    className="font-xs text-slate-700 truncate w-28"
                                    title={item.name}
                                  >
                                    {item.name}
                                  </span>
                                  <span className="text-[10px] text-slate-500 w-8 text-right">
                                    x{item.count}
                                  </span>
                                  <div className="flex-1" title={`${percent.toFixed(0)}%`}>
                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                      <div
                                        className={`h-1.5 rounded-full ${active ? "bg-blue-500" : "bg-gray-400"}`}
                                        style={{ width: `${percent.toFixed(2)}%` }}
                                      />
                                    </div>
                                  </div>
                                  <span className="font-semibold text-slate-800 tabular-nums w-24 text-right">
                                    {Number(item.total).toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        {paySummary.items.length > 5 && (
                          <div className="text-center">
                            <button
                              type="button"
                              onClick={() => setShowAllPayMethods((v) => !v)}
                              className="mt-2 px-1.5 text-xs text-sky-600 hover:underline"
                            >
                              {showAllPayMethods
                                ? "แสดงน้อยลง"
                                : `ดูทั้งหมด ${paySummary.items.length} รายการ`}
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Over-Budget Warning Modal (per selected owner) */}
        <Modal
          isOpen={overBudgetOpen}
          onClose={() => dismissOverBudget(false)}
          title="แจ้งเตือนงบประมาณ"
        >
          <div className="space-y-2 text-slate-700">
            <p className="text-sm">
              {overBudgetInfo.username ? (
                <>
                  คุณ{" "}
                  <span className="font-semibold">{overBudgetInfo.username}</span>{" "}
                  ใช้จ่ายเกินงบ!
                </>
              ) : (
                <>ใช้จ่ายเกินงบ!</>
              )}
            </p>
            <p className="text-sm">
              เกินจำนวน{" "}
              <span className="font-semibold text-amber-600">
                {Number(overBudgetInfo.exceed).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{" "}
                ฿
              </span>
            </p>
            <div className="text-xs text-slate-500">
              ใช้ไปแล้ว{" "}
              {Number(overBudgetInfo.totalExpense).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{" "}
              จากงบ{" "}
              {Number(overBudgetInfo.limit).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{" "}
              บาท
            </div>
            <div className="flex justify-center space-x-2 mt-6">
              <button
                type="button"
                onClick={() => dismissOverBudget(true)}
                className="w-full sm:w-32 px-2.5 py-1 rounded-lg bg-white border border-gray-300 text-slate-700 font-semibold hover:bg-gray-50 transition-all text-sm"
              >
                ไม่เตือนอีกในวันนี้
              </button>
              {/* <button
              type="button"
              onClick={() => dismissOverBudget(false)}
              className="w-full sm:w-32 px-2.5 py-1 rounded-lg bg-gradient-to-r from-amber-500 to-rose-500 text-white font-semibold hover:from-amber-600 hover:to-rose-600 transition-all shadow text-sm"
            >
              ปิด
            </button> */}
            </div>
          </div>
        </Modal>

        {/* Transactions List */}
        {/* Desktop: Table, Mobile: Card-like grouped by date */}
        <div className="bg-white p-6 rounded-2xl shadow-lg mb-4">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-4">
              <div>
                <h2 className="text-base font-semibold">รายการล่าสุด</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  ทั้งหมด {filteredTransactions.length} รายการ
                </p>
              </div>

              {/* View Mode Toggle - Relocated for Better Accessibility */}
              <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm h-7 items-center">
                <button
                  type="button"
                  className={`flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] sm:text-xs font-medium transition-all ${viewMode === "list"
                    ? "bg-blue-50 text-blue-700 shadow-sm"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                    }`}
                  onClick={() => setViewMode("list")}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 opacity-70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
                  <span>รายการ</span>
                </button>
                <button
                  type="button"
                  className={`flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] sm:text-xs font-medium transition-all ${viewMode === "calendar"
                    ? "bg-blue-50 text-blue-700 shadow-sm"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                    }`}
                  onClick={() => setViewMode("calendar")}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 opacity-70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                  <span>ปฏิทิน</span>
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-row flex-wrap items-center gap-2 mb-3">
            <button
              onClick={() => setShowTxFilters((v) => !v)}
              aria-expanded={showTxFilters}
              aria-controls="tx-filters-panel"
              className="flex items-center space-x-1 text-[10px] md:text-sm bg-white border border-gray-300 text-slate-700 hover:bg-gray-50 font-bold py-1 px-2 rounded-lg transition-colors"
            >
              <Filter size={12} />
              <span>{showTxFilters ? "Filter" : "Filter"}</span>
              <svg
                className={`h-4 w-4 transition-transform ${showTxFilters ? "rotate-180" : ""}`}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.584l3.71-4.353a.75.75 0 111.14.976l-4.25 5a.75.75 0 01-1.14 0l-4.25-5a.75.75 0 01.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <button
              onClick={() => handleExport("excel")}
              className="flex items-center space-x-1 text-[10px] md:text-sm bg-green-600 hover:bg-green-500 text-white font-bold py-1 px-2 rounded-lg transition-colors"
            >
              <FileDown size={12} />
              <span>Excel</span>
            </button>
            <button
              onClick={() => handleBulkMarkStatus("paid")}
              className="hidden md:flex items-center space-x-1 text-[10px] md:text-sm
                        bg-gradient-to-r from-blue-500 to-sky-600
                        hover:from-blue-600 hover:to-sky-700
                        text-white font-bold py-1 px-2 rounded-lg transition-colors shadow"
            // title="Mark selected as Paid"
            >
              <CheckCircle size={14} />
              <span>Mark Paid</span>
            </button>
            <button
              onClick={() => handleBulkMarkStatus("unpaid")}
              className="hidden md:flex items-center space-x-1 text-[10px] md:text-sm
                        bg-gradient-to-r from-gray-400 to-slate-500
                        hover:from-gray-500 hover:to-slate-600
                        text-white font-bold py-1 px-2 rounded-lg transition-colors shadow"
            // title="Mark selected as Unpaid"
            >
              <XCircle size={14} />
              <span>Mark Unpaid</span>
            </button>
            {/* <button
              onClick={() => handleExport('pdf')}
              className="flex items-center space-x-1 text-[10px] md:text-sm bg-red-600 hover:bg-red-500 text-white font-bold py-1 px-2 rounded-lg transition-colors"
            >
              <FileDown size={12} />
              <span>PDF</span>
            </button> */}
            <button
              onClick={() => {
                setEditingTransaction(null);
                setPrefillTransaction(null);
                setIsModalOpen(true);
              }}
              className="hidden md:flex items-center space-x-1 text-[10px] md:text-sm bg-gradient-to-r from-sky-500 to-blue-500 hover:from-sky-600 hover:to-blue-600 text-white font-bold py-1 px-2 rounded-lg transition-colors shadow"
            >
              <Plus size={12} />
              <span>เพิ่มรายการ</span>
            </button>
          </div>

          {/* ฟิลเตอร์ใหม่ 5 รายการ (responsive grid) */}
          <div
            id="tx-filters-panel"
            className={`${showTxFilters ? "mt-3" : "mt-0"} mb-3 flex flex-col md:flex-row md:items-end md:justify-between gap-2 duration-200 relative z-0duration-200 relative z-0`}
          >
            {showTxFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 text-xs">
                {/* วิธีชำระ */}
                <div className="w-full">
                  <TailwindDropdown
                    options={[{ id: "", name: "วิธีชำระ" }, ...paymentMethods]}
                    value={filterPaymentMethod}
                    onChange={(e) => setFilterPaymentMethod(e.target.value)}
                    placeholder="ทั้งหมด"
                    className="transaction-filter-payment-method"
                  />
                </div>
                {/* หมวดหมู่/}
                <div className="w-full">
                  <TailwindDropdown
                    options={[{ id: "", name: "หมวดหมู่" }, ...categories]}
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    placeholder="ทั้งหมด"
                    className="transaction-filter-category"
                  />
                </div>
                {/* สถานะ */}
                <div className="w-full lg:col-span-1">
                  <div className="inline-flex w-full rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm h-7 items-center overflow-hidden">
                    {[
                      { id: "", name: "ทั้งหมด" },
                      { id: "unpaid", name: "ยังไม่จ่าย" },
                      { id: "paid", name: "จ่ายแล้ว" },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setFilterStatus(opt.id)}
                        className={`
                          flex-1 flex items-center justify-center gap-1 rounded-md px-2 py-0.5 text-[10px] sm:text-xs font-medium transition-all
                          ${filterStatus === opt.id
                            ? "bg-blue-50 text-blue-700 shadow-sm"
                            : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                          }
                        `}
                      >
                        {opt.name}
                      </button>
                    ))}
                  </div>
                </div>
                {/* เจ้าของรายการ */}
                <div className="w-full">
                  <TailwindDropdown
                    options={[{ id: "", username: "เจ้าของ" }, ...owners]}
                    value={ownerFilterId}
                    onChange={(e) => {
                      setOwnerFilterId(e.target.value);
                    }}
                    placeholder="ทั้งหมด"
                    className="transaction-filter-owner"
                    optionLabel="username"
                    optionValue="id"
                  />
                </div>
                {/* ค้นหา memo */}
                <div className="w-full sm:col-span-2 lg:col-span-1">
                  <input
                    type="text"
                    value={filterMemo}
                    onChange={(e) => setFilterMemo(e.target.value)}
                    placeholder="ค้นหา Memo"
                    className="w-full inline-flex justify-between rounded-lg border border-gray-300 bg-white text-slate-800 text-base md:text-sm px-4 py-1 h-8 outline-none transition-all"
                  />
                </div>
              </div>
            )}
          </div>


          {viewMode === "calendar" ? (
            <CalendarView transactions={filteredTransactions} year={filterYear} month={filterMonth} />
          ) : (
            <>
              {/* Desktop table */}
              <div className="overflow-x-auto hidden md:block">
                {filteredTransactions.length === 0 ? (
                  <div className="text-center text-gray-500 py-4">ไม่พบข้อมูล</div>
                ) : (
                  <table className="min-w-full text-sm">
                    <colgroup><col style={{ width: "4%" }} /><col style={{ width: "18%" }} /><col style={{ width: "12%" }} /><col style={{ width: "10%" }} /><col style={{ width: "32%" }} /><col style={{ width: "10%" }} /><col style={{ width: "10%" }} /><col style={{ width: "8%" }} /></colgroup>
                    <thead className="border-b border-gray-200 text-slate-600 text-sm bg-gray-50">
                      <tr>
                        {/* <th className="p-1 whitespace-nowrap">วันที่</th> */}
                        <th className="p-1 text-center whitespace-nowrap w-8">
                          <input
                            type="checkbox"
                            className="tx-select-all"
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const table = e.target.closest("table");
                              if (table) {
                                table
                                  .querySelectorAll("tbody input.tx-select")
                                  .forEach((inp) => {
                                    inp.checked = checked;
                                  });
                              }
                            }}
                            aria-label="เลือกทั้งหมด"
                          />
                        </th>
                        <th className="p-1 text-left whitespace-nowrap">วิธีชำระ</th>
                        <th className="p-1 text-left whitespace-nowrap">หมวดหมู่</th>
                        <th className="p-1 text-right whitespace-nowrap">
                          จำนวนเงิน
                        </th>
                        <th className="p-1 text-left whitespace-nowrap">
                          บันทึกช่วยจำ
                        </th>
                        <th className="p-1 text-left whitespace-nowrap">สถานะ</th>
                        <th className="p-1 text-left whitespace-nowrap">เจ้าของ</th>
                        <th className="p-1 text-center whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Group by date (YYYY-MM-DD) for desktop, then sort date desc
                        const grouped = filteredTransactions
                          .slice(0, visibleCount)
                          .reduce((acc, tx) => {
                            const d = dayjs(tx.transaction_date)
                              .tz("Asia/Bangkok")
                              .format("YYYY-MM-DD");
                            if (!acc[d]) acc[d] = [];
                            acc[d].push(tx);
                            return acc;
                          }, {});
                        const dates = Object.keys(grouped).sort((a, b) =>
                          dayjs(b).diff(dayjs(a)),
                        );
                        return dates.map((date) => (
                          <React.Fragment key={date}>
                            <tr className="bg-gray-50">
                              <td
                                className="p-2 text-sm text-slate-700 font-semibold"
                                colSpan={8}
                              >
                                {format(new Date(date), "d MMM yyyy")}
                              </td>
                            </tr>
                            {grouped[date].map((t) => {
                              const category = categories.find(
                                (c) => c.id === t.category_id,
                              );
                              return (
                                <tr
                                  key={t.id}
                                  className="border-b border-gray-100 hover:bg-gray-50 text-sm"
                                >
                                  {/* <td className="p-1 whitespace-nowrap">{dayjs(t.transaction_date).tz('Asia/Bangkok').format('D/M/YY')}</td> */}
                                  <td className="p-1 text-center whitespace-nowrap">
                                    <input
                                      type="checkbox"
                                      className="tx-select"
                                      data-id={t.id}
                                      aria-label="เลือกแถวนี้"
                                    />
                                  </td>
                                  <td
                                    className="p-1 whitespace-nowrap truncate max-w-[10rem] cursor-pointer"
                                    onClick={() => handleCopyToClipboard(paymentMethods.find(p => p.id === t.payment_method_id)?.name || "")}
                                    title="คลิกเพื่อคัดลอก"
                                  >
                                    {paymentMethods.find(p => p.id === t.payment_method_id)?.name || "-"}
                                  </td>
                                  <td className="p-1 whitespace-nowrap truncate max-w-[12rem]">
                                    {category?.name || "N/A"}
                                  </td>
                                  <td
                                    className={`p-1 text-right whitespace-nowrap tabular-nums cursor-pointer ${t.type === "income" ? "text-green-400" : "text-red-400"}`}
                                    onClick={() => {
                                      const amountStr = (t.amount !== undefined && t.amount !== null && !isNaN(Number(t.amount)))
                                        ? Number(t.amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false })
                                        : "0.00";
                                      handleCopyToClipboard(amountStr, `คัดลอกจำนวนเงิน ${amountStr} แล้ว`);
                                    }}
                                    title="คลิกเพื่อคัดลอกจำนวนเงิน"
                                  >
                                    {t.type === "expense" ? "-" : "+"}
                                    {
                                      (t.amount !== undefined && t.amount !== null && !isNaN(Number(t.amount)))
                                        ? Number(t.amount).toLocaleString("en-US", {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })
                                        : "0.00"
                                    }
                                  </td>
                                  <td
                                    className="p-1 whitespace-nowrap truncate max-w-[28rem] cursor-pointer"
                                    onClick={() => handleCopyToClipboard(t.notes || "")}
                                    title="คลิกเพื่อคัดลอก"
                                  >
                                    {t.notes || "-"}
                                  </td>
                                  <td className="p-1 whitespace-nowrap">
                                    <button
                                      type="button"
                                      onClick={() => toggleTransactionStatus(t)}
                                      className={`px-2 py-1 text-xs rounded-full transition-colors ${t.status === "paid" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                                      title="คลิกเพื่อเปลี่ยนสถานะ"
                                    >
                                      {t.status === "paid"
                                        ? "จ่ายแล้ว"
                                        : "ยังไม่จ่าย"}
                                    </button>
                                  </td>
                                  <td className="p-1 whitespace-nowrap truncate max-w-[10rem]">
                                    {owners.find((o) => o.id === t.owner_id)
                                      ?.username || "-"}
                                  </td>
                                  <td className="p-1 text-center whitespace-nowrap">
                                    <button
                                      onClick={() => {
                                        setEditingTransaction(null);
                                        setPrefillTransaction({ ...t });
                                        setIsModalOpen(true);
                                      }}
                                      className="p-2 text-slate-400 hover:text-blue-400"
                                      title="คัดลอก"
                                      aria-label="คัดลอก"
                                    >
                                      <Plus size={16} />
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingTransaction(t);
                                        setIsModalOpen(true);
                                      }}
                                      className="p-2 text-slate-400 hover:text-sky-400"
                                      aria-label="แก้ไขรายการ"
                                    >
                                      <SquarePen size={14} />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteTransaction(t.id)}
                                      className="p-2 text-slate-400 hover:text-red-400"
                                      aria-label="ลบรายการ"
                                    >
                                      <Trash size={14} />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ));
                      })()}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Mobile (below md): Card-like grouped by date */}
              <div className="md:hidden">
                {filteredTransactions.length === 0 ? (
                  <div className="text-center text-gray-500 py-4">ไม่พบข้อมูล</div>
                ) : (
                  (() => {
                    // Group transactions by date (YYYY-MM-DD)
                    const groupedTransactions = Object.entries(
                      filteredTransactions
                        .slice(0, visibleCount)
                        .reduce((acc, tx) => {
                          const date = dayjs(tx.transaction_date)
                            .tz("Asia/Bangkok")
                            .format("YYYY-MM-DD");
                          if (!acc[date]) acc[date] = [];
                          acc[date].push(tx);
                          return acc;
                        }, {}),
                    );
                    return groupedTransactions.map(([date, items]) => (
                      <div key={date} className="mb-6">
                        <h3 className="text-sm font-semibold mb-2 text-slate-800">
                          {format(new Date(date), "d MMM yyyy")}
                        </h3>
                        <ul className="space-y-1">
                          {items.map((tx) => (
                            <li
                              key={tx.id}
                              className="text-slate-800 bg-white shadow rounded-md p-2"
                            >
                              <div className="flex justify-between text-sm font-medium text-slate-800">
                                <span>
                                  {categories.find((c) => c.id === tx.category_id)?.name || tx.category_name || "-"}
                                </span>
                                <span
                                  className={`p-1 text-right whitespace-nowrap ${tx.type === "income" ? "text-green-400" : "text-red-400"}`}
                                >
                                  {tx.type === "expense" ? "-" : "+"}
                                  {tx.amount !== undefined &&
                                    tx.amount !== null &&
                                    !isNaN(Number(tx.amount))
                                    ? Number(tx.amount).toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })
                                    : "0.00"}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm font-medium text-gray-900 mt-1">
                                <span className="text-xs text-slate-500">
                                  {paymentMethods.find(
                                    (p) => p.id === tx.payment_method_id,
                                  )?.name || "-"}
                                </span>
                                <span className="text-xs text-slate-500">
                                  {owners.find((o) => o.id === tx.owner_id)
                                    ?.username || "-"}
                                </span>
                              </div>
                              <div className="text-xs text-slate-500 mt-1">
                                Memo: {tx.memo || tx.notes || "-"}
                              </div>
                              <div className="flex justify-between text-sm font-medium text-gray-900 mt-1">
                                <span>
                                  <button
                                    onClick={() => {
                                      setEditingTransaction(null);
                                      setPrefillTransaction({ ...tx });
                                      setIsModalOpen(true);
                                    }}
                                    className="p-1 text-slate-400 hover:text-blue-400"
                                    title="คัดลอก"
                                    aria-label="คัดลอก"
                                  >
                                    <Plus size={16} />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingTransaction(tx);
                                      setIsModalOpen(true);
                                    }}
                                    className="p-1 text-slate-400 hover:text-sky-400"
                                    aria-label="แก้ไขรายการ"
                                  >
                                    <SquarePen size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteTransaction(tx.id)}
                                    className="p-1 text-slate-400 hover:text-red-400"
                                    aria-label="ลบรายการ"
                                  >
                                    <Trash size={14} />
                                  </button>
                                </span>
                                <span
                                  className={`px-2 py-1 text-xs rounded-full ${tx.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-700"}`}
                                >
                                  {tx.status === "paid" ? "จ่ายแล้ว" : "ยังไม่จ่าย"}
                                </span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ));
                  })()
                )}
              </div>

              {/* Load more / Show all controls */}
              {filteredTransactions.length > 0 && (
                <div className="mt-4 flex justify-center gap-2">
                  {visibleCount < filteredTransactions.length && (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          setVisibleCount((c) =>
                            Math.min(c + 10, filteredTransactions.length),
                          )
                        }
                        className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white text-slate-700 hover:bg-gray-50"
                      >
                        Load more
                      </button>
                      <button
                        type="button"
                        onClick={() => setVisibleCount(filteredTransactions.length)}
                        className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white text-slate-700 hover:bg-gray-50"
                      >
                        All ({filteredTransactions.length})
                      </button>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* ===== สรุปผ่อนชำระ ===== */}
        <div className="bg-white p-6 rounded-2xl shadow-lg mb-4">
          <div className="">
            <button
              type="button"
              onClick={() => setShowInstallSummary((v) => !v)}
              className="group flex w-full items-center justify-between mb-2 text-left select-none"
              aria-expanded={showInstallSummary}
              aria-controls="installment-summary-body"
            >
              <span className="text-base font-semibold">สรุปผ่อนชำระ</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className={`h-5 w-5 text-slate-500 transition-transform ${showInstallSummary ? "rotate-180" : ""}`}
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.58l3.71-4.35a.75.75 0 111.14.98l-4.25 5a.75.75 0 01-1.14 0l-4.25-5a.75.75 0 01.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            {showInstallSummary && (
              <div id="installment-summary-body" className="space-y-4">
                {/* แถวการ์ดสรุป */}

                {/* ===== ปฏิทินและสรุปผ่อนชำระ ===== */}
                <div className="rounded-2xl shadow-xl shadow-slate-100/50">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 mb-5">

                    <div className="flex flex-col sm:items-end">
                      <span className="text-[9px] text-slate-300 tracking-tighter">ยอดผ่อนคงเหลือ</span>
                      <div className="flex items-baseline gap-1">
                        <span className="text-xl font-black text-emerald-600 tracking-tighter">
                          {Number(installmentDashboardSummary.totalRemaining || 0).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                        <span className="text-xs font-black text-emerald-600">฿</span>
                      </div>
                    </div>
                  </div>

                  <InstallmentYearCalendar
                    items={installItems}
                    owners={owners}
                    paymentMethods={paymentMethods}
                    year={Number(filterYear)}
                    ownerId={ownerFilterId}
                  />
                </div>

              </div>
            )}
          </div>
        </div>



        {/* Floating Add Button (desktop only) */}
        <div className="hidden md:block fixed right-4 bottom-4 md:right-5 md:bottom-5 z-50">
          <button
            type="button"
            onClick={() => {
              setEditingTransaction(null);
              setPrefillTransaction(null);
              setIsModalOpen(true);
            }}
            className="flex items-center justify-center w-12 h-12 md:w-12 md:h-12 rounded-full bg-sky-600 text-white shadow-lg shadow-sky-600/30 hover:bg-sky-500 active:scale-95 transition"
            // title="เพิ่มรายการใหม่"
            aria-label="เพิ่มรายการใหม่"
          >
            <Plus className="w-6 h-6 md:w-6 md:h-6" />
          </button>
        </div>

        {/* Floating To Top Button */}
        {showToTop && (
          <div className="fixed right-4 bottom-20 md:right-5 md:bottom-20 z-50">
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="flex items-center justify-center w-12 h-12 rounded-full bg-slate-600 text-white shadow-lg shadow-slate-600/30 hover:bg-slate-500 active:scale-95 transition"
              // title="กลับไปด้านบน"
              aria-label="กลับไปด้านบน"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.5 15.75l7.5-7.5 7.5 7.5"
                />
              </svg>
            </button>
          </div>
        )}

        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={editingTransaction ? "แก้ไขรายการ" : "เพิ่มรายการใหม่"}
          size="lg"
        >
          <TransactionForm
            transaction={editingTransaction}
            initialData={prefillTransaction}
            onSave={handleSaveTransaction}
            onCancel={() => {
              setIsModalOpen(false);
              setEditingTransaction(null);
              setPrefillTransaction(null);
            }}
            categories={categories}
            paymentMethods={paymentMethods}
            owners={owners}
          />
        </Modal>
        {/* Confirm Delete Modal */}
        {confirmOpen && (
          <Modal
            isOpen={confirmOpen}
            onClose={cancelDelete}
            hideHeader
            size="sm"
            variant="center"
          >
            <div className="space-y-3">
              <div>
                <p className="text-base font-semibold text-slate-900">
                  คุณต้องการลบรายการนี้หรือไม่?
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  การลบจะไม่สามารถย้อนกลับได้
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={cancelDelete}
                  className="px-3 py-1 rounded-md border border-gray-200 bg-white text-sm"
                >
                  ยกเลิก
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="px-3 py-1 rounded-md bg-red-600 text-white text-sm"
                >
                  ลบ
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

// Login Page Component
const LoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { username, password });
      const { user, token } = res.data;
      onLogin(user, token);
    } catch (err) {
      setError("Username or Password Incorrect.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-white to-blue-100 flex flex-col justify-center items-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-slate-800">Expense Tracker</h1>
        </div>
        <div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-white">
          {error && (
            <div className="flex items-center gap-2 bg-rose-100 text-rose-700 text-sm p-3 rounded-lg mb-6 border border-rose-200">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 shrink-0">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1.5">
                Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <User className="h-4 w-4 text-slate-400" />
                </div>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-slate-800 outline-none transition focus:ring-2 focus:ring-sky-300 focus:border-sky-400"
                  placeholder="Please enter a username."
                  autoComplete="username"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1.5">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-slate-400"><path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" /></svg>
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 bg-white border border-gray-300 rounded-lg text-slate-800 outline-none transition focus:ring-2 focus:ring-sky-300 focus:border-sky-400"
                  placeholder="Please enter a password."
                  autoComplete="current-password"
                />
                <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-600" aria-label={showPassword ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"}>
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-bold py-2.5 px-4 rounded-lg transition-all duration-300 shadow-lg shadow-sky-600/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading && <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>}
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Main App Component
export default function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentPage, setCurrentPage] = useState("dashboard"); // 'dashboard', 'settings', etc.
  const [appData, setAppData] = useState({
    transactions: [],
    categories: [],
    paymentMethods: [],
    savings: [],
    owners: [],
    budget: null,
    installments: [],
    paymentUsage: {},
    recurrings: [],
    loading: true,
  });
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);

  // LIFF Initialization & Auto Login
  useEffect(() => {
    const initLiff = async () => {
      try {
        const liffId = process.env.REACT_APP_LIFF_ID || "YOUR_LIFF_ID";
        if (!liffId || liffId === "YOUR_LIFF_ID") {
          console.log("LIFF ID is not configured. Skipping LIFF init.");
          return;
        }

        await liff.init({ liffId });
        console.log("LIFF initialized successfully");

        if (liff.isLoggedIn()) {
          const profile = await liff.getProfile();
          const lineUserId = profile.userId;
          console.log("LIFF User ID:", lineUserId);

          // ถ้าผู้ใช้ตั้งใจ Logout ใน Session นี้ หรือมี token อยู่แล้ว ก็ไม่ต้อง Auto Login ซ้ำ
          if (sessionStorage.getItem("manualLogout") || localStorage.getItem("token")) {
            return;
          }

          try {
            const res = await api.post("/auth/line-login", { lineUserId });
            if (res.data && res.data.token) {
              localStorage.setItem("token", res.data.token);
              localStorage.setItem("user", JSON.stringify(res.data.user));
              setCurrentUser(res.data.user);
              if (typeof showToast === "function") {
                showToast("เข้าสู่ระบบผ่าน LINE สำเร็จ", "success");
              }
            }
          } catch (loginErr) {
            console.error("LINE Auto Login failed:", loginErr.response?.data || loginErr.message);
            if (loginErr.response && loginErr.response.status === 404) {
              if (typeof showToast === "function") {
                showToast("บัญชี LINE นี้ยังไม่ได้ผูกกับผู้ใช้ในระบบ", "warning");
              }
            }
          }
        }
      } catch (err) {
        console.error("LIFF initialization failed", err);
      }
    };

    initLiff();
  }, []);


  // --- Scroll Lock for Logout Modal ---
  useEffect(() => {
    const body = document.body;
    const docEl = document.documentElement;
    let originalScrollY = 0;
    let originalPaddingRight = "";

    if (isLogoutConfirmOpen) {
      originalScrollY = window.scrollY || window.pageYOffset || 0;
      const scrollBarWidth = window.innerWidth - docEl.clientWidth;
      originalPaddingRight = body.style.paddingRight;

      body.style.overflow = "hidden";
      if (scrollBarWidth > 0) {
        body.style.paddingRight = `${scrollBarWidth}px`;
      }
      body.style.position = "fixed";
      body.style.top = `-${originalScrollY}px`;
      body.style.width = "100%";
    } else {
      // This part will run when the modal closes
      const y = Math.abs(parseInt(body.style.top || "0", 10)) || 0;
      body.style.overflow = "";
      body.style.paddingRight = originalPaddingRight;
      body.style.position = "";
      body.style.top = "";
      body.style.width = "";
      if (y > 0) window.scrollTo(0, y);
    }
  }, [isLogoutConfirmOpen]);

  // Restore user from localStorage on first load
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token && !currentUser) {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        setCurrentUser(JSON.parse(storedUser));
      }
    }
  }, [currentUser]);

  // Filter month/year state
  const today = new Date();
  const [filterMonth, setFilterMonth] = useState(today.getMonth() + 1);
  const [filterYear, setFilterYear] = useState(today.getFullYear());
  const [ownerFilterId, setOwnerFilterId] = useState("");

  const hasInitializedOwner = React.useRef(false);
  useEffect(() => {
    if (currentUser && !hasInitializedOwner.current) {
      setOwnerFilterId(String(currentUser.id));
      hasInitializedOwner.current = true;
    }
  }, [currentUser]);

  // ... ใน useEffect ให้เรียก API จริง
  useEffect(() => {
    if (currentUser) {
      const fetchData = async () => {
        setAppData((prev) => ({ ...prev, loading: true }));
        try {
          // ดึง owner filter จาก state (dashboard owner filter)
          // แต่ที่นี่ยังไม่มี state ownerFilterId ใน App.js ให้ดึงทุกคน (ownerId='all') เป็น default
          // ถ้าอยาก filter owner เฉพาะ user ให้เพิ่ม state ownerFilterId ที่ App แล้วส่งไป budget API
          const ownerId = "all";
          const [transRes, catRes, pmRes, ownerRes, budgetRes, installRes, usageRes, savingsRes] =
            await Promise.all([
              api.get("/transactions", {
                params: { month: filterMonth, year: filterYear },
              }),
              api.get("/categories"),
              api.get("/payment-methods"),
              api.get("/owners"),
              api.get("/budgets", {
                params: { month: filterMonth, year: filterYear, ownerId },
              }),
              api.get("/installments"),
              api.get("/payment-methods/usage-summary"),
              api.get("/savings"),
            ]);

          const usageMap = {};
          (usageRes.data || []).forEach((item) => {
            const key = Number(item.payment_method_id);
            if (key) usageMap[key] = item.usage_count;
          });

          setAppData((prev) => ({
            ...prev,
            transactions: transRes.data,
            categories: catRes.data,
            paymentMethods: pmRes.data,
            owners: ownerRes.data,
            budget: budgetRes.data,
            installments: installRes.data,
            paymentUsage: usageMap,
            savings: savingsRes.data || [],
            loading: false,
          }));
        } catch (error) {
          console.error("Failed to fetch data", error);
          // อาจจะต้อง logout ถ้า token หมดอายุ
          if (
            error.response &&
            (error.response.status === 401 || error.response.status === 403)
          ) {
            handleLogout();
          }
          setAppData((prev) => ({ ...prev, loading: false }));
        }
      };
      fetchData();
    }
  }, [currentUser, filterMonth, filterYear]);

  // ... ในส่วน handleLogin ให้เก็บ token และ user
  const handleLogin = (user, token) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(user));
    sessionStorage.removeItem("manualLogout"); // ลบ flag ออกเมื่อล็อกอินปกติ
    setCurrentUser(user);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    sessionStorage.setItem("manualLogout", "true"); // ตั้ง flag ไว้เฉพาะ Session นี้
    setCurrentUser(null);
    setIsLogoutConfirmOpen(false);
    setCurrentPage("dashboard");
    // รีเซ็ตข้อมูลเพื่อป้องกันข้อมูลค้างของคนอื่น (Reset appData to prevent stale data)
    setAppData({
      transactions: [],
      categories: [],
      paymentMethods: [],
      savings: [],
      owners: [],
      budget: null,
      loading: true,
    });
  };

  const handlePaymentMethodsUpdated = useCallback((nextList) => {
    setAppData((prev) => ({ ...prev, paymentMethods: nextList }));
  }, []);

  const handleSavingsUpdated = useCallback((nextList) => {
    setAppData((prev) => ({ ...prev, savings: nextList }));
  }, []);

  const handleCategoriesUpdated = useCallback((nextList) => {
    setAppData((prev) => ({ ...prev, categories: nextList }));
  }, []);

  const handleOwnersUpdated = useCallback((nextList) => {
    setAppData((prev) => ({ ...prev, owners: nextList }));
  }, []);

  const handleBudgetUpdated = useCallback((nextList) => {
    setAppData((prev) => ({ ...prev, budget: nextList }));
  }, []);

  const handleRecurringsUpdated = useCallback((nextList) => {
    setAppData((prev) => ({ ...prev, recurrings: nextList }));
  }, []);

  const handleNavigate = (page) => {
    setCurrentPage(page);
  };

  const handleFilterMonthChange = (val) => {
    setFilterMonth(val);
    setAppData((prev) => ({ ...prev, loading: true }));
  };
  const handleFilterYearChange = (val) => {
    setFilterYear(val);
    setAppData((prev) => ({ ...prev, loading: true }));
  };

  const handleOwnerFilterChange = (id) => {
    setOwnerFilterId(id);
  };

  if (!currentUser) {
    return <LoginPage onLogin={handleLogin} />;
  }

  const renderLoading = () => (
    <div className="bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white flex justify-center items-center min-h-[calc(100vh-64px)] text-xl">
      <svg className="animate-spin h-10 w-10 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    </div>
  );

  const renderPage = () => {
    switch (currentPage) {
      case "dashboard":
        return (
          <DashboardPage
            key={`dashboard-${currentUser?.id}-${filterMonth}-${filterYear}`}
            user={currentUser}
            onRequestLogout={() => setIsLogoutConfirmOpen(true)}
            onNavigate={handleNavigate}
            data={appData}
            filterMonth={filterMonth}
            filterYear={filterYear}
            setFilterMonth={handleFilterMonthChange}
            setFilterYear={handleFilterYearChange}
            ownerFilterId={ownerFilterId}
            setOwnerFilterId={handleOwnerFilterChange}
          />
        );
      case "payment-methods":
        return (
          <PaymentMethodsPage
            key={`payment-methods-${currentUser?.id}`}
            onNavigate={handleNavigate}
            onRequestLogout={() => setIsLogoutConfirmOpen(true)}
            paymentMethods={appData.paymentMethods}
            paymentUsage={appData.paymentUsage}
            transactions={appData.transactions}
            filterMonth={filterMonth}
            filterYear={filterYear}
            onPaymentMethodsUpdate={handlePaymentMethodsUpdated}
          />
        );
      case "savings":
        return (
          <SavingsPage
            key={`savings-${currentUser?.id}`}
            onNavigate={handleNavigate}
            onRequestLogout={() => setIsLogoutConfirmOpen(true)}
            savings={appData.savings}
            owners={appData.owners}
            onSavingsUpdate={handleSavingsUpdated}
          />
        );
      case "manage-data":
        return (
          <ManageDataPage
            key={`manage-data-${currentUser?.id}`}
            onNavigate={handleNavigate}
            onRequestLogout={() => setIsLogoutConfirmOpen(true)}
            categories={appData.categories}
            owners={appData.owners}
            budget={appData.budget}
            transactions={appData.transactions}
            onCategoriesUpdate={handleCategoriesUpdated}
            onOwnersUpdate={handleOwnersUpdated}
            onBudgetUpdate={handleBudgetUpdated}
            recurrings={appData.recurrings}
            onRecurringsUpdate={handleRecurringsUpdated}
            paymentMethods={appData.paymentMethods}
            installments={appData.installments}
            onInstallmentsUpdate={(next) => setAppData(prev => ({ ...prev, installments: next }))}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="App">
      {currentUser && (
        <Navbar
          currentPage={currentPage}
          onNavigate={handleNavigate}
          onRequestLogout={() => setIsLogoutConfirmOpen(true)}
          onPlusClick={() => window.dispatchEvent(new CustomEvent("app-plus-clicked"))}
        />
      )}

      <main className="relative">
        {appData.loading ? renderLoading() : renderPage()}
      </main>

      {isLogoutConfirmOpen && (
        <Modal
          isOpen={isLogoutConfirmOpen}
          onClose={() => setIsLogoutConfirmOpen(false)}
          hideHeader
          size="sm"
          variant="center"
        >
          <div className="space-y-3 text-slate-800">
            <div>
              <p className="text-base font-semibold text-slate-900">
                ยืนยันการออกจากระบบ
              </p>
              <p className="mt-1 text-sm text-slate-600">
                คุณต้องการออกจากระบบใช่หรือไม่?
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsLogoutConfirmOpen(false)}
                className="px-3 py-1 rounded-md border border-gray-200 bg-white text-sm"
              >
                ยกเลิก
              </button>
              <button type="button" onClick={handleLogout} className="px-3 py-1 rounded-md bg-red-600 text-white text-sm">
                ออกจากระบบ
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
